<template>
  <div class="chat-container" :class="{ 'embedded-host': embedded }">
    <!-- Selection Mode Toolbar (Flutter sync: _isSelectionMode) -->
    <transition name="slide-down">
      <div v-if="isSelectionMode" class="selection-toolbar">
        <div class="selection-toolbar__left">
          <button class="selection-toolbar__close" @click="toggleSelectionMode">
            <i class="bi bi-x-lg"></i>
          </button>
          <span class="selection-toolbar__count">
            {{ selectedMessageIds.size }} {{ $t('chat.selected') || 'محدد' }}
          </span>
        </div>
        <div class="selection-toolbar__actions">
          <button 
            class="selection-toolbar__btn"
            @click="selectAllMessages"
            :title="$t('chat.select_all') || 'تحديد الكل'"
          >
            <i class="bi bi-check2-all"></i>
          </button>
          <button 
            class="selection-toolbar__btn selection-toolbar__btn--danger"
            @click="deleteSelectedMessages"
            :disabled="selectedMessageIds.size === 0"
            :title="$t('common.delete') || 'حذف'"
          >
            <i class="bi bi-trash3"></i>
          </button>
        </div>
      </div>
    </transition>
    
    <!-- Chat Header -->
    <div v-if="!embedded && !isSelectionMode" class="chat-header">
      <div class="chat-header__content">
        <button
          class="chat-header__back"
          @click="$router.back()"
          :title="$t('common.back') || 'Back'"
        >
          <i class="bi bi-arrow-right"></i>
        </button>
        <div class="chat-header__avatar">
          <img
            v-if="conversationPhoto"
            :src="conversationPhoto"
            alt=""
            @error="onAvatarError"
          />
          <div v-else class="chat-header__avatar-placeholder">
            {{ avatarInitial }}
          </div>
        </div>
        <div class="chat-header__info">
          <h1 class="chat-header__title">{{ headerTitle }}</h1>
          <!-- Typing indicator (Flutter sync: _isOtherUserTyping in AppBar subtitle) -->
          <span v-if="isOtherUserTyping" class="chat-header__typing">
            <span class="typing-dots"
              ><span></span><span></span><span></span
            ></span>
            {{
              isGroupChat && typingUserName
                ? `${typingUserName} ${$t("chat.typing") || "is typing..."}`
                : $t("chat.typing") || "typing..."
            }}
          </span>
          <span
            v-else-if="statusText"
            class="chat-header__status"
            :class="{
              'status-online': isOnline,
              'status-warning': isChatLocked,
              'status-error': isUserBlocked,
            }"
            >{{ statusText }}</span
          >
        </div>
        <!-- Search Button (Flutter sync: in-conversation search) -->
        <button
          class="chat-header__search-btn"
          @click="toggleSearchMode"
          :title="$t('chat.search_in_chat') || 'البحث في المحادثة'"
          :class="{ active: isSearchMode }"
        >
          <i class="bi bi-search"></i>
        </button>
        <!-- Mia Summary Button (Flutter sync: _showChatSummary) -->
        <button
          class="chat-header__mia-btn"
          @click="showSummaryDialog = true"
          :title="$t('chat.summarize_chat') || 'Summarize with Mia'"
        >
          <img src="/assets/img/mia_icon.png" alt="Mia" class="mia-icon" />
        </button>
        <!-- Actions Menu (Flutter sync: PopupMenuButton) -->
        <div class="chat-header__actions">
          <div class="dropdown">
            <button
              class="chat-header__menu-btn"
              data-bs-toggle="dropdown"
              :title="$t('common.more') || 'More'"
            >
              <i class="bi bi-three-dots-vertical"></i>
            </button>
            <ul class="dropdown-menu dropdown-menu-end">
              <!-- Search in chat -->
              <li>
                <button class="dropdown-item" @click="toggleSearchMode">
                  <i class="bi bi-search"></i>
                  {{ $t("chat.search_in_chat") || "البحث في المحادثة" }}
                </button>
              </li>
              <!-- Mute/Unmute -->
              <li>
                <button class="dropdown-item" @click="handleMuteAction">
                  <i :class="isMuted ? 'bi bi-bell' : 'bi bi-bell-slash'" style="margin-left: 8px;"></i>
                  {{ isMuted
                    ? $t("chat.unmute_chat") || "Unmute Chat"
                    : $t("chat.mute_chat") || "Mute Chat"
                  }}
                </button>
              </li>
              <li><hr class="dropdown-divider" /></li>
              <!-- Group actions -->
              <template v-if="isGroupChat">
                <li v-if="isCurrentUserOwner">
                  <button class="dropdown-item" @click="openModerationSheet">
                    <i class="bi bi-shield-check"></i>
                    {{ $t("chat.moderation") || "Moderation" }}
                  </button>
                </li>
                <li v-if="isCurrentUserOwner">
                  <button class="dropdown-item" @click="toggleChatLock">
                    <i
                      :class="isChatLocked ? 'bi bi-unlock' : 'bi bi-lock'"
                    ></i>
                    {{
                      isChatLocked
                        ? $t("chat.unlock_chat") || "Unlock Chat"
                        : $t("chat.lock_chat") || "Lock Chat"
                    }}
                  </button>
                </li>
                <li>
                  <button class="dropdown-item text-danger" @click="leaveGroup">
                    <i class="bi bi-box-arrow-right"></i>
                    {{ $t("chat.leave_group") || "Leave Group" }}
                  </button>
                </li>
              </template>
              <!-- DM actions (Block/Unblock) -->
              <template v-else>
                <li v-if="!isBlocked">
                  <button class="dropdown-item text-danger" @click="blockUser">
                    <i class="bi bi-slash-circle"></i>
                    {{ $t("chat.block_user") || "Block User" }}
                  </button>
                </li>
                <li v-else-if="isUserBlocker">
                  <button
                    class="dropdown-item text-success"
                    @click="unblockUser"
                  >
                    <i class="bi bi-check-circle"></i>
                    {{ $t("chat.unblock_user") || "Unblock User" }}
                  </button>
                </li>
              </template>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- In-Conversation Search Bar (Flutter sync: _isSearchMode) -->
    <!-- Moved outside the header so it remains visible in embedded mode -->
    <transition name="search-slide">
      <div v-if="isSearchMode" class="chat-search-bar" :class="{ 'chat-search-bar--embedded': embedded }">
        <div class="chat-search-bar__input-wrapper">
          <i class="bi bi-search"></i>
          <input
            ref="searchInput"
            v-model="searchQuery"
            type="text"
            :placeholder="$t('chat.search_messages') || 'Search messages...'"
            @input="onSearchInput"
            @keydown.escape="toggleSearchMode"
            @keydown.enter="navigateSearchResult('next')"
          />
          <button 
            v-if="searchQuery" 
            class="search-clear" 
            @click="clearSearch"
            :title="$t('chat.clear_search') || 'Clear search'"
          >
            <i class="bi bi-x"></i>
          </button>
        </div>
        <div class="chat-search-bar__nav">
          <span v-if="searchResults.length > 0" class="search-count">
            {{ currentSearchResultIndex + 1 }} / {{ searchResults.length }}
          </span>
          <button 
            class="search-nav-btn" 
            @click="navigateSearchResult('prev')"
            :disabled="searchResults.length === 0"
            :title="$t('chat.previous_result') || 'Previous result'"
          >
            <i class="bi bi-chevron-up"></i>
          </button>
          <button 
            class="search-nav-btn" 
            @click="navigateSearchResult('next')"
            :disabled="searchResults.length === 0"
            :title="$t('chat.next_result') || 'Next result'"
          >
            <i class="bi bi-chevron-down"></i>
          </button>
          <button 
            class="search-close-btn" 
            @click="toggleSearchMode"
            :title="$t('chat.close_search') || 'Close search'"
          >
            <i class="bi bi-x-lg"></i>
          </button>
        </div>
      </div>
    </transition>

    <!-- Main Content -->
    <div class="chat-content">
      <!-- Loading State -->
      <div v-if="loading" class="chat-loading">
        <div class="chat-loading__spinner"></div>
        <span>{{ $t("common.loading") || "Loading..." }}</span>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="chat-error">
        <i class="bi bi-exclamation-triangle"></i>
        <p>{{ error }}</p>
        <button class="chat-error__btn" @click="reload">
          {{ $t("common.retry") || "Retry" }}
        </button>
      </div>

      <!-- Chat Body -->
      <div v-else class="chat-body">
        <!-- Connection Status Banner (Flutter sync: _connectionState) -->
        <transition name="slide-down">
          <div 
            v-if="socketConnectionState === 'reconnecting' || socketConnectionState === 'disconnected'"
            class="connection-banner"
            :class="{ 'connection-banner--error': socketConnectionState === 'disconnected' }"
          >
            <i class="bi" :class="socketConnectionState === 'reconnecting' ? 'bi-arrow-repeat spin' : 'bi-wifi-off'"></i>
            <span>{{ 
              socketConnectionState === 'reconnecting' 
                ? ($t('chat.reconnecting') || 'Reconnecting...') 
                : ($t('chat.connection_lost') || 'Connection lost') 
            }}</span>
            <button 
              v-if="socketConnectionState === 'disconnected'"
              class="connection-banner__retry"
              @click="reconnectSocket"
            >
              {{ $t('common.retry') || 'Retry' }}
            </button>
          </div>
        </transition>
        
        <!-- Messages Container -->
        <div ref="list" class="chat-messages" @scroll.passive="onScroll">
          <!-- Loading More Indicator (infinite scroll) -->
          <div v-if="isLoadingMore" class="load-more-indicator">
            <div class="load-more-spinner"></div>
            <span>{{ $t('chat.loading_more') || 'Loading more messages...' }}</span>
          </div>
          <!-- End of Messages Indicator -->
          <div v-else-if="!hasMoreMessages && messagesRaw.length > 20" class="end-of-messages">
            <i class="bi bi-chat-square-text"></i>
            <span>{{ $t('chat.beginning_of_chat') || 'Beginning of conversation' }}</span>
          </div>
          
          <template v-for="(m, index) in displayMessages" :key="m.id">
            <!-- Day Separator (Flutter sync) -->
            <div v-if="m._isDaySeparator" class="day-separator">
              <span class="day-separator__text">{{ m._dateLabel }}</span>
            </div>
            <!-- System Message (Flutter sync: _buildSystemMessage) - centered pill style -->
            <div v-else-if="isSystemMessage(m)" class="system-message">
              <div class="system-message__pill">
                <span class="system-message__text">{{ m.text }}</span>
              </div>
            </div>
            <!-- Regular User Message -->
            <template v-else>
              <!-- Unread Messages Divider -->
              <div v-if="showUnreadDivider(index)" class="unread-divider">
                <span class="unread-divider__text">{{
                  $t("chat.unread_messages") || "Unread Messages"
                }}</span>
              </div>
              <div
                class="msg-row"
                :class="{
                  'msg-row--own': m._isMe,
                  'msg-row--unread': isMessageUnread(m),
                  'msg-row--grouped': !m._showSenderInfo,
                  'msg-row--first-in-group': m._isFirstInGroup,
                  'msg-row--highlighted': highlightedMessageId === m.id,
                  'msg-row--selected': selectedMessageIds.has(m.id),
                  'msg-row--search-match': searchResults.includes(m.id),
                  'msg-row--selection-mode': isSelectionMode,
                }"
                :ref="el => setMessageRef(m.id, el)"
                @click="isSelectionMode && isMe(m) ? toggleMessageSelection(m.id) : null"
                @contextmenu.prevent="isMe(m) && !isSelectionMode ? enterSelectionMode(m) : null"
              >
                <!-- Selection Checkbox (only for own messages in selection mode) -->
                <div 
                  v-if="isSelectionMode && isMe(m)" 
                  class="msg-selection-checkbox"
                  @click.stop="toggleMessageSelection(m.id)"
                >
                  <div 
                    class="checkbox-circle"
                    :class="{ 'checkbox-circle--checked': selectedMessageIds.has(m.id) }"
                  >
                    <i v-if="selectedMessageIds.has(m.id)" class="bi bi-check"></i>
                  </div>
                </div>
                
                <!-- Swipe Reply Indicator (left side for others' messages) -->
                <div 
                  v-if="!isMe(m) && !isSelectionMode" 
                  class="swipe-reply-indicator swipe-reply-indicator--left"
                  :style="getSwipeIndicatorStyle(m)"
                >
                  <i class="bi bi-reply-fill"></i>
                </div>
                
                <!-- Message Bubble with integrated actions -->
                <div 
                  class="msg-bubble-wrapper"
                  :style="getSwipeStyle(m)"
                  @touchstart="!isSelectionMode ? onSwipeTouchStart($event, m) : null"
                  @touchmove="!isSelectionMode ? onSwipeTouchMove : null"
                  @touchend="!isSelectionMode ? onSwipeTouchEnd : null"
                >
                  <div
                    class="msg-bubble"
                    :class="{
                      'msg-bubble--own': isMe(m),
                      'msg-bubble--pending': isPending(m),
                      'msg-bubble--failed': isFailed(m),
                      'msg-bubble--has-media': hasAttachment(m),
                      'msg-bubble--unread': isMessageUnread(m),
                    }"
                  >
                    <!-- Sender Name (for group chats, first message in group, not own message) -->
                    <div 
                      v-if="isGroupChat && !isMe(m) && m._isFirstInGroup" 
                      class="msg-sender-name"
                      :style="{ color: getSenderColor(m) }"
                    >
                      {{ getSenderName(m) }}
                    </div>

                    <!-- Reply Preview (clickable for scroll-to-reply) -->
                    <div 
                      v-if="replyOf(m)" 
                      class="msg-reply-preview"
                      @click="onReplyPreviewClick(replyOf(m))"
                    >
                      <div class="msg-reply-preview__bar"></div>
                      <div class="msg-reply-preview__content">
                        <span class="msg-reply-preview__sender">{{
                          $t("common.reply") || "رد"
                        }}</span>
                        <span class="msg-reply-preview__text">{{
                          replySnippet(replyOf(m))
                        }}</span>
                      </div>
                    </div>

                    <!-- Media Content -->
                    <div v-if="hasAttachment(m)" class="msg-media">
                      <AttachmentRenderer
                        :message="m"
                        :is-me="isMe(m)"
                        @open-lightbox="openLightbox"
                      />
                    </div>

                    <!-- Text Content -->
                    <p
                      v-if="m.text && String(m.text).trim().length > 0"
                      class="msg-text"
                      v-html="formatMessageText(m)"
                    ></p>

                    <!-- Footer with time and status -->
                    <div class="msg-meta">
                      <span v-if="m.editedAt" class="msg-meta__edited">{{
                        $t("common.edited") || "edited"
                      }}</span>
                      <span class="msg-meta__time">{{
                        formatTime(m.createdAt)
                      }}</span>
                      <span
                        v-if="isPending(m)"
                        class="msg-meta__status msg-meta__status--pending"
                      ></span>
                      <span
                        v-else-if="isFailed(m)"
                        class="msg-meta__status msg-meta__status--failed"
                        ><i class="bi bi-exclamation-circle-fill"></i
                      ></span>
                      <span
                        v-else-if="isMe(m) && isSeenByRecipient(m)"
                        class="msg-meta__status msg-meta__status--seen"
                        ><i class="bi bi-check2-all"></i
                      ></span>
                      <span
                        v-else-if="isMe(m)"
                        class="msg-meta__status msg-meta__status--sent"
                        ><i class="bi bi-check2"></i
                      ></span>
                    </div>

                    <!-- Upload Progress with Cancel Button -->
                    <div
                      v-if="
                        isPending(m) &&
                        m.custom &&
                        m.custom.uploadProgress !== undefined
                      "
                      class="msg-progress-wrapper"
                    >
                      <div class="msg-progress">
                        <div
                          class="msg-progress__bar"
                          :style="{ width: m.custom.uploadProgress + '%' }"
                        ></div>
                      </div>
                      <button 
                        v-if="currentUploadController"
                        class="msg-progress__cancel"
                        @click.stop="cancelCurrentUpload"
                        :title="$t('common.cancel') || 'Cancel'"
                      >
                        <i class="bi bi-x"></i>
                      </button>
                    </div>

                    <!-- Hover Actions (inside bubble) -->
                    <div
                      v-if="!isPending(m) && !isFailed(m)"
                      class="msg-actions"
                    >
                      <button
                        class="msg-actions__emoji"
                        @click.stop="openEnhancedEmojiPicker(m)"
                        :title="$t('chat.react') || 'React'"
                      >
                        <i class="bi bi-emoji-smile"></i>
                      </button>
                      <button
                        class="msg-actions__menu"
                        @click.stop="openMessageMenu(m, $event)"
                        :title="$t('common.more') || 'More'"
                      >
                        <i class="bi bi-chevron-down"></i>
                      </button>
                    </div>
                  </div>

                  <!-- Reactions (below bubble) -->
                  <div v-if="hasReactions(m)" class="msg-reactions">
                    <button
                      v-for="(data, emoji) in reactionMap(m)"
                      :key="emoji"
                      class="msg-reactions__item"
                      :class="{
                        'msg-reactions__item--active': hasReacted(m, emoji),
                      }"
                      @click.stop="toggleReaction(m, emoji)"
                    >
                      {{ emoji }} <span class="msg-reactions__count">{{ data.count }}</span>
                    </button>
                  </div>
                </div>

                <!-- Failed Message Actions -->
                <div v-if="isFailed(m)" class="message-failed-actions">
                  <button
                    class="failed-action failed-action--retry"
                    @click="resendFailedMessage(m)"
                  >
                    <i class="bi bi-arrow-clockwise"></i>
                    {{ $t("common.retry") || "إعادة المحاولة" }}
                  </button>
                  <button
                    class="failed-action failed-action--delete"
                    @click="deleteFailedMessage(m)"
                  >
                    <i class="bi bi-trash3"></i>
                  </button>
                </div>
                
                <!-- Swipe Reply Indicator (right side for own messages) -->
                <div 
                  v-if="isMe(m)" 
                  class="swipe-reply-indicator swipe-reply-indicator--right"
                  :style="getSwipeIndicatorStyle(m)"
                >
                  <i class="bi bi-reply-fill"></i>
                </div>
              </div>
            </template>
          </template>
        </div>

        <!-- Reply Banner -->
        <transition name="reply-slide">
          <div v-if="replyingTo" class="reply-banner">
            <div class="reply-banner__icon">
              <i class="bi bi-reply-fill"></i>
            </div>
            <div class="reply-banner__content">
              <div class="reply-banner__header">
                <i class="bi bi-chat-quote-fill reply-banner__quote-icon"></i>
                <span class="reply-banner__label">{{
                  $t("common.replying_to") || "الرد على"
                }}</span>
              </div>
              <span class="reply-banner__text">{{
                replyingTo.text || attachmentLabel(replyingTo)
              }}</span>
            </div>
            <button class="reply-banner__close" @click="cancelReply" :title="$t('common.cancel') || 'إلغاء'">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
        </transition>

        <!-- Blocker Banner (Flutter sync: _buildBlockerBanner - when user has blocked someone) -->
        <div v-if="isUserBlocker && !isGroupChat" class="chat-blocker-banner">
          <div class="chat-blocker-banner__content">
            <i class="bi bi-slash-circle"></i>
            <span>{{
              $t("chat.you_blocked_user") ||
              "You have blocked this user. They cannot send you messages."
            }}</span>
          </div>
          <button class="chat-blocker-banner__unblock" @click="unblockUser">
            <i class="bi bi-unlock"></i>
            {{ $t("chat.unblock") || "Unblock" }}
          </button>
        </div>

        <!-- Owner Chat Locked Banner (Flutter sync: _buildOwnerChatLockedBanner) -->
        <div
          v-if="isChatLocked && isCurrentUserOwner && canSend"
          class="chat-owner-locked-banner"
        >
          <i class="bi bi-lock"></i>
          <span>{{
            $t("chat.locked_owner_hint") ||
            "Chat is locked. Only you can send messages."
          }}</span>
        </div>

        <!-- Chat Input -->
        <div class="chat-input-area">
          <!-- Show blocked banner when user is blocked by the other -->
          <div v-if="isBlocked && isUserBlocked" class="chat-blocked-banner">
            <i class="bi bi-lock-fill"></i>
            <span>{{ cannotSendReason }}</span>
          </div>
          <!-- Show locked banner when chat is locked (not blocked, and user is not the blocker) -->
          <div v-else-if="!canSend && isChatLocked && !isUserBlocker" class="chat-blocked-banner">
            <i class="bi bi-lock-fill"></i>
            <span>{{ cannotSendReason }}</span>
          </div>
          <div v-else-if="canSend" class="chat-input">
            <button
              class="chat-input__action"
              @click.prevent="openFilePicker"
              :title="$t('chat.attach_file') || 'Attach'"
            >
              <i class="bi bi-paperclip"></i>
            </button>
            <div class="chat-input__field-wrapper">
              <input
                ref="chatInput"
                v-model="text"
                @keyup.enter="send"
                @input="onTextInput"
                @blur="handleTypingChanged(false)"
                class="chat-input__field"
                type="text"
                :placeholder="$t('common.type_message') || 'Type a message...'"
              />
            </div>
            <button
              class="chat-input__action chat-input__action--voice"
              :class="{ recording: isRecording }"
              @click.prevent="toggleRecording"
              :title="
                isRecording
                  ? $t('chat.stop_recording') || 'Stop'
                  : $t('chat.record_voice') || 'Voice'
              "
            >
              <i v-if="!isRecording" class="bi bi-mic"></i>
              <i v-else class="bi bi-stop-circle-fill"></i>
            </button>
            <button
              class="chat-input__send"
              :disabled="isSending || (!text.trim() && !isRecording)"
              @click="send"
              :title="$t('common.send') || 'Send'"
            >
              <span v-if="isSending" class="chat-input__send-spinner"></span>
              <i v-else class="bi bi-send-fill" style="color: white !important;"></i>
            </button>
          </div>
          <input
            ref="fileInput"
            type="file"
            class="d-none"
            @change="onFileSelected"
            accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar"
          />
        </div>
      </div>
    </div>

    <!-- Emoji Picker Popup (for quick reactions) -->
    <transition name="fade">
      <div
        v-if="showEmojiPicker"
        class="emoji-picker-popup"
        :style="emojiPickerPosition"
        @click.stop
      >
        <button
          v-for="emoji in emojiOptions"
          :key="emoji"
          class="emoji-pick-btn"
          @click="selectEmojiForMessage(emoji)"
        >
          {{ emoji }}
        </button>
      </div>
    </transition>
    <div
      v-if="showEmojiPicker"
      class="emoji-picker-backdrop"
      @click="closeEmojiPicker"
    ></div>

    <!-- Edit Modal -->
    <transition name="fade">
      <div v-if="showEditModal" class="modal-overlay" @click="closeEditModal">
        <div class="edit-modal" @click.stop>
          <div class="edit-modal__header">
            <h3>{{ $t("common.edit_message") || "Edit Message" }}</h3>
            <button class="edit-modal__close" @click="closeEditModal">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
          <div class="edit-modal__body">
            <textarea
              ref="editTextarea"
              v-model="editText"
              class="edit-modal__input"
              rows="4"
              :placeholder="$t('common.type_message') || 'Type a message...'"
            ></textarea>
          </div>
          <div class="edit-modal__footer">
            <button
              class="edit-modal__btn edit-modal__btn--cancel"
              @click="closeEditModal"
            >
              {{ $t("common.cancel") || "Cancel" }}
            </button>
            <button
              class="edit-modal__btn edit-modal__btn--save"
              :disabled="!editText.trim() || editText === editOriginalText"
              @click="confirmEdit"
            >
              {{ $t("common.save") || "Save" }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Delete Confirmation Modal -->
    <transition name="fade">
      <div
        v-if="showDeleteModal"
        class="modal-overlay"
        @click="closeDeleteModal"
      >
        <div class="confirm-modal" @click.stop>
          <div class="confirm-modal__icon confirm-modal__icon--danger">
            <i class="bi bi-trash3"></i>
          </div>
          <h3 class="confirm-modal__title">
            {{ $t("chat.delete_message") || "حذف الرسالة" }}
          </h3>
          <p class="confirm-modal__text">
            {{
              $t("chat.delete_message_confirm") ||
              "هل أنت متأكد من حذف هذه الرسالة؟ لا يمكن التراجع عن هذا الإجراء."
            }}
          </p>
          <div class="confirm-modal__actions">
            <button
              class="confirm-modal__btn confirm-modal__btn--cancel"
              @click="closeDeleteModal"
            >
              {{ $t("common.cancel") || "إلغاء" }}
            </button>
            <button
              class="confirm-modal__btn confirm-modal__btn--danger"
              @click="confirmDelete"
            >
              {{ $t("common.delete") || "حذف" }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Bulk Delete Confirmation Modal -->
    <transition name="fade">
      <div
        v-if="showBulkDeleteModal"
        class="modal-overlay"
        @click="closeBulkDeleteModal"
      >
        <div class="confirm-modal" @click.stop>
          <div class="confirm-modal__icon confirm-modal__icon--danger">
            <i class="bi bi-trash3"></i>
          </div>
          <h3 class="confirm-modal__title">
            {{ $t("chat.delete_messages") || "حذف الرسائل" }}
          </h3>
          <p class="confirm-modal__text">
            {{
              $t("chat.delete_selected_confirmation", { count: selectedMessageIds.size }) ||
              `هل تريد حذف ${selectedMessageIds.size} رسالة محددة؟ لا يمكن التراجع عن هذا الإجراء.`
            }}
          </p>
          <div class="confirm-modal__actions">
            <button
              class="confirm-modal__btn confirm-modal__btn--cancel"
              @click="closeBulkDeleteModal"
              :disabled="bulkDeleteInProgress"
            >
              {{ $t("common.cancel") || "إلغاء" }}
            </button>
            <button
              class="confirm-modal__btn confirm-modal__btn--danger"
              @click="confirmBulkDelete"
              :disabled="bulkDeleteInProgress"
            >
              <span v-if="bulkDeleteInProgress" class="btn-spinner"></span>
              <span v-else>{{ $t("common.delete") || "حذف" }}</span>
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Image Lightbox -->
    <transition name="fade">
      <div v-if="lightboxImage" class="lightbox" @click="closeLightbox">
        <button class="lightbox__close" @click="closeLightbox">
          <i class="bi bi-x-lg"></i>
        </button>
        <img :src="lightboxImage" class="lightbox__image" @click.stop />
      </div>
    </transition>

    <!-- Message Options Menu (WhatsApp-style) -->
    <transition name="menu-fade">
      <div
        v-if="messageMenuOpenId"
        ref="messageMenuDropdown"
        class="message-options-menu"
        :style="messageMenuPosition"
        @click.stop
      >
        <div class="message-options-menu__content">
          <button
            class="message-options-menu__item"
            @click="handleMenuAction('reply', getMessageById(messageMenuOpenId))"
          >
            <i class="bi bi-reply-fill"></i>
            <span>{{ $t('common.reply') || 'Reply' }}</span>
          </button>
          <button
            class="message-options-menu__item"
            @click="handleMenuAction('forward', getMessageById(messageMenuOpenId))"
          >
            <i class="bi bi-forward-fill"></i>
            <span>{{ $t('chat.forward') || 'Forward' }}</span>
          </button>
          <button
            class="message-options-menu__item"
            v-if="getMessageById(messageMenuOpenId)?.text"
            @click="handleMenuAction('copy', getMessageById(messageMenuOpenId))"
          >
            <i class="bi bi-copy"></i>
            <span>{{ $t('chat.copy') || 'Copy' }}</span>
          </button>
          <button
            class="message-options-menu__item"
            @click="handleMenuAction('star', getMessageById(messageMenuOpenId))"
          >
            <i :class="starredMessages[messageMenuOpenId] ? 'bi bi-star-fill' : 'bi bi-star'"></i>
            <span>{{ starredMessages[messageMenuOpenId] ? ($t('chat.unstar') || 'Unstar') : ($t('chat.star') || 'Star') }}</span>
          </button>
          <button
            class="message-options-menu__item"
            @click="handleMenuAction('select', getMessageById(messageMenuOpenId))"
          >
            <i class="bi bi-check-square"></i>
            <span>{{ $t('chat.select') || 'Select' }}</span>
          </button>
          <button
            class="message-options-menu__item"
            v-if="isGroupChat && !isMe(getMessageById(messageMenuOpenId))"
            @click="handleMenuAction('replyPrivately', getMessageById(messageMenuOpenId))"
          >
            <i class="bi bi-chat-left-text"></i>
            <span>{{ $t('chat.reply_privately') || 'Reply Privately' }}</span>
          </button>
          <div class="message-options-menu__divider"></div>
          <button
            class="message-options-menu__item"
            v-if="isMe(getMessageById(messageMenuOpenId))"
            @click="handleMenuAction('info', getMessageById(messageMenuOpenId))"
          >
            <i class="bi bi-info-circle"></i>
            <span>{{ $t('chat.message_info') || 'Message Info' }}</span>
          </button>
          <button
            class="message-options-menu__item"
            v-if="isMe(getMessageById(messageMenuOpenId)) && !hasAttachment(getMessageById(messageMenuOpenId)) && getMessageById(messageMenuOpenId)?.text"
            @click="handleMenuAction('edit', getMessageById(messageMenuOpenId))"
          >
            <i class="bi bi-pencil"></i>
            <span>{{ $t('common.edit') || 'Edit' }}</span>
          </button>
          <button
            class="message-options-menu__item message-options-menu__item--danger"
            v-if="isMe(getMessageById(messageMenuOpenId))"
            @click="handleMenuAction('delete', getMessageById(messageMenuOpenId))"
          >
            <i class="bi bi-trash"></i>
            <span>{{ $t('common.delete') || 'Delete' }}</span>
          </button>
        </div>
      </div>
    </transition>

    <!-- Mia Summary Dialog (Flutter sync: _showChatSummary) -->
    <transition name="fade">
      <div
        v-if="showSummaryDialog"
        class="chat-summary-overlay"
        @click.self="showSummaryDialog = false"
      >
        <div class="chat-summary-dialog">
          <div class="chat-summary-dialog__header">
            <img src="/assets/img/mia_icon.png" alt="Mia" class="mia-icon-small" />
            <span>{{
              $t("chat.summarize_chat_mia") || "Summarize Chat with Mia"
            }}</span>
            <button
              class="chat-summary-dialog__close"
              @click="showSummaryDialog = false"
            >
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
          <div class="chat-summary-dialog__body">
            <!-- Date Preset Chips -->
            <div class="chat-summary-presets">
              <button
                class="chat-summary-chip"
                :class="{ active: summaryPreset === 'today' }"
                :disabled="!!summaryResult"
                @click="selectSummaryPreset('today')"
              >
                {{ $t("chat.today") || "Today" }}
              </button>
              <button
                class="chat-summary-chip"
                :class="{ active: summaryPreset === 'yesterday' }"
                :disabled="!!summaryResult"
                @click="selectSummaryPreset('yesterday')"
              >
                {{ $t("chat.yesterday") || "Yesterday" }}
              </button>
              <button
                class="chat-summary-chip"
                :class="{ active: summaryPreset === 'custom' }"
                :disabled="!!summaryResult"
                @click="selectSummaryPreset('custom')"
              >
                {{ $t("chat.custom") || "Custom" }}
              </button>
            </div>
            <!-- Custom Date Pickers -->
            <div v-if="summaryPreset === 'custom'" class="chat-summary-dates">
              <label class="chat-summary-date-label">
                <i class="bi bi-calendar3"></i>
                {{ $t("chat.start_date") || "Start Date" }}
                <input
                  type="date"
                  v-model="summaryStartDate"
                  class="chat-summary-date-input"
                  :max="summaryEndDate || todayStr"
                  :disabled="!!summaryResult"
                />
              </label>
              <label class="chat-summary-date-label">
                <i class="bi bi-calendar3"></i>
                {{ $t("chat.end_date") || "End Date" }}
                <input
                  type="date"
                  v-model="summaryEndDate"
                  class="chat-summary-date-input"
                  :min="summaryStartDate"
                  :max="todayStr"
                  :disabled="!!summaryResult"
                />
              </label>
            </div>
            <!-- Summary Result -->
            <div v-if="summaryLoading" class="chat-summary-loading">
              <div class="chat-loading__spinner"></div>
              <span>{{
                $t("chat.generating_summary") || "Generating summary..."
              }}</span>
            </div>
            <div v-else-if="summaryResult" class="chat-summary-result">
              <div class="chat-summary-result__header">
                <div class="chat-summary-result__title">
                  <img src="/assets/img/mia_icon.png" alt="Mia" class="mia-icon-small" />
                  <div class="chat-summary-result__title-text">
                    <span>{{ $t("chat.chat_summary") || "Chat Summary" }}</span>
                    <span
                      v-if="summaryMessageCount"
                      class="chat-summary-result__count"
                      >{{ summaryMessageCount }}
                      {{ $t("chat.messages") || "messages" }}</span
                    >
                  </div>
                </div>
                <div class="chat-summary-result__actions">
                  <button
                    class="chat-summary-action-btn"
                    @click="copySummary"
                    :title="$t('chat.copy_summary') || 'نسخ الملخص'"
                  >
                    <i class="bi bi-clipboard"></i>
                  </button>
                  <button
                    class="chat-summary-action-btn"
                    @click="shareSummary"
                    :title="$t('chat.share_summary') || 'Share Summary'"
                  >
                    <i class="bi bi-share"></i>
                  </button>
                </div>
              </div>
              <div class="chat-summary-result__text">{{ summaryResult }}</div>
              <div v-if="summaryMetadata" class="chat-summary-result__footer">
                <div class="chat-summary-mia-badge">
                  <img src="/assets/img/mia_icon.png" alt="Mia" class="mia-icon-small" />
                  <span>{{
                    $t("chat.created_by_mia") || "Created by Mia AI"
                  }}</span>
                </div>
              </div>
            </div>
            <div v-else-if="summaryError" class="chat-summary-error">
              <i class="bi bi-exclamation-triangle"></i>
              <span>{{ summaryError }}</span>
            </div>
          </div>
          <div class="chat-summary-dialog__footer">
            <button
              class="chat-summary-btn--cancel"
              @click="showSummaryDialog = false"
            >
              {{ $t("common.cancel") || "Cancel" }}
            </button>
            <button
              class="chat-summary-btn--submit"
              :disabled="isSummaryButtonDisabled"
              @click.prevent="generateSummary"
            >
              <img src="/assets/img/mia_icon.png" alt="Mia" class="mia-icon-small" />
              {{ $t("chat.summarize") || "Summarize" }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ==================== NEW FEATURE COMPONENTS (Flutter Parity) ==================== -->

    <!-- Message Info Sheet -->
    <MessageInfoSheet
      v-if="messageInfoTarget"
      :visible="showMessageInfoSheet"
      :message="messageInfoTarget"
      :conversation-id="conversationId"
      :participants="participantsMap"
      @close="closeMessageInfo"
    />

    <!-- Enhanced Emoji Picker Sheet -->
    <EmojiPickerSheet
      :visible="showEmojiPickerSheet"
      :user-reacted-emojis="emojiPickerTargetMessage ? getUserReactedEmojis(emojiPickerTargetMessage) : new Set()"
      @close="closeEnhancedEmojiPicker"
      @select="onEmojiSelected"
    />

    <!-- Mute Chat Dialog -->
    <MuteChatDialog
      :visible="showMuteDialog"
      :conversation-id="conversationId"
      :is-muted="isMuted"
      :conversation-name="headerTitle"
      @close="closeMuteDialog"
      @muted="onMuted"
      @unmuted="onUnmuted"
    />

    <!-- Chat Moderation Sheet (for group owners) -->
    <ChatModerationSheet
      v-if="isGroupChat && isCurrentUserOwner"
      :visible="showModerationSheet"
      :conversation-id="conversationId"
      :is-chat-locked="isChatLocked"
      :participants="participantsMap"
      @close="closeModerationSheet"
      @action-complete="onModerationAction"
    />
  </div>
</template>

<script>
// This component adapts Flutter's CustomChatScreen to Vue Options API
// Full Flutter parity implementation:
// - Real-time Pusher events (like Flutter's _subscribeToPusher + _handlePusherEvent)
// - Polling as fallback every 15s (like Flutter's _startReactionPoll)
// - Optimistic send with pending/failed states
// - Reply support via referencedMessageId
// - Read receipts trigger via markAsRead on new messages
// - Typing indicators (send + receive)
// - Message grouping by sender & time

import { ChatService } from "@/services/chat";
import { chatState, updateLastMessage, bumpConversationToTop } from "@/stores/chat";
import { pusherService } from "@/services/pusher";
import VoiceMessagePlayer from "@/components/chat/VoiceMessagePlayer.vue";
import MessageInfoSheet from "@/components/chat/MessageInfoSheet.vue";
import EmojiPickerSheet from "@/components/chat/EmojiPickerSheet.vue";
import MuteChatDialog from "@/components/chat/MuteChatDialog.vue";
import ChatModerationSheet from "@/components/chat/ChatModerationSheet.vue";
import ChatVideoPlayer from "./ChatVideoPlayer.vue";

export default {
  name: "ChatConversation",
  props: {
    conversationId: { type: String, required: true },
    embedded: { type: Boolean, default: false },
  },
  emits: ['muted', 'unmuted'],
  components: {
    AttachmentRenderer: {
      props: {
        message: { type: Object, required: true },
        isMe: { type: Boolean, default: false },
      },
      computed: {
        // Like Flutter's message.fileData getter - extract file data from content or attachment
        // Flutter: checks content[].type === 'file' first, then falls back to attachment
        fileData() {
          const m = this.message || {};

          // Check content field first (has complete file metadata) - like Flutter
          if (Array.isArray(m.content) && m.content.length) {
            // Find file block in content array
            const fileBlock = m.content.find(
              (block) => block && block.type === "file"
            );
            if (fileBlock) {
              return fileBlock;
            }
            // Also check if first item is a file-like object (might not have type field)
            const first = m.content[0];
            if (
              first &&
              (first.url || first.fileUrl || first.link || first.fileToken)
            ) {
              return first;
            }
          }

          // Fall back to attachment field (might be missing subtype) - like Flutter
          if (m.attachment && typeof m.attachment === "object") {
            return m.attachment;
          }

          // Also check if attachment is a string URL
          if (m.attachment && typeof m.attachment === "string") {
            return { url: m.attachment };
          }

          // Check custom.localPath for pending messages
          if (m.custom && m.custom.localPath) {
            return { url: m.custom.localPath };
          }

          return null;
        },
        subtype() {
          const fd = this.fileData;
          // Check direct subtype on file data
          if (fd && fd.subtype) {
            // Normalize 'audio' to 'voice' for consistency
            if (fd.subtype === "audio") return "voice";
            return fd.subtype;
          }

          // Fallback to message_type from custom field (like Flutter)
          const m = this.message || {};
          const t = m.custom && m.custom.message_type;
          if (t === "audio") return "voice";
          if (t === "media") {
            // Detect image vs video by extension
            const name = fd && (fd.filename || fd.name);
            const url = this.fileUrl;
            const isVid =
              /\.(mp4|mov|webm|ogg|m4v)$/i.test(name || "") ||
              /\.(mp4|mov|webm|ogg|m4v)$/i.test(url || "");
            return isVid ? "video" : "image";
          }
          if (t === "attachment") return "document";

          // Try to infer from file extension
          const url = this.fileUrl;
          const name = fd && (fd.filename || fd.name);
          const pathOrName = url || name || "";
          if (/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(pathOrName))
            return "image";
          if (/\.(mp4|mov|webm|ogg|m4v|avi)$/i.test(pathOrName)) return "video";
          if (/\.(mp3|wav|ogg|m4a|webm|aac)$/i.test(pathOrName)) return "voice";

          return fd ? "document" : null;
        },
        fileUrl() {
          const m = this.message || {};
          const fd = this.fileData;

          // Get server URL from fileData (content[0] or attachment)
          // Based on real API response: content[0].url or attachment.url
          const serverUrl = fd && (
            fd.url ||
            fd.fileUrl ||
            fd.link ||
            fd.downloadUrl ||
            fd.src ||
            fd.path ||
            fd.file_url
          );

          // If we have a server URL, prefer it (message is confirmed)
          // This ensures images work after upload without refresh
          if (serverUrl && !serverUrl.startsWith('blob:')) {
            return serverUrl;
          }

          // For pending uploads, use local blob URL for preview
          // Only use localPath if message is still pending AND no server URL exists
          if (m.custom && m.custom.isPending && m.custom.localPath) {
            return m.custom.localPath;
          }

          // Fallback to localPath only if no server URL (for smooth transition during upload)
          if (m.custom && m.custom.localPath && !serverUrl) {
            return m.custom.localPath;
          }

          return serverUrl || null;
        },
        // Get dimensions from attachment if available (for images/videos)
        dimensions() {
          const fd = this.fileData;
          if (!fd) return null;
          const dims = fd.dimensions || {};
          return {
            width: dims.width || fd.width || null,
            height: dims.height || fd.height || null,
          };
        },
        filename() {
          const fd = this.fileData;
          return (
            (fd && (fd.filename || fd.name || fd.file_name || fd.fileName)) ||
            null
          );
        },
        duration() {
          const m = this.message || {};
          // Check multiple possible field names for duration
          // 1. First check custom field (like Flutter's voice_duration)
          let d =
            m.custom &&
            (m.custom.voice_duration ||
              m.custom.duration ||
              m.custom.voiceDuration ||
              m.custom.durationSeconds);
          if (d) return parseInt(d);

          // 2. Check fileData for duration
          const fd = this.fileData;
          if (fd && fd.duration) return parseInt(fd.duration);

          // 3. Check content[0].duration
          if (
            Array.isArray(m.content) &&
            m.content[0] &&
            m.content[0].duration
          ) {
            return parseInt(m.content[0].duration);
          }

          return null;
        },
        // Get waveform data for voice messages if available
        // NOTE: waveformData may be a JSON string (from TalkJS) or array (from local pending)
        waveformData() {
          const m = this.message || {};
          const raw = m.custom && m.custom.waveformData;
          if (!raw) return null;
          // If it's already an array, return it directly (local pending message)
          if (Array.isArray(raw)) return raw;
          // If it's a string, try to parse it as JSON (from TalkJS API)
          if (typeof raw === 'string') {
            try {
              const parsed = JSON.parse(raw);
              return Array.isArray(parsed) ? parsed : null;
            } catch (_) {
              return null;
            }
          }
          return null;
        },
        isPending() {
          const m = this.message || {};
          return m.custom && m.custom.isPending === true;
        },
        isFailed() {
          const m = this.message || {};
          return m.custom && m.custom.isFailed === true;
        },
        uploadProgress() {
          const m = this.message || {};
          return m.custom && m.custom.uploadProgress;
        },
        videoMimeType() {
          const fd = this.fileData;
          // 1. Check for MIME type in file metadata (most reliable)
          if (fd) {
            const mime = fd.mimeType || fd.mime_type || fd.contentType || fd.content_type || fd.type;
            if (mime && mime.startsWith('video/')) return mime;
          }
          // 2. Fall back to extension-based detection
          const url = this.fileUrl || '';
          const name = this.filename || '';
          const pathOrName = (name || url).toLowerCase();
          if (/\.mp4$/i.test(pathOrName)) return 'video/mp4';
          if (/\.webm$/i.test(pathOrName)) return 'video/webm';
          if (/\.ogg$/i.test(pathOrName) || /\.ogv$/i.test(pathOrName)) return 'video/ogg';
          if (/\.mov$/i.test(pathOrName)) return 'video/quicktime';
          if (/\.m4v$/i.test(pathOrName)) return 'video/x-m4v';
          if (/\.avi$/i.test(pathOrName)) return 'video/x-msvideo';
          if (/\.3gp$/i.test(pathOrName)) return 'video/3gpp';
          if (/\.mkv$/i.test(pathOrName)) return 'video/x-matroska';
          // 3. Return null to let browser auto-detect (critical for Firefox compatibility)
          return null;
        },
      },
      methods: {
        label() {
          const st = this.subtype;
          if (st === "image")
            return this.$parent.$t("chat.msg_photo") || "Photo";
          if (st === "video")
            return this.$parent.$t("chat.msg_video") || "Video";
          if (st === "voice")
            return this.$parent.$t("chat.msg_voice") || "Voice";
          return this.$parent.$t("chat.msg_document") || "Attachment";
        },
        handleImageError(e) {
          // Show placeholder on image load error
          e.target.style.display = "none";
          console.warn(
            "[AttachmentRenderer] Image failed to load:",
            this.fileUrl
          );
        },
        formatDuration(seconds) {
          const mins = Math.floor(seconds / 60);
          const secs = seconds % 60;
          return mins + ":" + String(secs).padStart(2, "0");
        },
        getFileIcon() {
          const name = (this.filename || "").toLowerCase();
          if (/\.pdf$/.test(name)) return "bi bi-file-earmark-pdf-fill";
          if (/\.(doc|docx)$/.test(name)) return "bi bi-file-earmark-word-fill";
          if (/\.(xls|xlsx)$/.test(name))
            return "bi bi-file-earmark-excel-fill";
          if (/\.(ppt|pptx)$/.test(name)) return "bi bi-file-earmark-ppt-fill";
          if (/\.(zip|rar|7z)$/.test(name))
            return "bi bi-file-earmark-zip-fill";
          if (/\.(txt|md)$/.test(name)) return "bi bi-file-earmark-text-fill";
          return "bi bi-file-earmark-fill";
        },
        getFileIconClass() {
          const name = (this.filename || "").toLowerCase();
          if (/\.pdf$/.test(name)) return "file-icon--pdf";
          if (/\.(doc|docx)$/.test(name)) return "file-icon--word";
          if (/\.(xls|xlsx)$/.test(name)) return "file-icon--excel";
          if (/\.(ppt|pptx)$/.test(name)) return "file-icon--ppt";
          if (/\.(zip|rar|7z)$/.test(name)) return "file-icon--zip";
          return "file-icon--default";
        },
        getFileExtension() {
          const name = (this.filename || "").toLowerCase();
          const match = name.match(/\.([a-z0-9]+)$/i);
          if (match) return match[1].toUpperCase();
          return "FILE";
        },
        getFilenameWithoutExtension() {
          const name = this.filename || "";
          const lastDot = name.lastIndexOf(".");
          if (lastDot > 0) return name.substring(0, lastDot);
          return name || this.$parent.$t("chat.msg_document") || "Document";
        },
        getFileSize() {
          const fd = this.fileData;
          if (!fd) return null;
          const size = fd.size || fd.fileSize || fd.file_size || fd.bytes;
          if (!size) return null;
          return this.formatFileSize(size);
        },
        formatFileSize(bytes) {
          if (!bytes || bytes === 0) return null;
          const units = ["B", "KB", "MB", "GB"];
          let unitIndex = 0;
          let size = bytes;
          while (size >= 1024 && unitIndex < units.length - 1) {
            size /= 1024;
            unitIndex++;
          }
          return size.toFixed(unitIndex > 0 ? 1 : 0) + " " + units[unitIndex];
        },
        openImagePreview() {
          if (this.fileUrl) {
            this.$emit("open-lightbox", this.fileUrl);
          }
        },
      },
      components: {
        VoiceMessagePlayer,
        ChatVideoPlayer,
      },
      emits: ["open-lightbox"],
      template: `
        <div class="attachment-content">
          <!-- Failed state -->
          <div v-if="isFailed" class="attachment-failed">
            <div class="failed-icon"><i class="bi bi-exclamation-triangle"></i></div>
            <span>{{ $parent.$t('chat.upload_failed') || 'Upload failed' }}</span>
          </div>
          
          <!-- Voice message - WhatsApp-style waveform player (Flutter sync: VoiceMessagePlayer) -->
          <template v-else-if="subtype === 'voice'">
            <!-- Uploading state with waveform preview -->
            <div v-if="isPending" class="voice-message voice-message--uploading">
              <div class="voice-icon">
                <i class="bi bi-mic-fill"></i>
              </div>
              <div class="voice-content">
                <div class="voice-uploading">
                  <div class="voice-waves" :class="{ 'has-waveform': waveformData }">
                    <template v-if="waveformData && waveformData.length">
                      <span v-for="(val, i) in waveformData.slice(0, 20)" :key="i" :style="{ height: Math.max(4, val * 24) + 'px' }"></span>
                    </template>
                    <template v-else>
                      <span></span><span></span><span></span><span></span><span></span>
                    </template>
                  </div>
                  <div v-if="uploadProgress != null" class="upload-progress-mini">
                    <div class="progress-track"><div class="progress-fill" :style="{ width: uploadProgress + '%' }"></div></div>
                    <span>{{ uploadProgress }}%</span>
                  </div>
                  <span v-else class="uploading-text">{{ $parent.$t('chat.uploading') || 'Uploading...' }}</span>
                </div>
              </div>
              <div v-if="duration" class="voice-duration">{{ formatDuration(duration) }}</div>
            </div>
            <!-- Ready to play - use VoiceMessagePlayer component -->
            <VoiceMessagePlayer
              v-else-if="fileUrl"
              :ref="'voice-' + message.id"
              :audio-url="fileUrl"
              :voice-duration="duration || 0"
              :is-me="isMe"
              :waveform-data="waveformData"
              :auto-play="$parent.currentPlayingVoiceId === message.id"
              @playback-complete="$parent.onVoiceMessageEnded(message.id)"
            />
            <!-- Fallback placeholder -->
            <div v-else class="voice-message">
              <div class="voice-icon">
                <i class="bi bi-mic-fill"></i>
              </div>
              <div class="voice-content">
                <div class="voice-placeholder">
                  <div class="voice-waves"><span></span><span></span><span></span><span></span><span></span></div>
                  <span class="voice-label">{{ $parent.$t('chat.msg_voice') || 'Voice message' }}</span>
                </div>
              </div>
              <div v-if="duration" class="voice-duration">{{ formatDuration(duration) }}</div>
            </div>
          </template>
          
          <!-- Image - Clean upload experience -->
          <template v-else-if="subtype === 'image'">
            <div class="image-message">
              <img :src="fileUrl || 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'" alt="image" class="chat-image" :class="{ 'is-uploading': isPending }" :style="{ opacity: fileUrl ? (isPending ? 0.7 : 1) : 0 }" style="width: 220px; height: 220px; max-width: 220px; max-height: 220px; object-fit: cover; transition: opacity 0.3s ease;" @error="handleImageError" @click="openImagePreview" />
              <!-- Elegant upload indicator -->
              <div v-if="isPending" class="image-upload-indicator">
                <i class="bi bi-arrow-up"></i>
                <span v-if="uploadProgress != null && uploadProgress < 100">{{ uploadProgress }}%</span>
              </div>
            </div>
          </template>
          
          <!-- Video - Cross-browser compatible player (Firefox fix) -->
          <template v-else-if="subtype === 'video'">
            <div 
              class="video-message" 
              :class="{ 'is-uploading': isPending }"
              style="display: block; width: 220px; max-width: 220px; height: 180px; max-height: 180px; border-radius: 12px; overflow: hidden; background: #000;"
            >
              <ChatVideoPlayer
                v-if="fileUrl && !isPending"
                :video-url="fileUrl"
                :filename="filename"
                :width="220"
                :height="180"
              />
              <div v-else class="video-placeholder" style="display: flex; flex-direction: column; align-items: center; justify-content: center; width: 100%; height: 100%; background: linear-gradient(135deg, #1f2937 0%, #374151 100%); color: white;">
                <div class="play-icon" style="width: 48px; height: 48px; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.2); border-radius: 50%; font-size: 20px; margin-bottom: 8px;"><i class="bi bi-play-fill"></i></div>
                <div v-if="uploadProgress != null" class="upload-info">
                  <div class="progress-bar-mini"><div :style="{ width: uploadProgress + '%' }"></div></div>
                  <span>{{ uploadProgress }}%</span>
                </div>
                <span v-else>{{ $parent.$t('chat.msg_video') || 'Video' }}</span>
              </div>
            </div>
          </template>
          
          <!-- Document/File - Premium Glass Design -->
          <template v-else-if="fileData || subtype === 'document'">
            <div class="file-card-premium" :class="getFileIconClass()">
              <!-- Decorative background elements -->
              <div class="file-card-bg">
                <div class="file-card-pattern"></div>
                <div class="file-card-glow"></div>
              </div>
              
              <!-- Main content -->
              <div class="file-card-content">
                <!-- File icon with 3D effect -->
                <div class="file-icon-3d" :class="getFileIconClass()">
                  <div class="file-icon-face file-icon-front">
                    <i :class="getFileIcon()"></i>
                  </div>
                  <div class="file-icon-face file-icon-fold"></div>
                  <span class="file-type-badge">{{ getFileExtension() }}</span>
                </div>
                
                <!-- File info -->
                <div class="file-info-section">
                  <h4 class="file-name-title">{{ getFilenameWithoutExtension() }}</h4>
                  <div class="file-meta-row">
                    <span class="file-ext-tag">.{{ getFileExtension().toLowerCase() }}</span>
                    <span v-if="getFileSize()" class="file-size-tag">
                      <i class="bi bi-hdd"></i>
                      {{ getFileSize() }}
                    </span>
                  </div>
                </div>
              </div>
              
              <!-- Action buttons -->
              <div class="file-actions-row">
                <template v-if="fileUrl && !isPending">
                  <a :href="fileUrl" target="_blank" rel="noopener" class="file-action-btn file-action-download" :download="filename">
                    <i class="bi bi-cloud-arrow-down-fill"></i>
                    <span>{{ $parent.$t('common.download') || 'تحميل' }}</span>
                  </a>
                  <a :href="fileUrl" target="_blank" rel="noopener" class="file-action-btn file-action-open">
                    <i class="bi bi-eye-fill"></i>
                    <span>{{ $parent.$t('common.open') || 'فتح' }}</span>
                  </a>
                </template>
                <template v-else-if="isPending">
                  <div class="file-upload-status">
                    <div class="file-upload-bar">
                      <div class="file-upload-fill" :style="{ width: (uploadProgress || 0) + '%' }"></div>
                      <div class="file-upload-shine"></div>
                    </div>
                    <span class="file-upload-text">
                      <i class="bi bi-cloud-arrow-up"></i>
                      {{ uploadProgress != null ? uploadProgress + '%' : ($parent.$t('chat.uploading') || 'جاري الرفع...') }}
                    </span>
                  </div>
                </template>
              </div>
            </div>
          </template>
          
          <!-- Fallback -->
          <template v-else-if="fileData">
            <div class="file-message">
              <div class="file-icon"><i class="bi bi-paperclip"></i></div>
              <div class="file-info">
                <a v-if="fileUrl" :href="fileUrl" target="_blank" rel="noopener" class="file-name">{{ filename || 'Attachment' }}</a>
                <span v-else class="file-name">{{ filename || label() }}</span>
              </div>
            </div>
          </template>
          
          <!-- No data -->
          <template v-else>
            <div class="attachment-empty">
              <i class="bi bi-paperclip"></i>
              <span>{{ label() }}</span>
            </div>
          </template>
        </div>
      `,
    },
    MessageInfoSheet,
    EmojiPickerSheet,
    MuteChatDialog,
    ChatModerationSheet,
  },
  data() {
    return {
      loading: false,
      error: null,
      conversation: null,
      messagesRaw: [], // newest -> oldest (same as Flutter service behavior)
      lastMessageId: null,
      poller: null,
      isSending: false,
      replyingTo: null,
      text: "",
      // send constraints (Flutter sync: _canSendMessages, _isChatLocked, _isBlocked, _isUserBlocker, _isUserBlocked)
      canSend: true,
      isChatLocked: false,
      isBlocked: false, // Is conversation blocked (either direction)
      isUserBlocker: false, // Did current user block the other? (can still send)
      isUserBlocked: false, // Is current user blocked by the other? (cannot send)
      isCurrentUserOwner: false, // Is current user the group owner/creator
      cannotSendReason: "",
      isGroupChat: false, // Is this a group conversation
      replyCache: {},
      // reactions & emoji picker
      emojiOptions: ["❤️", "👍", "😂", "😮", "😢", "🙏"],
      emojiPickerFor: null,
      localReactions: {}, // { [messageId]: Set(emoji) }
      localReactionsStorageKey: null,
      // attachments & voice
      isRecording: false,
      mediaRecorder: null,
      recordChunks: [],
      recordStartedAt: 0,
      // Waveform data capture (Flutter sync: _waveformData)
      audioContext: null,
      analyser: null,
      waveformData: [],
      waveformInterval: null,
      // Pending message (like Flutter's _pendingMessage - separate from messagesRaw)
      pendingMessage: null,
      // Emoji picker popup
      showEmojiPicker: false,
      emojiPickerMessage: null,
      emojiPickerPosition: { top: "0px", left: "0px" },
      // Modals
      showEditModal: false,
      editText: "",
      editOriginalText: "",
      editMessageId: null,
      showDeleteModal: false,
      deleteMessageId: null,
      showBulkDeleteModal: false,
      bulkDeleteInProgress: false,
      // Message options menu (WhatsApp-style)
      messageMenuOpenId: null,
      messageMenuPosition: { top: "0px", right: "0px" },
      // Lightbox
      lightboxImage: null,
      // ==================== Pusher / Real-time (Flutter sync) ====================
      pusherUnsubscribe: null, // Function to unsubscribe from Pusher events
      // Typing indicators (Flutter sync: _isOtherUserTyping, _typingUserName)
      isOtherUserTyping: false,
      typingUserName: null,
      typingTimeout: null, // Auto-clear typing after 4s
      typingTimer: null, // Timer for auto-stop sending typing
      // Reaction poll timer (fallback, like Flutter's _reactionPollTimer)
      reactionPollTimer: null,
      // Mia Summary dialog (Flutter sync: _showChatSummary)
      showSummaryDialog: false,
      summaryPreset: "today",
      summaryStartDate: "",
      summaryEndDate: "",
      summaryLoading: false,
      summaryResult: null,
      summaryError: null,
      summaryMessageCount: null,
      summaryMetadata: null,
      // ==================== New Features (Flutter Parity) ====================
      // Message Info Sheet (Flutter sync: MessageInfoSheet)
      showMessageInfoSheet: false,
      messageInfoTarget: null,
      // Enhanced Emoji Picker (Flutter sync: EmojiPickerSheet)
      showEmojiPickerSheet: false,
      emojiPickerTargetMessage: null,
      // Mute Chat Dialog (Flutter sync: MuteChatDialog)
      showMuteDialog: false,
      isMuted: false,
      // Chat Moderation Sheet (Flutter sync: ChatModerationSheet)
      showModerationSheet: false,
      // In-conversation search (Flutter sync: _isSearchMode)
      isSearchMode: false,
      searchQuery: "",
      searchResults: [],
      currentSearchResultIndex: -1,
      // Scroll-to-reply (Flutter sync: _messageKeys, _highlightedMessageId)
      messageRefs: {},
      highlightedMessageId: null,
      highlightTimeout: null,
      // Selection mode (Flutter sync: _isSelectionMode, _selectedMessageIds)
      isSelectionMode: false,
      selectedMessageIds: new Set(),
      // Starred messages (local state, Flutter sync: starred messages)
      starredMessages: {},
      // Infinite scroll pagination (Flutter sync: afterMessageId)
      isLoadingMore: false,
      hasMoreMessages: true,
      oldestMessageId: null,
      // Upload cancel support (Flutter sync: _uploadCancelToken)
      currentUploadController: null,
      // Auto-play voice messages (Flutter sync: _autoPlayMessageId)
      autoPlayNextVoice: false,
      currentPlayingVoiceId: null,
      // Participants map for message info
      participantsMap: {},
      // Swipe-to-reply (Flutter sync: Dismissible)
      swipeStartX: 0,
      swipeStartY: 0,
      swipeCurrentX: 0,
      swipeMessage: null,
      isSwipeTracking: false,
      swipeTriggered: false,
      // Presence/Online Status (Flutter sync: _userOnlineStatus)
      peerOnlineStatus: null, // { isOnline: bool, lastSeen: timestamp }
      onlineStatusPollTimer: null,
      // Socket connection state (Flutter sync: _connectionState)
      socketConnectionState: 'disconnected',
      connectionStateUnsubscribe: null,
      visibilityChangeHandler: null,
      // Performance optimization timers
      scrollDebounceTimer: null,
      typingDebounceTimer: null,
    };
  },
  computed: {
    // Check if summary button should be disabled
    isSummaryButtonDisabled() {
      return this.summaryLoading || !!this.summaryResult;
    },
    // For direct chats, get the other participant's info (not current user)
    directChatPeer() {
      const c =
        (this.conversation && (this.conversation.data || this.conversation)) ||
        {};
      const custom = c.custom || {};
      if (custom.type !== "direct") return null;

      const currentUserId = String(chatState.userId || "");
      let details = [];
      try {
        const raw = custom.participants_details;
        if (typeof raw === "string" && raw.trim().length) {
          details = JSON.parse(raw);
        } else if (Array.isArray(raw)) {
          details = raw;
        }
      } catch (_) {
        return null;
      }

      if (!Array.isArray(details) || !details.length) return null;
      // Find the other participant (not current user)
      const other = details.find((p) => p && String(p.id) !== currentUserId);
      return other || details[0] || null;
    },
    headerTitle() {
      // For direct chats, show the other participant's name
      if (this.directChatPeer && this.directChatPeer.name) {
        return this.directChatPeer.name;
      }
      const c = this.conversation || {};
      
      // Try subject first
      if (c.subject) return c.subject;
      
      // Try name field directly (some APIs return this)
      if (c.name) return c.name;
      
      // Try title field
      if (c.title) return c.title;
      
      // Try participantNames map (from API mapping)
      if (c.participantNames) {
        const names = Object.values(c.participantNames);
        if (names && names.length) {
          // Filter out current user's name if possible
          const currentUserId = String(chatState.userId || "");
          const keys = Object.keys(c.participantNames);
          const otherNames = names.filter((_, idx) => keys[idx] !== currentUserId);
          if (otherNames.length) return otherNames[0];
          return names[0];
        }
      }
      
      // Try participants array (alternative format)
      if (c.participants && Array.isArray(c.participants)) {
        const currentUserId = String(chatState.userId || "");
        const other = c.participants.find(p => String(p.id) !== currentUserId);
        if (other && other.name) return other.name;
        if (c.participants[0] && c.participants[0].name) return c.participants[0].name;
      }
      
      // Try custom.name or custom.title
      const custom = c.custom || {};
      if (custom.name) return custom.name;
      if (custom.title) return custom.title;
      
      return this.$t("common.chat") || "Chat";
    },
    conversationPhoto() {
      // For direct chats, show the other participant's avatar
      if (this.directChatPeer && this.directChatPeer.image) {
        return this.directChatPeer.image;
      }
      const c = this.conversation || {};
      return c.photoUrl || c.photo_url || null;
    },
    avatarInitial() {
      const title = this.headerTitle || "";
      const words = title.trim().split(" ");
      if (words.length === 0) return "?";
      if (words.length === 1) return words[0].charAt(0).toUpperCase();
      return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
    },
    statusText() {
      if (this.isUserBlocked)
        return (
          this.$t("chat.blocked_message") ||
          "You are blocked and cannot send messages"
        );
      if (this.isChatLocked && this.canSend)
        return (
          this.$t("chat.locked_owner_hint") ||
          "Chat is locked (owners can still send)"
        );
      if (this.isChatLocked) return this.$t("chat.locked") || "Chat is locked";
      // Show online status for direct chats
      if (!this.isGroupChat && this.peerOnlineStatus) {
        if (this.peerOnlineStatus.isOnline) {
          return this.$t("chat.online") || "online";
        }
        if (this.peerOnlineStatus.lastSeen) {
          return this.formatLastSeen(this.peerOnlineStatus.lastSeen);
        }
      }
      return this.$t("chat.last_seen_recently") || "last seen recently";
    },
    isOnline() {
      return !this.isGroupChat && this.peerOnlineStatus?.isOnline;
    },
    todayStr() {
      return new Date().toISOString().split("T")[0];
    },
    displayMessages() {
      // UI expects oldest -> newest (messagesRaw is newest->oldest)
      // Append pendingMessage at the end (newest) like Flutter does
      const reversed = [...this.messagesRaw].reverse();
      if (this.pendingMessage) {
        reversed.push(this.pendingMessage);
      }

      // Add grouping info and day separators (Flutter sync: showSenderInfo logic)
      const currentUserId = String(chatState.userId || "");
      const result = [];
      let lastDateStr = null;

      for (let i = 0; i < reversed.length; i++) {
        const msg = reversed[i];
        const msgDate = new Date(msg.createdAt);
        const dateStr = msgDate.toDateString();

        // Add day separator if date changed (Flutter sync: day headers)
        if (dateStr !== lastDateStr) {
          result.push({
            _isDaySeparator: true,
            _dateStr: dateStr,
            _dateLabel: this.formatDateLabel(msgDate),
            id: `day_${dateStr}`,
          });
          lastDateStr = dateStr;
        }

        // Determine if this message should show sender info (Flutter sync: showSenderInfo)
        // Show avatar/tail on LAST message in group (check next message since we're oldest->newest)
        const isMe = String(msg.senderId) === currentUserId;
        let showSenderInfo = true;

        if (i < reversed.length - 1) {
          const nextMsg = reversed[i + 1];
          const nextIsMe = String(nextMsg.senderId) === currentUserId;

          // Group messages if:
          // 1. Same sender
          // 2. Sent within 5 minutes of each other
          if (
            isMe === nextIsMe &&
            String(msg.senderId) === String(nextMsg.senderId)
          ) {
            const timeDiff = Math.abs(
              (nextMsg.createdAt || 0) - (msg.createdAt || 0)
            );
            if (timeDiff < 5 * 60 * 1000) {
              // 5 minutes
              showSenderInfo = false;
            }
          }
        }

        // Check if this is first message in a group (for showing sender name in groups)
        let isFirstInGroup = true;
        if (i > 0) {
          const prevMsg = reversed[i - 1];
          if (!prevMsg._isDaySeparator) {
            const prevIsMe = String(prevMsg.senderId) === currentUserId;
            if (
              isMe === prevIsMe &&
              String(msg.senderId) === String(prevMsg.senderId)
            ) {
              const timeDiff = Math.abs(
                (msg.createdAt || 0) - (prevMsg.createdAt || 0)
              );
              if (timeDiff < 5 * 60 * 1000) {
                isFirstInGroup = false;
              }
            }
          }
        }

        result.push({
          ...msg,
          _isMe: isMe,
          _showSenderInfo: showSenderInfo,
          _isFirstInGroup: isFirstInGroup,
        });
      }

      return result;
    },
    // Format date label for day separators (Flutter sync)
    formatDateLabel() {
      return (date) => {
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        if (date.toDateString() === today.toDateString()) {
          return this.$t("chat.today") || "Today";
        } else if (date.toDateString() === yesterday.toDateString()) {
          return this.$t("chat.yesterday") || "Yesterday";
        } else {
          return date.toLocaleDateString(undefined, {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
          });
        }
      };
    },
  },
  mounted() {
    this.localReactionsStorageKey = `chat_reactions_${
      chatState.userId || "guest"
    }_${this.conversationId}`;
    this.loadLocalReactionsFromStorage();
    this.init();
    document.addEventListener("click", this.closeEmojiPickerOnOutsideClick);
    // Subscribe to socket connection state
    this.subscribeToConnectionState();
    // Theme detection for chat background
    this.updateChatBackground();
    this.themeObserver = new MutationObserver(() =>
      this.updateChatBackground()
    );
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-bs-theme", "data-theme"],
    });
    // Load mute status (Flutter parity)
    this.loadMuteStatus();
  },
  beforeUnmount() {
    // Cleanup polling
    if (this.poller) clearInterval(this.poller);
    if (this.reactionPollTimer) clearInterval(this.reactionPollTimer);
    // Cleanup Pusher (Flutter sync: deactivate -> unsubscribeConversation)
    this.unsubscribeFromPusher();
    // Cleanup typing timers
    if (this.typingTimeout) clearTimeout(this.typingTimeout);
    if (this.typingTimer) clearTimeout(this.typingTimer);
    // Cleanup waveform capture resources
    if (this.waveformInterval) clearInterval(this.waveformInterval);
    if (this.audioContext) {
      try {
        this.audioContext.close();
      } catch (_) {}
    }
    // Cleanup listeners
    document.removeEventListener("click", this.closeEmojiPickerOnOutsideClick);
    if (this.themeObserver) this.themeObserver.disconnect();
    // Cleanup highlight timeout
    if (this.highlightTimeout) clearTimeout(this.highlightTimeout);
    // Cancel any pending upload and clean up blob URLs
    this.revokePendingBlobUrl();
    this.cancelCurrentUpload();
    // Stop online status polling
    this.stopOnlineStatusPoll();
    // Unsubscribe from connection state
    this.unsubscribeFromConnectionState();
    // Clear debounce timers
    if (this.scrollDebounceTimer) clearTimeout(this.scrollDebounceTimer);
    if (this.typingDebounceTimer) clearTimeout(this.typingDebounceTimer);
    // Clear message refs to prevent memory leaks
    this.messageRefs = {};
  },
  watch: {
    conversationId(newId, oldId) {
      // Cleanup old conversation (Flutter sync: deactivate)
      // Clear all timers
      if (this.poller) clearInterval(this.poller);
      if (this.reactionPollTimer) clearInterval(this.reactionPollTimer);
      if (this.typingTimeout) clearTimeout(this.typingTimeout);
      if (this.typingTimer) clearTimeout(this.typingTimer);
      if (this.highlightTimeout) clearTimeout(this.highlightTimeout);
      if (this.scrollDebounceTimer) clearTimeout(this.scrollDebounceTimer);
      if (this.typingDebounceTimer) clearTimeout(this.typingDebounceTimer);
      // Stop online status polling
      this.stopOnlineStatusPoll();
      // Cancel any pending upload
      this.cancelCurrentUpload();
      // Unsubscribe from Pusher
      if (oldId) {
        pusherService.unsubscribeConversation(oldId);
      }
      if (this.pusherUnsubscribe) {
        this.pusherUnsubscribe();
        this.pusherUnsubscribe = null;
      }
      // Reset state
      this.messagesRaw = [];
      this.pendingMessage = null;
      this.replyCache = {};
      this.error = null;
      this.isOtherUserTyping = false;
      this.typingUserName = null;
      this.peerOnlineStatus = null;
      this.highlightedMessageId = null;
      this.selectedMessageIds = new Set();
      this.isSelectionMode = false;
      this.searchQuery = '';
      this.searchResults = [];
      this.isSearchMode = false;
      this.messageRefs = {};
      // Initialize new conversation
      this.init();
    },
    showSummaryDialog(val) {
      if (val) {
        const todayStr = new Date().toISOString().split("T")[0];
        this.summaryPreset = "today";
        this.summaryStartDate = todayStr;
        this.summaryEndDate = todayStr;
        this.summaryResult = null;
        this.summaryError = null;
      }
    },
  },
  methods: {
    updateChatBackground() {
      // Detect theme from Metronic's data-bs-theme attribute
      const theme =
        document.documentElement.getAttribute("data-bs-theme") ||
        document.documentElement.getAttribute("data-theme") ||
        "light";
      const isDark = theme === "dark";

      // Toggle dark mode class on chat container (CSS handles the backgrounds)
      const chatContainer = this.$el;
      if (chatContainer) {
        if (isDark) {
          chatContainer.classList.add("chat-dark-mode");
        } else {
          chatContainer.classList.remove("chat-dark-mode");
        }
      }
    },
    async init() {
      this.loading = true;
      this.error = null;
      this.messagesRaw = []; // Clear messages before loading to prevent duplicates
      this.pendingMessage = null; // Clear pending message on init
      this.lastMessageId = null;
      this.hasMoreMessages = true; // Reset pagination state
      this.messageRefs = {}; // Clear message refs
      try {
        // Load conversation meta first to compute permissions (maps Flutter _initializeChat + _checkIfUserCanSendMessages)
        this.conversation = await ChatService.getConversation(
          this.conversationId
        );
        this.applyConversationGuards();
        
        // Build participants map for message info (Flutter parity)
        this.buildParticipantsMap();

        await this.loadMessages();

        // Subscribe to Pusher for real-time updates (Flutter sync: _subscribeToPusher)
        await this.subscribeToPusher();

        // Start reaction poll as fallback (Flutter sync: _startReactionPoll - every 15s)
        this.startReactionPoll();

        // Mark as read (like Flutter after first load)
        if (this.messagesRaw.length) {
          await ChatService.markAsRead({ conversationId: this.conversationId });
        }
        
        // Start online status polling for direct chats
        if (!this.isGroupChat) {
          this.startOnlineStatusPoll();
        }
      } catch (e) {
        this.error = this.$t("chat.failed_to_load") || "Failed to load chat";
      } finally {
        this.loading = false;
        this.$nextTick(() => {
          // Check if we need to scroll to a specific message (from search)
          const targetMessageId = this.$route.query.messageId;
          const shouldHighlight = this.$route.query.highlight === 'true';
          
          if (targetMessageId) {
            // Give DOM time to render all messages
            setTimeout(() => {
              this.scrollToMessage(targetMessageId, shouldHighlight);
              // Clear the query params after scrolling
              this.$router.replace({ 
                name: this.$route.name, 
                params: this.$route.params,
                query: {} 
              }).catch(() => {});
            }, 300);
          } else {
            this.scrollToBottom();
          }
          this.updateChatBackground();
        });
      }
    },
    applyConversationGuards() {
      // Flutter sync: _checkIfUserCanSendMessages logic
      const c =
        (this.conversation && (this.conversation.data || this.conversation)) ||
        {};
      const custom = c.custom || {};
      const currentUserId = String(chatState.userId || "");

      // Determine conversation type (Flutter: isGroup, isDirect)
      const convType = custom.type || "";
      this.isGroupChat =
        convType === "group" ||
        convType === "student_group" ||
        convType === "course_group";

      // Chat enabled check (Flutter: isChatEnabled)
      const chatEnabledRaw = custom.chat_enabled;
      let isEnabled = true;
      if (chatEnabledRaw === false) isEnabled = false;
      if (typeof chatEnabledRaw === "string")
        isEnabled = chatEnabledRaw.toLowerCase() === "true";
      this.isChatLocked = !isEnabled;

      // Block status (Flutter: isBlocked, isUserBlocker, isUserBlocked)
      const blockedBy = custom.blocked_by;
      this.isBlocked = !!blockedBy;
      this.isUserBlocker = !!blockedBy && String(blockedBy) === currentUserId;
      this.isUserBlocked = !!blockedBy && String(blockedBy) !== currentUserId;

      // Owner check (Flutter: _isCurrentUserOwner)
      const creatorId =
        custom.creator_id != null ? String(custom.creator_id) : null;
      this.isCurrentUserOwner = creatorId && creatorId === currentUserId;

      // Can send logic (Flutter: _canSendMessages)
      // User can send if:
      // - Not blocked (neither blocker nor blocked can send - matches Flutter)
      // - Chat is enabled OR user is owner
      this.canSend =
        !this.isBlocked && (isEnabled || this.isCurrentUserOwner);

      // Reason for not being able to send
      if (this.isUserBlocked) {
        this.cannotSendReason =
          this.$t("chat.blocked_message") ||
          "You cannot send messages to this user. They have blocked you.";
      } else if (this.isUserBlocker) {
        this.cannotSendReason =
          this.$t("chat.you_blocked_user") ||
          "You have blocked this user.";
      } else if (this.isChatLocked && !this.isCurrentUserOwner) {
        this.cannotSendReason = this.$t("chat.locked") || "Chat is locked";
      } else {
        this.cannotSendReason = "";
      }
    },
    // ==================== Pusher Real-Time (Flutter sync: _subscribeToPusher, _handlePusherEvent) ====================
    async subscribeToPusher() {
      try {
        // Initialize Pusher if not already
        await pusherService.initialize();

        if (!pusherService.isInitialized) {
          console.warn(
            "[Conversation] Pusher not available, using polling fallback"
          );
          this.startPolling(); // Fallback to polling
          return;
        }

        // Subscribe to conversation channel
        pusherService.subscribeConversation(this.conversationId);

        // Listen for events (Flutter sync: eventsForConversation().listen(_handlePusherEvent))
        this.pusherUnsubscribe = pusherService.onConversationEvent(
          this.conversationId,
          this.handlePusherEvent
        );

        console.log(
          "[Conversation] Subscribed to Pusher for:",
          this.conversationId
        );
      } catch (e) {
        console.error("[Conversation] Pusher subscription failed:", e);
        this.startPolling(); // Fallback to polling
      }
    },
    unsubscribeFromPusher() {
      if (this.pusherUnsubscribe) {
        this.pusherUnsubscribe();
        this.pusherUnsubscribe = null;
      }
      pusherService.unsubscribeConversation(this.conversationId);
    },
    handlePusherEvent(event) {
      // Flutter sync: _handlePusherEvent switch statement
      const eventName = event.event;
      const data = event.data || {};

      switch (eventName) {
        case "message.sent":
          this.handlePusherNewMessage(data);
          break;
        case "message.edited":
          this.handlePusherMessageEdited(data);
          break;
        case "message.deleted":
          this.handlePusherMessageDeleted(data);
          break;
        case "message.read":
          this.handlePusherMessageRead(data);
          break;
        case "reaction.added":
          this.handlePusherReactionAdded(data);
          break;
        case "reaction.removed":
          this.handlePusherReactionRemoved(data);
          break;
        case "user.typing":
        case "client-user.typing":
          this.handlePusherTyping(data);
          break;
        case "user.stopped_typing":
        case "client-user.stopped_typing":
          this.handlePusherStoppedTyping(data);
          break;
        case "group.chat_toggled":
          this.handlePusherChatToggled(data);
          break;
        case "group.user_kicked":
          this.handlePusherUserKicked(data);
          break;
        case "group.user_added":
          this.handlePusherUserAdded(data);
          break;
        case "group.user_left":
          this.handlePusherUserLeft(data);
          break;
        case "user.blocked":
        case "user.unblocked":
          this.handlePusherBlockStatus(data);
          break;
      }
    },
    // Flutter sync: _handlePusherNewMessage
    async handlePusherNewMessage(data) {
      const messageId = data.message_id?.toString();
      if (!messageId) return;

      // Skip if it's our own message (already added optimistically)
      const senderId = data.sender_id?.toString();
      if (senderId === String(chatState.userId)) return;

      // Fetch full message from API
      const message = await ChatService.getMessage({
        conversationId: this.conversationId,
        messageId,
      });

      if (!message) return;

      // Avoid duplicates
      if (this.messagesRaw.some((m) => String(m.id) === String(message.id)))
        return;

      // Add to messages (newest first)
      this.messagesRaw.unshift(message);
      this.$nextTick(() => this.scrollToBottomSmooth());

      // Update the shared conversation list preview in real-time
      updateLastMessage(this.conversationId, message);
      bumpConversationToTop(this.conversationId);

      // Mark as read
      await ChatService.markAsRead({ conversationId: this.conversationId });
    },
    // Flutter sync: _handlePusherMessageEdited
    handlePusherMessageEdited(data) {
      const messageId = data.message_id?.toString();
      const newText = data.new_text;
      if (!messageId || newText === undefined) return;

      const idx = this.messagesRaw.findIndex((m) => String(m.id) === messageId);
      if (idx === -1) return;

      const old = this.messagesRaw[idx];
      this.messagesRaw.splice(idx, 1, {
        ...old,
        text: newText,
        editedAt: Date.now(),
      });
    },
    // Flutter sync: _handlePusherMessageDeleted
    handlePusherMessageDeleted(data) {
      const messageId = data.message_id?.toString();
      if (!messageId) return;

      const idx = this.messagesRaw.findIndex((m) => String(m.id) === messageId);
      if (idx !== -1) {
        this.messagesRaw.splice(idx, 1);
      }
    },
    // Flutter sync: _handlePusherMessageRead
    handlePusherMessageRead(data) {
      const userId = data.user_id?.toString();
      if (!userId) return;

      // Update readBy on all messages from current user
      let hasUpdates = false;
      for (let i = 0; i < this.messagesRaw.length; i++) {
        const msg = this.messagesRaw[i];
        if (
          String(msg.senderId) === String(chatState.userId) &&
          !(msg.readBy || []).includes(userId)
        ) {
          hasUpdates = true;
          this.messagesRaw.splice(i, 1, {
            ...msg,
            readBy: [...(msg.readBy || []), userId],
          });
        }
      }
      if (hasUpdates) {
        this.messagesRaw = [...this.messagesRaw]; // Trigger reactivity
      }
    },
    // Flutter sync: _handlePusherReactionAdded
    handlePusherReactionAdded(data) {
      const messageId = data.message_id?.toString();
      const emoji = data.emoji;
      const userId = data.user_id?.toString();
      if (!messageId || !emoji) return;

      // Skip if this is our own reaction (already handled optimistically)
      if (userId === String(chatState.userId)) return;

      const idx = this.messagesRaw.findIndex((m) => String(m.id) === messageId);
      if (idx === -1) return;

      const msg = this.messagesRaw[idx];
      const updatedReactions = { ...(msg.reactions || {}) };

      if (updatedReactions[emoji]) {
        const currentCount = updatedReactions[emoji].count || 0;
        updatedReactions[emoji] = {
          ...updatedReactions[emoji],
          count: currentCount + 1,
        };
      } else {
        updatedReactions[emoji] = { count: 1, users: [] };
      }

      this.messagesRaw.splice(idx, 1, { ...msg, reactions: updatedReactions });
    },
    // Flutter sync: _handlePusherReactionRemoved
    handlePusherReactionRemoved(data) {
      const messageId = data.message_id?.toString();
      const emoji = data.emoji;
      const userId = data.user_id?.toString();
      if (!messageId || !emoji) return;

      // Skip if this is our own reaction
      if (userId === String(chatState.userId)) return;

      const idx = this.messagesRaw.findIndex((m) => String(m.id) === messageId);
      if (idx === -1) return;

      const msg = this.messagesRaw[idx];
      const updatedReactions = { ...(msg.reactions || {}) };

      if (updatedReactions[emoji]) {
        const currentCount = updatedReactions[emoji].count || 0;
        if (currentCount <= 1) {
          delete updatedReactions[emoji];
        } else {
          updatedReactions[emoji] = {
            ...updatedReactions[emoji],
            count: currentCount - 1,
          };
        }
      }

      this.messagesRaw.splice(idx, 1, {
        ...msg,
        reactions: Object.keys(updatedReactions).length
          ? updatedReactions
          : null,
      });
    },
    // Flutter sync: _handlePusherTyping
    handlePusherTyping(data) {
      const userId = data.user_id?.toString();
      if (!userId || userId === String(chatState.userId)) return;

      const userName = data.user_name;

      this.isOtherUserTyping = true;
      this.typingUserName = userName;

      // Auto-clear typing after 4 seconds (Flutter sync)
      if (this.typingTimeout) clearTimeout(this.typingTimeout);
      this.typingTimeout = setTimeout(() => {
        this.isOtherUserTyping = false;
        this.typingUserName = null;
      }, 4000);
    },
    // Flutter sync: _handlePusherStoppedTyping
    handlePusherStoppedTyping(data) {
      const userId = data.user_id?.toString();
      if (!userId || userId === String(chatState.userId)) return;

      if (this.typingTimeout) clearTimeout(this.typingTimeout);
      this.isOtherUserTyping = false;
      this.typingUserName = null;
    },
    // Flutter sync: _handlePusherChatToggled
    handlePusherChatToggled(data) {
      const isLocked = data.is_locked === true;
      this.isChatLocked = isLocked;
      this.canSend = !isLocked || this.isCurrentUserOwner;
    },
    // Flutter sync: _handlePusherBlockStatus
    handlePusherBlockStatus(data) {
      const blockedBy = data.blocked_by?.toString();
      const unblockedBy = data.unblocked_by?.toString();

      if (unblockedBy) {
        // Unblock event - revert to normal state
        this.isBlocked = false;
        this.isUserBlocker = false;
        this.isUserBlocked = false;
        this.canSend = true;
      } else if (blockedBy) {
        // Block event - neither party can send messages (matches Flutter)
        this.isBlocked = true;
        this.isUserBlocker = blockedBy === String(chatState.userId);
        this.isUserBlocked = !this.isUserBlocker;
        this.canSend = false; // Neither blocker nor blocked can send (matches Flutter)
      }
    },
    // Flutter sync: _handlePusherUserKicked
    handlePusherUserKicked(data) {
      const kickedUserId = data.kicked_user_id?.toString();

      if (kickedUserId === String(chatState.userId)) {
        // Current user was kicked - leave the screen
        alert(
          this.$t("chat.you_were_kicked") ||
            "You have been removed from this chat"
        );
        this.$router.push({ name: "ChatMaster" });
        return;
      }

      // Another user was kicked - reload messages for system message
      this.loadMessages();
    },
    // Flutter sync: _handlePusherUserAdded
    handlePusherUserAdded(data) {
      // Reload messages to pick up system message about new member
      this.loadMessages();
    },
    // Flutter sync: _handlePusherUserLeft
    handlePusherUserLeft(data) {
      const userId = data.user?.id?.toString();

      if (userId === String(chatState.userId)) {
        // Current user left - exit the screen
        this.$router.push({ name: "ChatMaster" });
        return;
      }

      // Reload messages for system message
      this.loadMessages();
    },
    // Flutter sync: _startReactionPoll (fallback poll every 15s for reactions/read receipts)
    startReactionPoll() {
      if (this.reactionPollTimer) clearInterval(this.reactionPollTimer);

      this.reactionPollTimer = setInterval(async () => {
        if (!this.messagesRaw.length) return;

        try {
          // Fetch recent messages to sync reactions and read receipts
          const recent = await ChatService.getMessages({
            conversationId: this.conversationId,
            limit: 20,
          });

          if (Array.isArray(recent) && recent.length) {
            // Update existing messages with fresh data
            recent.forEach((freshMsg) => {
              const idx = this.messagesRaw.findIndex(
                (m) => String(m.id) === String(freshMsg.id)
              );
              if (idx !== -1) {
                const existing = this.messagesRaw[idx];
                // Only update if reactions or readBy changed
                const reactionsChanged =
                  JSON.stringify(existing.reactions) !==
                  JSON.stringify(freshMsg.reactions);
                const readByChanged =
                  JSON.stringify(existing.readBy) !==
                  JSON.stringify(freshMsg.readBy);

                if (reactionsChanged || readByChanged) {
                  this.messagesRaw.splice(idx, 1, {
                    ...existing,
                    reactions: freshMsg.reactions,
                    readBy: freshMsg.readBy,
                  });
                }
              }
            });
          }
        } catch (e) {
          // Silent fail for background poll
        }
      }, 15000); // 15 seconds like Flutter
    },
    // Flutter sync: _handleTypingChanged - send typing events
    handleTypingChanged(isTyping) {
      if (!pusherService.isInitialized) return;

      // Cancel existing timer
      if (this.typingTimer) clearTimeout(this.typingTimer);

      if (isTyping) {
        // Trigger typing event
        pusherService.triggerTyping(this.conversationId, chatState.userName);

        // Auto-stop typing after 3 seconds (Flutter sync)
        this.typingTimer = setTimeout(() => {
          pusherService.triggerStoppedTyping(this.conversationId);
        }, 3000);
      } else {
        // Stop typing
        pusherService.triggerStoppedTyping(this.conversationId);
      }
    },
    // Handle text input - trigger typing event (debounced for performance)
    onTextInput() {
      if (this.typingDebounceTimer) {
        clearTimeout(this.typingDebounceTimer);
      }
      
      this.typingDebounceTimer = setTimeout(() => {
        if (this.text.trim()) {
          this.handleTypingChanged(true);
        }
      }, 300); // 300ms debounce to avoid excessive typing events
    },
    async loadMessages() {
      const response = await ChatService.getMessages({
        conversationId: this.conversationId,
        lastMessageId: this.lastMessageId,
      });
      const data = response && (response.data || response);
      const incoming = Array.isArray(data) ? data : [];
      if (incoming.length) {
        // Filter out duplicates before adding
        const existing = new Set(this.messagesRaw.map((m) => String(m.id)));
        const newMessages = incoming.filter((m) => !existing.has(String(m.id)));

        if (newMessages.length) {
          this.messagesRaw.unshift(...newMessages);
          const last = incoming[incoming.length - 1];
          if (last && last.id != null) this.lastMessageId = last.id;
          await this.resolveRepliesFor(this.messagesRaw);
        }
      }
    },
    startPolling() {
      if (this.poller) clearInterval(this.poller);
      this.poller = setInterval(async () => {
        if (!this.lastMessageId) return;
        try {
          const newOnes = await ChatService.getMessages({
            conversationId: this.conversationId,
            limit: 50,
            afterMessageId: this.lastMessageId,
          });
          if (Array.isArray(newOnes) && newOnes.length) {
            // Get existing real message IDs (exclude temp/optimistic messages)
            const existingRealIds = new Set(
              this.messagesRaw
                .filter(
                  (m) => !String(m.id).startsWith("temp_") && !m._isOptimistic
                )
                .map((m) => String(m.id))
            );

            // Filter out messages that already exist
            const uniques = newOnes.filter(
              (m) => !existingRealIds.has(String(m.id))
            );

            if (uniques.length) {
              // For each new message, preserve localPath from matching optimistic message
              const enhancedUniques = uniques.map((u) => {
                const matchingOptimistic = this.messagesRaw.find(
                  (m) =>
                    (String(m.id).startsWith("temp_") || m._isOptimistic) &&
                    String(u.senderId) === String(m.senderId) &&
                    m.custom &&
                    m.custom.localPath
                );
                if (
                  matchingOptimistic &&
                  matchingOptimistic.custom &&
                  matchingOptimistic.custom.localPath
                ) {
                  // Preserve localPath in the real message for smooth transition
                  return {
                    ...u,
                    custom: {
                      ...(u.custom || {}),
                      localPath: matchingOptimistic.custom.localPath,
                    },
                  };
                }
                return u;
              });

              // Remove any optimistic messages that match the new real messages
              const newMessages = this.messagesRaw.filter((m) => {
                if (!String(m.id).startsWith("temp_") && !m._isOptimistic)
                  return true;
                const matchingReal = enhancedUniques.find(
                  (u) =>
                    u.text === m.text &&
                    String(u.senderId) === String(m.senderId) &&
                    Math.abs((u.createdAt || 0) - (m.createdAt || 0)) < 60000
                );
                return !matchingReal;
              });

              newMessages.unshift(...enhancedUniques);
              this.messagesRaw = newMessages;
              this.lastMessageId = String(uniques[0].id);
              await ChatService.markAsRead({
                conversationId: this.conversationId,
              });
              this.resolveRepliesFor(uniques);
              this.$nextTick(this.scrollToBottomSmooth);
            }
          }
        } catch (_) {}
      }, 3000);
    },
    isMe(m) {
      return (
        m.senderId && String(m.senderId) === String(chatState.userId || "")
      );
    },
    getSenderName(m) {
      if (!m.senderId) return "";
      const senderId = String(m.senderId);
      
      // Check participantsMap first
      if (this.participantsMap[senderId]) {
        return this.participantsMap[senderId].name || "";
      }
      
      // Fallback to message's sender info if available
      if (m.sender && typeof m.sender === 'object') {
        return m.sender.name || m.sender.username || "";
      }
      
      // Last fallback - use senderId
      return `User ${senderId.slice(-4)}`;
    },
    getSenderColor(m) {
      if (!m.senderId) return "#6b7280";
      const senderId = String(m.senderId);
      // Generate consistent color based on senderId
      const colors = [
        "#ef4444", "#f97316", "#f59e0b", "#84cc16", 
        "#22c55e", "#14b8a6", "#06b6d4", "#0ea5e9",
        "#3b82f6", "#6366f1", "#8b5cf6", "#a855f7",
        "#d946ef", "#ec4899", "#f43f5e"
      ];
      let hash = 0;
      for (let i = 0; i < senderId.length; i++) {
        hash = senderId.charCodeAt(i) + ((hash << 5) - hash);
      }
      return colors[Math.abs(hash) % colors.length];
    },
    isPending(m) {
      return !!(m.custom && m.custom.isPending);
    },
    isFailed(m) {
      return !!(m.custom && m.custom.isFailed);
    },
    // Check if message is a system message (Flutter sync: message.type == 'SystemMessage')
    isSystemMessage(m) {
      return m.type === 'SystemMessage';
    },
    // Check if message has been seen/read by at least one recipient (not the sender)
    // Like Flutter: readByOthers = message.readBy.where((userId) => userId != message.senderId && userId != currentUserId)
    isSeenByRecipient(m) {
      const readBy = m.readBy || [];
      if (readBy.length === 0) return false;

      // Get sender ID and current user ID
      const senderId = String(m.senderId || "");
      const currentUserId = String(chatState.userId || "");

      // Check if anyone OTHER than the sender AND current user has read the message
      // This matches Flutter's logic exactly
      return readBy.some((id) => {
        const idStr = String(id);
        return idStr !== senderId && idStr !== currentUserId;
      });
    },
    // Check if a message is unread (not sent by current user and not in readBy)
    isMessageUnread(m) {
      // Own messages are never "unread" to us
      if (this.isMe(m)) return false;
      // Pending/failed messages are not unread
      if (this.isPending(m) || this.isFailed(m)) return false;
      // Check if current user is in readBy array
      const currentUserId = String(chatState.userId || "");
      const readBy = m.readBy || [];
      return (
        !readBy.includes(currentUserId) &&
        !readBy.includes(parseInt(currentUserId))
      );
    },
    // Show unread divider before the first unread message
    showUnreadDivider(index) {
      if (index === 0) return false;
      const messages = this.displayMessages;
      const currentMsg = messages[index];
      const prevMsg = messages[index - 1];
      // Show divider if current is unread and previous is not
      return this.isMessageUnread(currentMsg) && !this.isMessageUnread(prevMsg);
    },
    // Get index of first unread message
    firstUnreadIndex() {
      return this.displayMessages.findIndex((m) => this.isMessageUnread(m));
    },
    // Like Flutter's hasAttachment getter - check if message has file attachment
    hasAttachment(m) {
      // Check attachment field
      if (m.attachment != null) return true;
      // Check content field for file blocks
      if (Array.isArray(m.content) && m.content.length) {
        return m.content.some((block) => block && block.type === "file");
      }
      // Check custom.localPath for pending messages
      if (m.custom && m.custom.localPath) return true;
      return false;
    },
    attachmentLabel(m) {
      const content = m.content;
      if (
        Array.isArray(content) &&
        content.length &&
        content[0].type === "file"
      ) {
        const subtype = content[0].subtype;
        if (subtype === "image") return this.$t("chat.msg_photo") || "Photo";
        if (subtype === "video") return this.$t("chat.msg_video") || "Video";
        if (subtype === "voice") return this.$t("chat.msg_voice") || "Voice";
        return this.$t("chat.msg_document") || "Attachment";
      }
      return this.$t("common.attachment") || "Attachment";
    },
    formatTime(ts) {
      try {
        const d = new Date(typeof ts === "number" ? ts : Date.parse(ts));
        return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      } catch (_) {
        return "";
      }
    },
    /**
     * Send a text message (matches Flutter's _sendMessage pattern)
     * Uses optimistic UI with pendingMessage, then replaces with confirmed message
     */
    async send() {
      const text = (this.text || "").trim();
      if (!text || this.isSending || !this.canSend) return;

      // Validate user can send messages
      if (this.isUserBlocked) {
        this.$toast?.error(this.$t('chat.blocked_message') || 'You cannot send messages to this user');
        return;
      }

      const replyId = this.replyingTo ? this.replyingTo.id : null;
      this.replyingTo = null;
      this.text = "";

      // Create optimistic message (matches Flutter's tempMessage pattern)
      const tempMessage = {
        id: `temp_${Date.now()}`,
        conversationId: this.conversationId,
        type: "UserMessage",
        senderId: chatState.userId,
        text: text,
        readBy: [],
        createdAt: Date.now(),
        content: null,
        attachment: null,
        referencedMessageId: replyId,
        custom: {
          isPending: true,
          message_type: 'text',
        },
      };

      // Set state BEFORE async operation (matches Flutter)
      this.isSending = true;
      this.pendingMessage = tempMessage;
      this.$nextTick(this.scrollToBottomSmooth);

      try {
        // Send message and get confirmed response (matches Flutter's await pattern)
        const confirmedMessage = replyId
          ? await ChatService.sendReply({
              conversationId: this.conversationId,
              text,
              referencedMessageId: replyId,
              custom: {},
            })
          : await ChatService.sendMessage({
              conversationId: this.conversationId,
              text,
              custom: {},
            });

        if (confirmedMessage && confirmedMessage.id) {
          // Clear pending message FIRST to avoid duplicate (matches Flutter)
          this.pendingMessage = null;
          this.isSending = false;

          // Check if message already exists (avoid duplicates from Pusher)
          const exists = this.messagesRaw.some(
            (m) => String(m.id) === String(confirmedMessage.id)
          );

          if (!exists) {
            // Add confirmed message at the beginning (newest position)
            this.messagesRaw.unshift(confirmedMessage);
            this.lastMessageId = confirmedMessage.id;
          }

          // Update the shared conversation list preview in real-time
          updateLastMessage(this.conversationId, confirmedMessage);
          bumpConversationToTop(this.conversationId);

          this.$nextTick(() => {
            this.scrollToBottomSmooth();
            this.$refs.chatInput?.focus();
          });
        } else {
          // Message was null but no error - clear pending state
          this.pendingMessage = null;
          this.isSending = false;
          this.$nextTick(() => {
            this.$refs.chatInput?.focus();
          });
        }
      } catch (err) {
        console.error("[Conversation] send error:", err);
        // Move to failed state (matches Flutter's _failedMessages pattern)
        if (this.pendingMessage) {
          this.pendingMessage = {
            ...this.pendingMessage,
            custom: {
              ...this.pendingMessage.custom,
              isFailed: true,
              isPending: false,
              errorMessage: err.message || 'Failed to send message',
            },
          };
        }
        this.isSending = false;
        this.$nextTick(() => {
          this.$refs.chatInput?.focus();
        });
      }
    },
    dedupeMessages() {
      const seen = new Set();
      const out = [];
      for (const m of this.messagesRaw) {
        const id = String(m.id);
        if (seen.has(id)) continue;
        seen.add(id);
        out.push(m);
      }
      if (out.length !== this.messagesRaw.length) this.messagesRaw = out;
    },
    // Reply helpers
    async ensureReplyCached(messageId) {
      const id = String(messageId);
      if (!id || this.replyCache[id]) return;
      try {
        const res = await ChatService.getMessage({
          conversationId: this.conversationId,
          messageId: id,
        });
        const msg = (res && (res.data || res)) || null;
        if (msg && msg.id != null) this.replyCache[String(msg.id)] = msg;
      } catch (_) {}
    },
    resolveRepliesFor(list) {
      const missing = new Set();
      (list || []).forEach((m) => {
        const rid = m.referencedMessageId || (m.custom && m.custom.replyTo);
        if (
          rid != null &&
          !this.replyCache[String(rid)] &&
          !this.messagesRaw.find((x) => String(x.id) === String(rid))
        ) {
          missing.add(String(rid));
        }
      });
      missing.forEach((id) => this.ensureReplyCached(id));
    },
    replyOf(m) {
      const rid =
        m && (m.referencedMessageId || (m.custom && m.custom.replyTo));
      if (!rid) return null;
      const local = this.messagesRaw.find((x) => String(x.id) === String(rid));
      if (local) return local;
      return this.replyCache[String(rid)] || null;
    },
    replySnippet(rm) {
      if (!rm) return "";
      if (rm.text && rm.text.length) return rm.text;
      return this.attachmentLabel(rm);
    },
    reactionMap(m) {
      const base = (m && m.reactions) || {};
      return base;
    },
    // Helper to check if message has reactions
    hasReactions(m) {
      const reactions = m && m.reactions;
      return reactions && Object.keys(reactions).length > 0;
    },
    // Emoji picker popup methods
    openEmojiPicker(m, event) {
      if (this.isPending(m)) return;
      this.emojiPickerMessage = m;
      // Position near the button
      const rect = event.target.getBoundingClientRect();
      this.emojiPickerPosition = {
        top: `${rect.top - 50}px`,
        left: `${rect.left}px`,
      };
      this.showEmojiPicker = true;
    },
    closeEmojiPicker() {
      this.showEmojiPicker = false;
      this.emojiPickerMessage = null;
    },
    selectEmojiForMessage(emoji) {
      if (this.emojiPickerMessage) {
        this.toggleReaction(this.emojiPickerMessage, emoji);
      }
      this.closeEmojiPicker();
    },
    closeEditModal() {
      this.showEditModal = false;
      this.editText = "";
      this.editOriginalText = "";
      this.editMessageId = null;
    },
    closeDeleteModal() {
      this.showDeleteModal = false;
      this.deleteMessageId = null;
    },
    async confirmEdit() {
      if (
        !this.editMessageId ||
        !this.editText.trim() ||
        this.editText === this.editOriginalText
      )
        return;

      try {
        const success = await ChatService.editMessage({
          conversationId: this.conversationId,
          messageId: this.editMessageId,
          text: this.editText,
        });

        if (success) {
          // Update in UI immediately (like Flutter updates after edit)
          const idx = this.messagesRaw.findIndex(
            (m) => String(m.id) === String(this.editMessageId)
          );
          if (idx !== -1) {
            this.messagesRaw[idx] = {
              ...this.messagesRaw[idx],
              text: this.editText,
              editedAt: Date.now(),
            };
            // Trigger reactivity
            this.messagesRaw = [...this.messagesRaw];
          }
        }
      } catch (e) {
        console.error("Edit message error:", e);
      } finally {
        this.closeEditModal();
      }
    },
    async confirmDelete() {
      if (!this.deleteMessageId) return;

      try {
        const success = await ChatService.deleteMessage({
          conversationId: this.conversationId,
          messageId: this.deleteMessageId,
        });

        if (success) {
          // Remove from UI immediately (like Flutter)
          const idx = this.messagesRaw.findIndex(
            (m) => String(m.id) === String(this.deleteMessageId)
          );
          if (idx !== -1) {
            this.messagesRaw.splice(idx, 1);
          }
        }
      } catch (e) {
        console.error("Delete message error:", e);
      } finally {
        this.closeDeleteModal();
      }
    },
    // Legacy methods for direct calls (still needed for failed message actions)
    async deleteMessage(msg) {
      if (!msg || !msg.id || String(msg.id).startsWith("temp_")) return;
      this.deleteMessageId = msg.id;
      this.showDeleteModal = true;
    },
    async editMessage(msg) {
      if (!msg || !msg.id || String(msg.id).startsWith("temp_")) return;
      this.editText = msg.text || "";
      this.editOriginalText = msg.text || "";
      this.editMessageId = msg.id;
      this.showEditModal = true;
    },
    // Lightbox methods
    openLightbox(url) {
      this.lightboxImage = url;
    },
    closeLightbox() {
      this.lightboxImage = null;
    },
    // Avatar error handler
    onAvatarError(e) {
      e.target.style.display = "none";
    },
    // Convert URLs in text to clickable links
    linkify(text) {
      if (!text) return "";
      // Escape HTML to prevent XSS
      const escapeHtml = (str) => {
        const div = document.createElement("div");
        div.textContent = str;
        return div.innerHTML;
      };
      const escaped = escapeHtml(String(text));
      // URL regex pattern - matches http, https, and www URLs
      const urlPattern = /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+)/gi;
      return escaped.replace(urlPattern, (url) => {
        const href = url.startsWith("www.") ? "https://" + url : url;
        return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="msg-link">${url}</a>`;
      });
    },
    // Escape regex special characters
    escapeRegex(str) {
      return String(str).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    },
    // Highlight search keyword in HTML while preserving existing HTML tags
    highlightSearchKeyword(html, query) {
      if (!query || !html) return html;
      const q = this.escapeRegex(query.trim());
      if (!q) return html;

      // Protect existing HTML tags by replacing them with placeholders
      const tags = [];
      const withoutTags = html.replace(/<[^>]+>/g, (match) => {
        tags.push(match);
        return `{{TAG${tags.length - 1}}}`;
      });

      const regex = new RegExp(`(${q})`, "gi");
      const highlighted = withoutTags.replace(
        regex,
        '<mark class="search-highlight">$1</mark>'
      );

      // Restore HTML tags
      return highlighted.replace(/\{\{TAG(\d+)\}\}/g, (_, i) => tags[i]);
    },
    // Format message text: linkify URLs and highlight search query
    formatMessageText(message) {
      const linkified = this.linkify(message.text);
      const query = this.isSearchMode ? this.searchQuery : "";
      if (!query || !message.text) return linkified;
      // Only highlight if message text contains the query
      const lowerText = String(message.text).toLowerCase();
      const lowerQuery = query.trim().toLowerCase();
      if (!lowerQuery || !lowerText.includes(lowerQuery)) return linkified;
      return this.highlightSearchKeyword(linkified, query);
    },
    startReply(m) {
      this.replyingTo = m;
    },
    cancelReply() {
      this.replyingTo = null;
    },
    // ===== WhatsApp-style Message Options Menu =====
    openMessageMenu(m, event) {
      if (!m || !m.id) return;
      // If already open for this message, close it
      if (this.messageMenuOpenId === m.id) {
        this.closeMessageMenu();
        return;
      }
      // Close any existing menu first
      this.closeMessageMenu();
      this.messageMenuOpenId = m.id;
      // Position menu near the trigger button
      const rect = event?.target?.getBoundingClientRect();
      if (rect) {
        const isOwn = this.isMe(m);
        const menuHeight = 320;
        const spaceBelow = window.innerHeight - rect.bottom;
        const showAbove = spaceBelow < menuHeight && rect.top > menuHeight;
        this.messageMenuPosition = {
          top: showAbove ? `${rect.top - menuHeight - 8}px` : `${rect.bottom + 8}px`,
          right: isOwn ? `${window.innerWidth - rect.right}px` : "auto",
          left: isOwn ? "auto" : `${rect.left}px`,
        };
      }
      // Close menu on outside click
      this.$nextTick(() => {
        setTimeout(() => {
          document.addEventListener("click", this.closeMessageMenuOnOutsideClick);
        }, 10);
      });
    },
    closeMessageMenuOnOutsideClick(e) {
      const menu = this.$refs.messageMenuDropdown;
      if (!menu || !menu.contains(e.target)) {
        this.closeMessageMenu();
      }
    },
    closeMessageMenu() {
      this.messageMenuOpenId = null;
      document.removeEventListener("click", this.closeMessageMenuOnOutsideClick);
    },
    getMessageById(id) {
      if (!id) return null;
      return this.messagesRaw.find((m) => String(m.id) === String(id)) || null;
    },
    handleMenuAction(action, m) {
      this.closeMessageMenu();
      if (!m || !m.id) return;
      switch (action) {
        case "reply":
          this.startReply(m);
          break;
        case "forward":
          this.forwardMessage(m);
          break;
        case "copy":
          this.copyMessage(m);
          break;
        case "star":
          this.starMessage(m);
          break;
        case "select":
          this.selectMessage(m);
          break;
        case "replyPrivately":
          this.replyPrivately(m);
          break;
        case "info":
          this.openMessageInfo(m);
          break;
        case "edit":
          this.editMessage(m);
          break;
        case "delete":
          this.deleteMessage(m);
          break;
      }
    },
    forwardMessage(m) {
      // TODO: Implement forward dialog/flow (Flutter parity)
      this.$toast?.info?.("Forward feature coming soon") ||
        alert(this.$t("chat.forward_coming_soon") || "Forward feature coming soon");
    },
    copyMessage(m) {
      const text = m.text || "";
      if (!text) return;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => {
          this.$toast?.success?.(this.$t("chat.copied") || "Copied");
        }).catch(() => {
          this.fallbackCopyText(text);
        });
      } else {
        this.fallbackCopyText(text);
      }
    },
    fallbackCopyText(text) {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand("copy");
        this.$toast?.success?.(this.$t("chat.copied") || "Copied");
      } catch (err) {
        console.error("Failed to copy text:", err);
      }
      document.body.removeChild(textarea);
    },
    starMessage(m) {
      const messageId = String(m.id);
      const isStarred = this.starredMessages[messageId];
      this.$set(this.starredMessages, messageId, !isStarred);
      this.$toast?.success?.(
        isStarred
          ? (this.$t("chat.unstarred") || "Unstarred")
          : (this.$t("chat.starred") || "Starred")
      );
    },
    selectMessage(m) {
      // Enable selection mode and select this message
      this.isSelectionMode = true;
      const newSet = new Set(this.selectedMessageIds);
      newSet.add(m.id);
      this.selectedMessageIds = newSet;
    },
    replyPrivately(m) {
      // TODO: Implement reply privately in groups (Flutter parity)
      this.$toast?.info?.("Reply privately feature coming soon") ||
        alert(this.$t("chat.reply_privately_coming_soon") || "Reply privately feature coming soon");
    },
    toggleEmojiPicker(m) {
      this.emojiPickerFor = this.emojiPickerFor === m.id ? null : m.id;
    },
    closeEmojiPickerOnOutsideClick(e) {
      if (!this.emojiPickerFor) return;
      const target = e.target;
      if (target && !target.closest(".emoji-picker-container")) {
        this.emojiPickerFor = null;
      }
    },
    async selectEmoji(m, emoji) {
      await this.toggleReaction(m, emoji);
      this.emojiPickerFor = null;
    },
    async toggleReaction(m, emoji) {
      const messageId = String(m.id);
      const idx = this.messagesRaw.findIndex((x) => String(x.id) === messageId);
      if (idx === -1) return;

      const currentMessage = this.messagesRaw[idx];

      if (!this.localReactions[messageId]) {
        this.localReactions[messageId] = new Set();
      }

      const userHasReacted = this.localReactions[messageId].has(emoji);
      console.log(
        "[toggleReaction]",
        emoji,
        "userHasReacted:",
        userHasReacted,
        "localReactions:",
        Array.from(this.localReactions[messageId])
      );

      const reactions = currentMessage.reactions || {};

      const original = { ...currentMessage };
      const originalLocalReactions = new Set(this.localReactions[messageId]);
      const optimisticReactions = { ...reactions };

      if (userHasReacted) {
        this.localReactions[messageId].delete(emoji);
        this.saveLocalReactionsToStorage();
        if (optimisticReactions[emoji]) {
          const newCount = Math.max(
            0,
            (optimisticReactions[emoji].count || 1) - 1
          );
          if (newCount === 0) {
            delete optimisticReactions[emoji];
          } else {
            optimisticReactions[emoji] = {
              ...optimisticReactions[emoji],
              count: newCount,
            };
          }
        }
      } else {
        this.localReactions[messageId].add(emoji);
        this.saveLocalReactionsToStorage();
        if (optimisticReactions[emoji]) {
          optimisticReactions[emoji] = {
            ...optimisticReactions[emoji],
            count: (optimisticReactions[emoji].count || 0) + 1,
          };
        } else {
          optimisticReactions[emoji] = { count: 1, users: [] };
        }
      }

      const updated = { ...currentMessage, reactions: optimisticReactions };
      const newMessages = [...this.messagesRaw];
      newMessages.splice(idx, 1, updated);
      this.messagesRaw = newMessages;

      try {
        if (userHasReacted) {
          console.log("[toggleReaction] Removing reaction");
          await ChatService.removeReaction({
            conversationId: this.conversationId,
            messageId: m.id,
            emoji,
          });
        } else {
          console.log("[toggleReaction] Adding reaction");
          await ChatService.addReaction({
            conversationId: this.conversationId,
            messageId: m.id,
            emoji,
          });
        }

        const serverUpdated = await ChatService.getMessage({
          conversationId: this.conversationId,
          messageId: m.id,
        });
        if (serverUpdated && serverUpdated.id) {
          const currentIdx = this.messagesRaw.findIndex(
            (x) => String(x.id) === messageId
          );
          if (currentIdx !== -1) {
            const newMessages = [...this.messagesRaw];
            newMessages.splice(currentIdx, 1, serverUpdated);
            this.messagesRaw = newMessages;
          }
        }
      } catch (err) {
        console.error("Reaction toggle failed:", err);
        const revertIdx = this.messagesRaw.findIndex(
          (x) => String(x.id) === messageId
        );
        if (revertIdx !== -1) {
          const newMessages = [...this.messagesRaw];
          newMessages.splice(revertIdx, 1, original);
          this.messagesRaw = newMessages;
        }
        this.localReactions[messageId] = originalLocalReactions;
        this.saveLocalReactionsToStorage();
      }
    },
    loadLocalReactionsFromStorage() {
      try {
        const stored = localStorage.getItem(this.localReactionsStorageKey);
        if (stored) {
          const parsed = JSON.parse(stored);
          this.localReactions = {};
          for (const [messageId, emojis] of Object.entries(parsed)) {
            this.localReactions[messageId] = new Set(emojis);
          }
        }
      } catch (e) {
        // Silent fail - reactions will be empty
      }
    },
    saveLocalReactionsToStorage() {
      try {
        const toStore = {};
        for (const [messageId, emojis] of Object.entries(this.localReactions)) {
          toStore[messageId] = Array.from(emojis);
        }
        localStorage.setItem(
          this.localReactionsStorageKey,
          JSON.stringify(toStore)
        );
      } catch (e) {
        // Silent fail
      }
    },
    hasReacted(m, emoji) {
      const messageId = String(m.id);
      if (!this.localReactions[messageId]) {
        return false;
      }
      return this.localReactions[messageId].has(emoji);
    },
    openFilePicker() {
      const el = this.$refs.fileInput;
      if (el) el.click();
    },
    /**
     * Handle file selection and upload (matches Flutter's _sendFile pattern)
     * Uses optimistic UI with pendingMessage, upload progress, then confirmed message
     */
    async onFileSelected(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;

      if (this.isSending) return;

      // Validate user can send
      if (this.isUserBlocked) {
        this.$toast?.error(this.$t('chat.blocked_message') || 'You cannot send messages to this user');
        return;
      }
      if (!this.canSend) {
        this.$toast?.error(this.$t('chat.locked') || 'Chat is locked');
        return;
      }

      try {
        const type = (file.type || "").toLowerCase();
        let subtype = "document";
        if (type.startsWith("image/")) subtype = "image";
        else if (type.startsWith("video/")) subtype = "video";
        else if (type.startsWith("audio/")) subtype = "audio";

        // Create optimistic file message (matches Flutter's tempMessage pattern)
        const previewUrl = URL.createObjectURL(file);
        const tempMessage = {
          id: `temp_${Date.now()}`,
          conversationId: this.conversationId,
          type: "UserMessage",
          senderId: chatState.userId,
          text: "",
          readBy: [],
          createdAt: Date.now(),
          content: [
            {
              type: "file",
              subtype: subtype,
              filename: file.name,
              url: previewUrl, // Local preview URL
            },
          ],
          attachment: null,
          referencedMessageId: this.replyingTo ? this.replyingTo.id : undefined,
          custom: {
            isPending: true,
            uploadProgress: 0,
            localPath: previewUrl,
            localFile: file,
          },
        };

        // Capture reply ID before clearing
        const replyId = this.replyingTo ? this.replyingTo.id : null;
        this.replyingTo = null;

        // Set state BEFORE async operation (matches Flutter)
        this.isSending = true;
        this.pendingMessage = tempMessage;
        this.$nextTick(this.scrollToBottomSmooth);

        // Compute image dimensions if needed
        let width, height;
        if (subtype === "image") {
          try {
            const dims = await this.computeImageDimensions(file);
            width = dims.width;
            height = dims.height;
          } catch (_) {}
        }

        // Step 1: Upload file with progress (matches Flutter's uploadFile call)
        let fileToken = null;
        try {
          this.currentUploadController = new AbortController();
          
          const uploadResult = await ChatService.uploadFileWithCancel({
            file,
            filename: file.name,
            subtype,
            width,
            height,
            onProgress: (progress) => {
              // Update upload progress (matches Flutter's onProgress callback)
              // Cap at 90% - remaining 10% is for server confirmation
              const cappedProgress = Math.min(progress, 90);
              if (this.pendingMessage) {
                this.pendingMessage = {
                  ...this.pendingMessage,
                  custom: {
                    ...this.pendingMessage.custom,
                    uploadProgress: cappedProgress,
                  },
                };
              }
            },
            abortController: this.currentUploadController,
          });
          
          fileToken = uploadResult?.token || uploadResult;
          this.currentUploadController = null;

          // Upload confirmed - bump progress to 95% (matches Flutter)
          if (this.pendingMessage) {
            this.pendingMessage = {
              ...this.pendingMessage,
              custom: {
                ...this.pendingMessage.custom,
                uploadProgress: 95,
              },
            };
          }
        } catch (uploadErr) {
          this.currentUploadController = null;
          if (uploadErr.name === 'AbortError' || uploadErr.message === 'Upload cancelled') {
            console.log('[Conversation] Upload cancelled by user');
            this.revokePendingBlobUrl();
            this.pendingMessage = null;
            this.isSending = false;
            e.target.value = "";
            return;
          }
          throw new Error(`Upload failed: ${uploadErr.message}`);
        }

        if (!fileToken) {
          throw new Error("Upload succeeded but no file token returned");
        }

        // Step 2: Send message with file token (matches Flutter's sendMessage call)
        const confirmedMessage = replyId
          ? await ChatService.sendReply({
              conversationId: this.conversationId,
              text: "",
              referencedMessageId: replyId,
              fileToken,
              subtype,
              custom: {},
            })
          : await ChatService.sendMessage({
              conversationId: this.conversationId,
              text: "",
              fileToken,
              subtype,
              custom: {},
            });

        if (confirmedMessage && confirmedMessage.id) {
          // Clear pending message FIRST to avoid duplicate (matches Flutter)
          const localPath = this.pendingMessage?.custom?.localPath;
          this.pendingMessage = null;
          this.isSending = false;

          // Check if message already exists (avoid duplicates from Pusher)
          const exists = this.messagesRaw.some(
            (m) => String(m.id) === String(confirmedMessage.id)
          );

          if (!exists) {
            // Preserve localPath for smooth image transition
            const enhancedMessage = {
              ...confirmedMessage,
              custom: {
                ...(confirmedMessage.custom || {}),
                localPath: localPath,
              },
            };
            this.messagesRaw.unshift(enhancedMessage);
            this.lastMessageId = confirmedMessage.id;
          }

          // Update the shared conversation list preview in real-time
          updateLastMessage(this.conversationId, confirmedMessage);
          bumpConversationToTop(this.conversationId);

          // Revoke blob URL after a delay to allow smooth transition
          setTimeout(() => {
            if (localPath) {
              try { URL.revokeObjectURL(localPath); } catch (_) {}
            }
          }, 2000);

          this.$nextTick(this.scrollToBottomSmooth);
        } else {
          // Message was null but no error - clear pending state
          this.revokePendingBlobUrl();
          this.pendingMessage = null;
          this.isSending = false;
        }
      } catch (error) {
        console.error("File send error:", error);
        // Mark message as failed (matches Flutter's failedMessage pattern)
        if (this.pendingMessage) {
          this.pendingMessage = {
            ...this.pendingMessage,
            custom: {
              ...this.pendingMessage.custom,
              isFailed: true,
              isPending: false,
              uploadProgress: null, // Clear stale progress
              errorMessage: error.message || "Unknown error",
            },
          };
        }
        this.isSending = false;
      } finally {
        e.target.value = "";
      }
    },
    async computeImageDimensions(file) {
      return new Promise((resolve, reject) => {
        try {
          const img = new Image();
          const url = URL.createObjectURL(file);
          img.onload = () => {
            const w = img.naturalWidth;
            const h = img.naturalHeight;
            URL.revokeObjectURL(url);
            resolve({ width: w, height: h });
          };
          img.onerror = reject;
          img.src = url;
        } catch (e) {
          reject(e);
        }
      });
    },
    async resendFailedMessage(m) {
      // Like Flutter's _resendFailedMessage - works with pendingMessage
      if (
        !this.pendingMessage ||
        String(this.pendingMessage.id) !== String(m.id)
      )
        return;

      const mm = this.pendingMessage;
      const content = Array.isArray(mm.content) ? mm.content[0] : null;

      if (content && content.type === "file") {
        // Re-upload from local file if available
        const subtype =
          content.subtype ||
          (mm.custom && mm.custom.message_type === "audio"
            ? "voice"
            : "document");
        const file = mm.custom && mm.custom.localFile;
        const duration =
          mm.custom && mm.custom.voice_duration
            ? parseInt(mm.custom.voice_duration)
            : undefined;
        if (!file) return; // No local file to resend

        // Clear pending message and resend
        this.pendingMessage = null;
        await this.sendFileFromLocal(
          file,
          subtype,
          mm.referencedMessageId,
          duration
        );
      } else if (mm.text) {
        // Resend text
        this.pendingMessage = null;
        this.text = mm.text;
        await this.send();
      }
    },
    deleteFailedMessage(m) {
      // Like Flutter's _deleteFailedMessage - clears pendingMessage
      if (
        this.pendingMessage &&
        String(this.pendingMessage.id) === String(m.id)
      ) {
        this.revokePendingBlobUrl();
        this.pendingMessage = null;
        this.isSending = false;
      }
    },
    /**
     * Send file from local storage (for retries) - matches Flutter's _sendFile pattern
     */
    async sendFileFromLocal(
      file,
      subtype,
      referencedMessageId,
      durationSeconds
    ) {
      if (this.isSending) return;
      if (this.isUserBlocked || !this.canSend) return;

      const localUrl = URL.createObjectURL(file);
      const tempMessage = {
        id: `temp_${Date.now()}`,
        conversationId: this.conversationId,
        type: "UserMessage",
        senderId: chatState.userId,
        text: "",
        readBy: [],
        createdAt: Date.now(),
        content: [
          { type: "file", subtype, filename: file.name, url: localUrl },
        ],
        attachment: null,
        referencedMessageId,
        custom: {
          isPending: true,
          uploadProgress: 0,
          localFile: file,
          localPath: localUrl,
          ...(durationSeconds ? { voice_duration: String(durationSeconds) } : {}),
        },
      };

      this.isSending = true;
      this.pendingMessage = tempMessage;
      this.$nextTick(this.scrollToBottomSmooth);

      try {
        // Compute image dimensions if needed
        let width, height;
        if (subtype === "image") {
          try {
            const dims = await this.computeImageDimensions(file);
            width = dims.width;
            height = dims.height;
          } catch (_) {}
        }

        // Step 1: Upload file with progress (matches Flutter)
        const fileToken = await ChatService.uploadFile({
          file,
          filename: file.name,
          subtype,
          width,
          height,
          duration: durationSeconds,
          onProgress: (progress) => {
            // Cap at 90% - remaining 10% for server confirmation
            const cappedProgress = Math.min(progress, 90);
            if (this.pendingMessage) {
              this.pendingMessage = {
                ...this.pendingMessage,
                custom: {
                  ...this.pendingMessage.custom,
                  uploadProgress: cappedProgress,
                },
              };
            }
          },
        });

        if (!fileToken) {
          throw new Error("Failed to upload file");
        }

        // Upload confirmed - bump progress to 95% (matches Flutter)
        if (this.pendingMessage) {
          this.pendingMessage = {
            ...this.pendingMessage,
            custom: {
              ...this.pendingMessage.custom,
              uploadProgress: 95,
            },
          };
        }

        // Step 2: Send message with file token (matches Flutter)
        const confirmedMessage = referencedMessageId
          ? await ChatService.sendReply({
              conversationId: this.conversationId,
              text: "",
              referencedMessageId,
              fileToken,
              subtype,
              custom: {},
              durationSeconds,
            })
          : await ChatService.sendMessage({
              conversationId: this.conversationId,
              text: "",
              fileToken,
              subtype,
              custom: {},
              durationSeconds,
            });

        if (confirmedMessage && confirmedMessage.id) {
          // Clear pending message FIRST to avoid duplicate (matches Flutter)
          const localPath = this.pendingMessage?.custom?.localPath;
          this.pendingMessage = null;
          this.isSending = false;

          // Check if message already exists (avoid duplicates from Pusher)
          const exists = this.messagesRaw.some(
            (m) => String(m.id) === String(confirmedMessage.id)
          );

          if (!exists) {
            // Preserve localPath for smooth transition
            const enhancedMessage = {
              ...confirmedMessage,
              custom: {
                ...(confirmedMessage.custom || {}),
                localPath: localPath,
              },
            };
            this.messagesRaw.unshift(enhancedMessage);
            this.lastMessageId = confirmedMessage.id;
          }

          // Update the shared conversation list preview in real-time
          updateLastMessage(this.conversationId, confirmedMessage);
          bumpConversationToTop(this.conversationId);

          // Revoke blob URL after a delay
          setTimeout(() => {
            if (localPath) {
              try { URL.revokeObjectURL(localPath); } catch (_) {}
            }
          }, 2000);

          this.$nextTick(this.scrollToBottomSmooth);
        } else {
          // Message was null but no error - clear pending state
          this.revokePendingBlobUrl();
          this.pendingMessage = null;
          this.isSending = false;
        }
      } catch (error) {
        console.error("File send error:", error);
        // Mark message as failed (matches Flutter) - don't revoke URL so user can retry
        if (this.pendingMessage) {
          this.pendingMessage = {
            ...this.pendingMessage,
            custom: {
              ...this.pendingMessage.custom,
              isFailed: true,
              isPending: false,
              uploadProgress: null,
              errorMessage: error.message || "Unknown error",
            },
          };
        }
        this.isSending = false;
      }
    },
    async toggleRecording() {
      if (!this.isRecording) {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia)
          return;

        // Validate user can send
        if (this.isUserBlocked || !this.canSend) {
          return;
        }

        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
          });
          const mr = new MediaRecorder(stream);
          this.mediaRecorder = mr;
          this.recordChunks = [];
          this.recordStartedAt = Date.now();
          this.waveformData = [];

          // Set up Web Audio API for waveform capture (Flutter sync: _waveformData)
          try {
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
            this.analyser = this.audioContext.createAnalyser();
            this.analyser.fftSize = 256;
            const source = this.audioContext.createMediaStreamSource(stream);
            source.connect(this.analyser);

            // Capture waveform data periodically
            const bufferLength = this.analyser.frequencyBinCount;
            const dataArray = new Uint8Array(bufferLength);
            
            this.waveformInterval = setInterval(() => {
              if (this.analyser && this.isRecording) {
                this.analyser.getByteFrequencyData(dataArray);
                // Get average amplitude and normalize to 0-1
                const sum = dataArray.reduce((a, b) => a + b, 0);
                const avg = sum / bufferLength / 255;
                this.waveformData.push(Math.min(1, Math.max(0.1, avg * 2)));
                // Keep only last 50 samples for reasonable waveform
                if (this.waveformData.length > 50) {
                  this.waveformData.shift();
                }
              }
            }, 100); // Sample every 100ms
          } catch (audioErr) {
            console.warn('[Conversation] Web Audio API not available for waveform:', audioErr);
          }

          mr.ondataavailable = (ev) => {
            if (ev.data && ev.data.size > 0) this.recordChunks.push(ev.data);
          };

          mr.onstop = async () => {
            // Stop waveform capture first
            if (this.waveformInterval) {
              clearInterval(this.waveformInterval);
              this.waveformInterval = null;
            }
            if (this.audioContext) {
              try {
                this.audioContext.close();
              } catch (_) {}
              this.audioContext = null;
              this.analyser = null;
            }

            // CRITICAL: Check if we have recorded data
            if (!this.recordChunks || this.recordChunks.length === 0) {
              console.warn('[Conversation] No audio data recorded');
              this.isSending = false;
              return;
            }

            // CRITICAL: If already sending, queue this recording for later
            // This prevents race conditions where rapid stop/start causes failures
            if (this.isSending) {
              console.log('[Conversation] Already sending, skipping duplicate voice upload');
              return;
            }

            // Capture values BEFORE any async operations to avoid race conditions
            const recordedChunks = [...this.recordChunks];
            const recordStartTime = this.recordStartedAt;
            const capturedWaveform = [...this.waveformData];
            
            // Clear recording state immediately
            this.recordChunks = [];
            this.waveformData = [];

            // Create blob from recorded chunks
            const blob = new Blob(recordedChunks, { type: "audio/webm" });
            
            // Validate blob has content
            if (blob.size === 0) {
              console.warn('[Conversation] Empty audio blob, skipping upload');
              return;
            }

            const durationSeconds = Math.max(
              1,
              Math.round((Date.now() - recordStartTime) / 1000)
            );
            
            // Create file with unique timestamp
            const timestamp = Date.now();
            const file = new File([blob], `voice_${timestamp}.webm`, {
              type: "audio/webm",
            });
            const localUrl = URL.createObjectURL(blob);

            // Create optimistic voice message (matches Flutter's _pendingMessage)
            const tempMessage = {
              id: `temp_${timestamp}`,
              conversationId: this.conversationId,
              type: "UserMessage",
              senderId: chatState.userId,
              text: "",
              readBy: [],
              createdAt: timestamp,
              content: [
                {
                  type: "file",
                  subtype: "voice",
                  filename: file.name,
                  url: localUrl,
                  duration: durationSeconds,
                },
              ],
              attachment: null,
              custom: {
                isPending: true,
                message_type: 'audio',
                voice_duration: durationSeconds.toString(),
                uploadProgress: 0,
                localPath: localUrl,
                localFile: file,
                waveformData: capturedWaveform,
              },
            };

            // Set sending state BEFORE any async operations
            this.isSending = true;
            this.pendingMessage = tempMessage;
            this.$nextTick(this.scrollToBottomSmooth);

            try {
              // Step 1: Upload voice file with progress (matches Flutter's uploadFile call)
              const fileToken = await ChatService.uploadFile({
                file,
                filename: file.name,
                subtype: "voice",
                duration: durationSeconds,
                onProgress: (progress) => {
                  // Update upload progress (cap at 90%, remaining 10% for server confirmation)
                  const cappedProgress = Math.min(progress, 90);
                  if (this.pendingMessage && this.pendingMessage.id === tempMessage.id) {
                    this.pendingMessage = {
                      ...this.pendingMessage,
                      custom: {
                        ...this.pendingMessage.custom,
                        uploadProgress: cappedProgress,
                      },
                    };
                  }
                },
              });

              if (!fileToken) {
                throw new Error("Failed to upload voice - no token returned");
              }

              // Upload confirmed - bump progress to 95% (matches Flutter)
              if (this.pendingMessage && this.pendingMessage.id === tempMessage.id) {
                this.pendingMessage = {
                  ...this.pendingMessage,
                  custom: {
                    ...this.pendingMessage.custom,
                    uploadProgress: 95,
                  },
                };
              }

              // Step 2: Send message with file token (matches Flutter's sendMessage call)
              // NOTE: TalkJS custom fields must be strings, not arrays/objects
              const confirmedMessage = await ChatService.sendMessage({
                conversationId: this.conversationId,
                text: "",
                fileToken,
                subtype: "voice",
                durationSeconds,
                custom: {
                  // Stringify waveformData since TalkJS custom fields must be strings
                  waveformData: JSON.stringify(capturedWaveform),
                },
              });

              if (confirmedMessage && confirmedMessage.id) {
                // Clear pending message FIRST to avoid duplicate (matches Flutter)
                const localPath = this.pendingMessage?.custom?.localPath;
                this.pendingMessage = null;
                this.isSending = false;

                // Check if message already exists (avoid duplicates from Pusher)
                const exists = this.messagesRaw.some(
                  (m) => String(m.id) === String(confirmedMessage.id)
                );

                if (!exists) {
                  // Preserve waveformData and localPath for smooth transition
                  const enhancedMessage = {
                    ...confirmedMessage,
                    custom: {
                      ...(confirmedMessage.custom || {}),
                      waveformData: capturedWaveform,
                      localPath: localPath,
                    },
                  };
                  this.messagesRaw.unshift(enhancedMessage);
                  this.lastMessageId = confirmedMessage.id;
                }

                // Update the shared conversation list preview in real-time
                updateLastMessage(this.conversationId, confirmedMessage);
                bumpConversationToTop(this.conversationId);

                // Revoke blob URL after a delay to allow smooth transition
                setTimeout(() => {
                  if (localPath) {
                    try { URL.revokeObjectURL(localPath); } catch (_) {}
                  }
                }, 2000);

                this.$nextTick(this.scrollToBottomSmooth);
              } else {
                // Message was null but no error - clear pending state
                this.revokePendingBlobUrl();
                this.pendingMessage = null;
                this.isSending = false;
              }
            } catch (error) {
              console.error("Voice send error:", error);
              // Mark message as failed (matches Flutter's failedMessage pattern)
              // Only update if this is still the same pending message
              if (this.pendingMessage && this.pendingMessage.id === tempMessage.id) {
                this.pendingMessage = {
                  ...this.pendingMessage,
                  custom: {
                    ...this.pendingMessage.custom,
                    isFailed: true,
                    isPending: false,
                    uploadProgress: null,
                    errorMessage: error.message || "Failed to send voice message",
                  },
                };
              }
              this.isSending = false;
            } finally {
              // Always stop the media stream tracks
              stream.getTracks().forEach((t) => t.stop());
            }
          };

          mr.start();
          this.isRecording = true;
        } catch (error) {
          console.error("Recording error:", error);
          this.isRecording = false;
        }
      } else {
        try {
          this.mediaRecorder && this.mediaRecorder.stop();
        } catch (_) {}
        this.isRecording = false;
      }
    },
    renderAttachmentComponent() {
      return "AttachmentRenderer";
    },
    // Flutter sync: _scrollToBottom with multiple retry attempts
    scrollToBottom() {
      const el = this.$refs.list;
      if (!el) return;

      // Instant scroll with multiple attempts (like Flutter)
      el.scrollTop = el.scrollHeight;

      // Additional delayed attempts to ensure it works
      setTimeout(() => {
        if (this.$refs.list) {
          this.$refs.list.scrollTop = this.$refs.list.scrollHeight;
        }
      }, 100);

      setTimeout(() => {
        if (this.$refs.list) {
          this.$refs.list.scrollTop = this.$refs.list.scrollHeight;
        }
      }, 300);
    },
    scrollToBottomSmooth() {
      const el = this.$refs.list;
      if (!el) return;
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    },
    onScroll(e) {
      // Debounced scroll handler for performance
      if (this.scrollDebounceTimer) {
        clearTimeout(this.scrollDebounceTimer);
      }
      
      this.scrollDebounceTimer = setTimeout(() => {
        // Infinite scroll pagination - load more when near top
        // (messages are displayed oldest->newest, so scrolling up loads older)
        const el = e?.target || this.$refs.list;
        if (!el) return;
        
        // Check if scrolled near top (within 150px)
        if (el.scrollTop < 150 && !this.isLoadingMore && this.hasMoreMessages) {
          this.loadMoreMessages();
        }
      }, 100); // 100ms debounce
    },
    reload() {
      this.init();
    },
    // ==================== Block/Unblock (Flutter Sync) ====================
    // Like Flutter's _blockUser and _unblockUser methods
    async blockUser() {
      if (this.isGroupChat) return; // Only for DMs

      // Confirm action
      const confirmed = window.confirm(
        this.$t("chat.block_user_confirmation") ||
          "Are you sure you want to block this user? They will not be able to send you messages."
      );
      if (!confirmed) return;

      try {
        const result = await ChatService.blockUser(this.conversationId);

        if (result.success) {
          // Update state like Flutter's _handlePusherBlockStatus
          // When you block someone, you also cannot send messages (Flutter: _canSendMessages = false)
          this.isBlocked = true;
          this.isUserBlocker = true;
          this.isUserBlocked = false;
          this.canSend = false; // Blocker cannot send either (matches Flutter)

          // Refresh conversation to get updated state
          this.conversation = await ChatService.getConversation(
            this.conversationId
          );
          this.applyConversationGuards();

          // Show success message
          alert(this.$t("chat.user_blocked") || "User has been blocked");
        } else {
          alert(
            result.message ||
              this.$t("chat.failed_to_block") ||
              "Failed to block user"
          );
        }
      } catch (e) {
        console.error("Block user error:", e);
        alert(this.$t("chat.failed_to_block") || "Failed to block user");
      }
    },
    async unblockUser() {
      if (this.isGroupChat) return; // Only for DMs
      if (!this.isUserBlocker) return; // Can only unblock if we blocked them

      // Confirm action
      const confirmed = window.confirm(
        this.$t("chat.unblock_user_confirmation") ||
          "Are you sure you want to unblock this user?"
      );
      if (!confirmed) return;

      try {
        const result = await ChatService.unblockUser(this.conversationId);

        if (result.success) {
          // Update state like Flutter's _handlePusherBlockStatus
          this.isBlocked = false;
          this.isUserBlocker = false;
          this.isUserBlocked = false;
          this.canSend = true;

          // Refresh conversation to get updated state
          this.conversation = await ChatService.getConversation(
            this.conversationId
          );
          this.applyConversationGuards();

          // Show success message
          alert(this.$t("chat.user_unblocked") || "User has been unblocked");
        } else {
          alert(
            result.message ||
              this.$t("chat.failed_to_unblock") ||
              "Failed to unblock user"
          );
        }
      } catch (e) {
        console.error("Unblock user error:", e);
        alert(this.$t("chat.failed_to_unblock") || "Failed to unblock user");
      }
    },
    // ==================== Group Moderation (Flutter Sync) ====================
    async toggleChatLock() {
      if (!this.isGroupChat || !this.isCurrentUserOwner) return;

      try {
        const result = await ChatService.toggleChatLock(this.conversationId);

        if (result.success) {
          // Toggle local state
          this.isChatLocked = !this.isChatLocked;

          // Refresh conversation
          this.conversation = await ChatService.getConversation(
            this.conversationId
          );
          this.applyConversationGuards();

          const msg = this.isChatLocked
            ? this.$t("chat.chat_locked") || "Chat has been locked"
            : this.$t("chat.chat_unlocked") || "Chat has been unlocked";
          alert(msg);
        } else {
          alert(
            result.message ||
              this.$t("chat.failed_to_toggle_lock") ||
              "Failed to toggle chat lock"
          );
        }
      } catch (e) {
        console.error("Toggle chat lock error:", e);
        alert(
          this.$t("chat.failed_to_toggle_lock") || "Failed to toggle chat lock"
        );
      }
    },
    async leaveGroup() {
      if (!this.isGroupChat) return;

      // Confirm action
      const confirmed = window.confirm(
        this.$t("chat.leave_group_confirmation") ||
          "Are you sure you want to leave this group?"
      );
      if (!confirmed) return;

      try {
        const result = await ChatService.leaveConversation(this.conversationId);

        if (result.success) {
          // Navigate back to chat list
          this.$router.push({ name: "ChatMaster" });

          // Show appropriate message based on response (like Flutter)
          const adminLeft = result.admin_left;
          const adminTransferred = result.admin_transferred;
          const groupMuted = result.group_muted;

          let msg;
          if (adminLeft && groupMuted) {
            msg =
              this.$t("chat.left_group_admin_muted") ||
              "You left the group. The group has been muted.";
          } else if (adminLeft && adminTransferred) {
            msg =
              this.$t("chat.left_group_admin_transferred") ||
              "You left the group. Admin role has been transferred.";
          } else {
            msg =
              this.$t("chat.left_group_successfully") ||
              "You have left the group";
          }
          alert(msg);
        } else {
          alert(
            result.message ||
              this.$t("chat.failed_to_leave_group") ||
              "Failed to leave group"
          );
        }
      } catch (e) {
        console.error("Leave group error:", e);
        alert(this.$t("chat.failed_to_leave_group") || "Failed to leave group");
      }
    },
    // ==================== Mia Summary (Flutter Sync: _showChatSummary) ====================
    selectSummaryPreset(preset) {
      // Prevent changing preset if summary already exists
      if (this.summaryResult) return;
      
      this.summaryPreset = preset;
      // Don't clear the summary result when switching tabs - only clear error
      this.summaryError = null;
      const now = new Date();
      const todayStr = now.toISOString().split("T")[0];
      if (preset === "today") {
        this.summaryStartDate = todayStr;
        this.summaryEndDate = todayStr;
      } else if (preset === "yesterday") {
        const y = new Date(now);
        y.setDate(y.getDate() - 1);
        const yStr = y.toISOString().split("T")[0];
        this.summaryStartDate = yStr;
        this.summaryEndDate = yStr;
      }
    },
    async generateSummary() {
      console.log('[Summary] Generate clicked. Loading:', this.summaryLoading, 'Result exists:', !!this.summaryResult);
      // Prevent generating summary if one already exists
      if (this.summaryLoading || this.summaryResult) {
        console.log('[Summary] BLOCKED - Summary already exists or loading');
        return;
      }
      this.summaryLoading = true;
      this.summaryError = null;
      this.summaryMessageCount = null;
      this.summaryMetadata = null;
      try {
        const todayStr = new Date().toISOString().split("T")[0];
        const startDate = new Date(this.summaryStartDate || todayStr);
        let endDate;
        if (this.summaryPreset === "yesterday") {
          endDate = new Date(this.summaryEndDate || todayStr);
          endDate.setHours(23, 59, 59, 999);
        } else if (this.summaryPreset === "today") {
          endDate = new Date();
        } else {
          endDate = new Date(this.summaryEndDate || todayStr);
          endDate.setHours(23, 59, 59, 999);
        }
        const result = await ChatService.getChatSummary(
          this.conversationId,
          startDate,
          endDate
        );
        if (result.success && result.summary) {
          this.summaryResult = result.summary;
          this.summaryMessageCount = result.message_count;
          this.summaryMetadata = result.metadata;
          console.log('[Summary] Result set:', this.summaryResult ? 'YES' : 'NO');
          console.log('[Summary] Button should be disabled:', this.isSummaryButtonDisabled);
          this.$forceUpdate(); // Force re-render to update button state
        } else {
          this.summaryError =
            result.message ||
            this.$t("chat.failed_to_load_summary") ||
            "Failed to generate summary";
        }
      } catch (e) {
        console.error("[Conversation] Summary error:", e);
        this.summaryError =
          this.$t("chat.failed_to_load_summary") ||
          "Failed to generate summary";
      } finally {
        this.summaryLoading = false;
      }
    },
    copySummary() {
      if (this.summaryResult) {
        navigator.clipboard
          .writeText(this.summaryResult)
          .then(() => {
            alert(
              this.$t("chat.summary_copied") || "Summary copied to clipboard"
            );
          })
          .catch(() => {});
      }
    },
    shareSummary() {
      if (this.summaryResult && navigator.share) {
        navigator
          .share({
            title:
              this.$t("chat.conversation_summary") || "Conversation Summary",
            text: this.summaryResult,
          })
          .catch(() => {});
      } else if (this.summaryResult) {
        // Fallback: copy to clipboard
        this.copySummary();
      }
    },

    // ==================== NEW FEATURES (Flutter Parity) ====================

    // ===== Message Info Sheet (Flutter sync: MessageInfoSheet) =====
    openMessageInfo(message) {
      this.messageInfoTarget = message;
      this.showMessageInfoSheet = true;
    },
    closeMessageInfo() {
      this.showMessageInfoSheet = false;
      this.messageInfoTarget = null;
    },

    // ===== Enhanced Emoji Picker (Flutter sync: EmojiPickerSheet) =====
    openEnhancedEmojiPicker(message) {
      this.emojiPickerTargetMessage = message;
      this.showEmojiPickerSheet = true;
    },
    closeEnhancedEmojiPicker() {
      this.showEmojiPickerSheet = false;
      this.emojiPickerTargetMessage = null;
    },
    async onEmojiSelected(emoji) {
      if (this.emojiPickerTargetMessage) {
        await this.toggleReaction(this.emojiPickerTargetMessage, emoji);
      }
    },
    getUserReactedEmojis(message) {
      const messageId = String(message?.id);
      return this.localReactions[messageId] || new Set();
    },

    // ===== Mute Chat (Flutter sync: MuteChatDialog) =====
    handleMuteAction() {
      // Check current mute state
      if (this.isMuted) {
        // If muted, unmute immediately
        this.unmuteConversation();
      } else {
        // If not muted, open mute dialog
        this.openMuteDialog();
      }
    },
    async unmuteConversation() {
      try {
        await ChatService.unmuteConversation(this.conversationId);
        console.log('[Conversation] Conversation unmuted successfully');
        this.onUnmuted();
      } catch (e) {
        console.error('[Conversation] Failed to unmute conversation:', e);
      }
    },
    openMuteDialog() {
      this.showMuteDialog = true;
    },
    closeMuteDialog() {
      this.showMuteDialog = false;
    },
    async loadMuteStatus() {
      try {
        const status = await ChatService.getMuteStatus(this.conversationId);
        this.isMuted = status.isMuted;
      } catch (e) {
        console.error('[Conversation] Failed to load mute status:', e);
      }
    },
    onMuted(duration) {
      this.isMuted = true;
      console.log('[Conversation] Chat muted for:', duration);
      // Emit to parent Master.vue to update conversation list
      this.$emit('muted', duration);
    },
    onUnmuted() {
      this.isMuted = false;
      console.log('[Conversation] Chat unmuted');
      // Emit to parent Master.vue to update conversation list
      this.$emit('unmuted');
    },

    // ===== Chat Moderation Sheet (Flutter sync: ChatModerationSheet) =====
    openModerationSheet() {
      this.showModerationSheet = true;
    },
    closeModerationSheet() {
      this.showModerationSheet = false;
    },
    async onModerationAction(result) {
      console.log('[Conversation] Moderation action:', result);
      // Refresh conversation state
      this.conversation = await ChatService.getConversation(this.conversationId);
      this.applyConversationGuards();
      
      if (result.action === 'toggle_lock') {
        this.isChatLocked = result.isLocked;
      }
    },

    // ===== In-Conversation Search (Flutter sync: _isSearchMode) =====
    toggleSearchMode() {
      this.isSearchMode = !this.isSearchMode;
      this.$emit("search-toggled", this.isSearchMode);
      if (!this.isSearchMode) {
        this.clearSearch();
      } else {
        this.$nextTick(() => {
          this.$refs.searchInput?.focus();
        });
      }
    },
    onSearchInput() {
      const query = this.searchQuery.trim().toLowerCase();
      if (!query) {
        this.searchResults = [];
        this.currentSearchResultIndex = -1;
        return;
      }
      
      // Search through messages
      this.searchResults = this.messagesRaw
        .filter(m => m.text && m.text.toLowerCase().includes(query))
        .map(m => m.id);
      
      this.currentSearchResultIndex = this.searchResults.length > 0 ? 0 : -1;
      
      if (this.currentSearchResultIndex >= 0) {
        this.scrollToSearchResult();
      }
    },
    navigateSearchResult(direction) {
      if (this.searchResults.length === 0) return;
      
      if (direction === 'next') {
        this.currentSearchResultIndex = 
          (this.currentSearchResultIndex + 1) % this.searchResults.length;
      } else {
        this.currentSearchResultIndex = 
          (this.currentSearchResultIndex - 1 + this.searchResults.length) % this.searchResults.length;
      }
      
      this.scrollToSearchResult();
    },
    scrollToSearchResult() {
      if (this.currentSearchResultIndex < 0) return;
      const messageId = this.searchResults[this.currentSearchResultIndex];
      this.scrollToMessage(messageId, true);
    },
    clearSearch() {
      this.searchQuery = '';
      this.searchResults = [];
      this.currentSearchResultIndex = -1;
      this.highlightedMessageId = null;
    },

    // ===== Scroll-to-Reply (Flutter sync: _messageKeys, _highlightedMessageId) =====
    setMessageRef(messageId, el) {
      if (el) {
        this.messageRefs[messageId] = el;
      } else {
        delete this.messageRefs[messageId];
      }
    },
    scrollToMessage(messageId, highlight = true) {
      const el = this.messageRefs[messageId];
      if (!el) {
        console.warn('[Conversation] Message element not found:', messageId);
        return;
      }
      
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      
      if (highlight) {
        // Clear previous highlight
        if (this.highlightTimeout) {
          clearTimeout(this.highlightTimeout);
        }
        
        this.highlightedMessageId = messageId;
        
        // Remove highlight after animation
        this.highlightTimeout = setTimeout(() => {
          this.highlightedMessageId = null;
        }, 2000);
      }
    },
    onReplyPreviewClick(replyMessage) {
      if (replyMessage && replyMessage.id) {
        this.scrollToMessage(replyMessage.id, true);
      }
    },

    // ===== Selection Mode (Flutter sync: _isSelectionMode) =====
    toggleSelectionMode() {
      this.isSelectionMode = !this.isSelectionMode;
      if (!this.isSelectionMode) {
        this.selectedMessageIds = new Set();
      }
    },
    enterSelectionMode(message) {
      // Enter selection mode and select the long-pressed message
      this.isSelectionMode = true;
      this.selectedMessageIds = new Set([message.id]);
      // Haptic feedback
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
    },
    toggleMessageSelection(messageId) {
      const newSet = new Set(this.selectedMessageIds);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      this.selectedMessageIds = newSet;
      
      // Exit selection mode if no messages selected
      if (newSet.size === 0) {
        this.isSelectionMode = false;
      }
    },
    selectAllMessages() {
      this.selectedMessageIds = new Set(
        this.messagesRaw.filter(m => this.isMe(m)).map(m => m.id)
      );
    },
    deleteSelectedMessages() {
      if (this.selectedMessageIds.size === 0) return;
      this.showBulkDeleteModal = true;
    },
    closeBulkDeleteModal() {
      if (this.bulkDeleteInProgress) return;
      this.showBulkDeleteModal = false;
    },
    async confirmBulkDelete() {
      if (this.selectedMessageIds.size === 0) return;
      
      this.bulkDeleteInProgress = true;
      
      try {
        const messageIds = Array.from(this.selectedMessageIds);
        const deletedIds = [];
        
        // Delete messages one by one using the single delete endpoint
        for (const messageId of messageIds) {
          const success = await ChatService.deleteMessage({
            conversationId: this.conversationId,
            messageId: messageId,
          });
          if (success) {
            deletedIds.push(messageId);
          }
        }
        
        if (deletedIds.length > 0) {
          // Remove successfully deleted messages from local state
          const deletedSet = new Set(deletedIds);
          this.messagesRaw = this.messagesRaw.filter(
            m => !deletedSet.has(m.id)
          );
          this.selectedMessageIds = new Set();
          this.isSelectionMode = false;
        }
        
        if (deletedIds.length < messageIds.length) {
          const failedCount = messageIds.length - deletedIds.length;
          alert(`فشل حذف ${failedCount} رسالة`);
        }
      } catch (e) {
        console.error('[Conversation] Delete selected messages error:', e);
        alert('Failed to delete messages');
      } finally {
        this.bulkDeleteInProgress = false;
        this.showBulkDeleteModal = false;
      }
    },

    // ===== Infinite Scroll Pagination (Flutter sync: afterMessageId) =====
    async loadMoreMessages() {
      if (this.isLoadingMore || !this.hasMoreMessages) return;
      
      this.isLoadingMore = true;
      
      try {
        const oldestId = this.messagesRaw.length > 0 
          ? this.messagesRaw[this.messagesRaw.length - 1].id 
          : null;
        
        const olderMessages = await ChatService.getMessages({
          conversationId: this.conversationId,
          limit: 50,
          afterMessageId: oldestId,
        });
        
        if (!olderMessages || olderMessages.length === 0) {
          this.hasMoreMessages = false;
        } else {
          // Filter out duplicates before appending
          const existingIds = new Set(this.messagesRaw.map(m => String(m.id)));
          const uniqueOlder = olderMessages.filter(m => !existingIds.has(String(m.id)));
          
          if (uniqueOlder.length === 0) {
            // All returned messages are duplicates, likely end of history
            this.hasMoreMessages = false;
          } else {
            // Append older messages (they come newest first)
            this.messagesRaw = [...this.messagesRaw, ...uniqueOlder];
            
            // If we got fewer unique messages than requested, we're near the end
            if (uniqueOlder.length < 50) {
              this.hasMoreMessages = olderMessages.length === 50; // Only continue if API returned full page
            }
          }
        }
      } catch (e) {
        console.error('[Conversation] Load more error:', e);
      } finally {
        this.isLoadingMore = false;
      }
    },
    onScrollForPagination(e) {
      const el = e.target;
      // Load more when scrolled near top (since messages are reversed)
      if (el.scrollTop < 100 && !this.isLoadingMore && this.hasMoreMessages) {
        this.loadMoreMessages();
      }
    },

    // ===== Upload Cancel (Flutter sync: _uploadCancelToken) =====
    cancelCurrentUpload() {
      if (this.currentUploadController) {
        this.currentUploadController.abort();
        this.currentUploadController = null;
        this.revokePendingBlobUrl();
        this.pendingMessage = null;
      }
    },
    
    // Revoke blob URL from pending message to prevent memory leak
    revokePendingBlobUrl() {
      if (this.pendingMessage?.custom?.localPath) {
        try {
          URL.revokeObjectURL(this.pendingMessage.custom.localPath);
        } catch (_) {}
      }
    },

    // ===== Auto-play Voice Messages (Flutter sync: _autoPlayMessageId) =====
    onVoiceMessageEnded(messageId) {
      if (!this.autoPlayNextVoice) return;
      
      // Find next voice message
      const messages = this.displayMessages;
      const currentIndex = messages.findIndex(m => m.id === messageId);
      
      for (let i = currentIndex + 1; i < messages.length; i++) {
        const msg = messages[i];
        const subtype = msg.custom?.message_type || msg.content?.[0]?.subtype;
        if (subtype === 'voice' || subtype === 'audio') {
          this.currentPlayingVoiceId = msg.id;
          // Emit event to trigger playback (handled by VoiceMessagePlayer)
          this.$nextTick(() => {
            const playerEl = this.$refs[`voice-${msg.id}`];
            if (playerEl && playerEl.play) {
              playerEl.play();
            }
          });
          return;
        }
      }
      
      // No more voice messages
      this.currentPlayingVoiceId = null;
    },
    toggleAutoPlayVoice() {
      this.autoPlayNextVoice = !this.autoPlayNextVoice;
    },

    // ===== Build Participants Map =====
    buildParticipantsMap() {
      const conv = this.conversation || {};
      const participants = conv.participants || {};
      const custom = conv.custom || {};
      
      this.participantsMap = {};
      
      // From participants object
      Object.entries(participants).forEach(([id, data]) => {
        this.participantsMap[id] = {
          id,
          name: data.name || data.username,
          avatar: data.avatar || data.photoUrl,
        };
      });
      
      // From participants_details
      try {
        const details = custom.participants_details;
        if (details) {
          const parsed = typeof details === 'string' ? JSON.parse(details) : details;
          if (Array.isArray(parsed)) {
            parsed.forEach(p => {
              this.participantsMap[String(p.id)] = {
                id: p.id,
                name: p.name || p.username,
                avatar: p.avatar || p.image,
              };
            });
          }
        }
      } catch (e) {
        // Silent fail
      }
    },

    // ===== Swipe-to-Reply (Flutter sync: Dismissible) =====
    onSwipeTouchStart(event, message) {
      if (!event.touches || event.touches.length !== 1) return;
      
      const touch = event.touches[0];
      this.swipeStartX = touch.clientX;
      this.swipeStartY = touch.clientY;
      this.swipeCurrentX = touch.clientX;
      this.isSwipeTracking = true;
      this.swipeMessage = message;
      this.swipeTriggered = false;
    },
    
    onSwipeTouchMove(event) {
      if (!this.isSwipeTracking || !event.touches || event.touches.length !== 1) return;
      
      const touch = event.touches[0];
      const diffX = touch.clientX - this.swipeStartX;
      const diffY = touch.clientY - this.swipeStartY;
      
      // Cancel if vertical movement is dominant
      if (Math.abs(diffY) > Math.abs(diffX) && Math.abs(diffX) < 20) {
        this.resetSwipe();
        return;
      }
      
      this.swipeCurrentX = touch.clientX;
      
      // Check threshold (80px)
      const swipeOffset = this.getSwipeOffset(this.swipeMessage);
      if (!this.swipeTriggered && Math.abs(swipeOffset) >= 80) {
        this.swipeTriggered = true;
        // Haptic feedback
        if (navigator.vibrate) {
          navigator.vibrate(10);
        }
      }
      
      // Prevent scroll when swiping horizontally
      if (Math.abs(diffX) > 10) {
        event.preventDefault();
      }
    },
    
    onSwipeTouchEnd() {
      if (!this.isSwipeTracking) return;
      
      if (this.swipeTriggered && this.swipeMessage) {
        this.startReply(this.swipeMessage);
      }
      
      this.resetSwipe();
    },
    
    getSwipeOffset(message) {
      if (!this.isSwipeTracking || !message || this.swipeMessage?.id !== message.id) {
        return 0;
      }
      
      const diff = this.swipeCurrentX - this.swipeStartX;
      const isOwn = this.isMe(message);
      const maxSwipe = 100;
      
      if (isOwn) {
        // Own message - swipe left (negative)
        return Math.max(-maxSwipe, Math.min(0, diff));
      } else {
        // Other message - swipe right (positive)
        return Math.min(maxSwipe, Math.max(0, diff));
      }
    },
    
    getSwipeStyle(message) {
      const offset = this.getSwipeOffset(message);
      if (offset === 0) return {};
      
      return {
        transform: `translateX(${offset}px)`,
        transition: this.isSwipeTracking ? 'none' : 'transform 0.2s ease-out',
      };
    },
    
    getSwipeIndicatorStyle(message) {
      const offset = this.getSwipeOffset(message);
      const absOffset = Math.abs(offset);
      const opacity = Math.min(1, absOffset / 80);
      const scale = Math.min(1, absOffset / 60);
      
      return {
        opacity,
        transform: `scale(${scale})`,
      };
    },
    
    resetSwipe() {
      this.isSwipeTracking = false;
      this.swipeStartX = 0;
      this.swipeStartY = 0;
      this.swipeCurrentX = 0;
      this.swipeMessage = null;
      this.swipeTriggered = false;
    },

    // ===== Presence/Online Status (Flutter sync: _fetchUserOnlineStatus) =====
    async fetchOnlineStatus() {
      // Only for direct chats
      if (this.isGroupChat || !this.directChatPeer) return;
      
      try {
        const peerId = this.directChatPeer.id;
        const status = await ChatService.getUserStatus(peerId);
        
        if (status) {
          this.peerOnlineStatus = {
            isOnline: status.isOnline || status.is_online || false,
            lastSeen: status.lastSeen || status.last_seen || null,
          };
        }
      } catch (e) {
        console.error('[Conversation] Failed to fetch online status:', e);
      }
    },
    
    startOnlineStatusPoll() {
      // Poll every 30 seconds for online status
      if (this.onlineStatusPollTimer) {
        clearInterval(this.onlineStatusPollTimer);
      }
      
      // Initial fetch
      this.fetchOnlineStatus();
      
      // Start polling
      this.onlineStatusPollTimer = setInterval(() => {
        this.fetchOnlineStatus();
      }, 30000);
    },
    
    stopOnlineStatusPoll() {
      if (this.onlineStatusPollTimer) {
        clearInterval(this.onlineStatusPollTimer);
        this.onlineStatusPollTimer = null;
      }
    },
    
    formatLastSeen(timestamp) {
      if (!timestamp) return this.$t("chat.last_seen_recently") || "last seen recently";
      
      const date = typeof timestamp === 'number' 
        ? new Date(timestamp) 
        : new Date(timestamp);
      
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 1) {
        return this.$t("chat.last_seen_just_now") || "last seen just now";
      }
      if (diffMins < 60) {
        return (this.$t("chat.last_seen_minutes_ago") || "last seen {n} min ago")
          .replace("{n}", diffMins);
      }
      if (diffHours < 24) {
        return (this.$t("chat.last_seen_hours_ago") || "last seen {n}h ago")
          .replace("{n}", diffHours);
      }
      if (diffDays === 1) {
        return this.$t("chat.last_seen_yesterday") || "last seen yesterday";
      }
      if (diffDays < 7) {
        return (this.$t("chat.last_seen_days_ago") || "last seen {n} days ago")
          .replace("{n}", diffDays);
      }
      
      // Format as date
      return (this.$t("chat.last_seen_on") || "last seen on {date}")
        .replace("{date}", date.toLocaleDateString());
    },

    // ===== Socket Connection Handling (Flutter sync: _connectionState) =====
    subscribeToConnectionState() {
      if (this.connectionStateUnsubscribe) return;
      
      this.connectionStateUnsubscribe = pusherService.onConnectionStateChange((state) => {
        console.log(`[Conversation] Connection state changed: ${this.socketConnectionState} -> ${state}`);
        this.socketConnectionState = state;
        
        // If reconnected, refresh messages to catch any missed
        if (state === 'connected' && this.messagesRaw.length > 0) {
          this.refreshMessagesAfterReconnect();
        }
      });
      
      // Also listen for visibility changes to sync state when tab becomes visible
      this.visibilityChangeHandler = () => {
        if (document.visibilityState === 'visible') {
          // Sync connection state when tab becomes visible
          pusherService.syncConnectionState();
        }
      };
      document.addEventListener('visibilitychange', this.visibilityChangeHandler);
    },
    
    unsubscribeFromConnectionState() {
      if (this.connectionStateUnsubscribe) {
        this.connectionStateUnsubscribe();
        this.connectionStateUnsubscribe = null;
      }
      if (this.visibilityChangeHandler) {
        document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
        this.visibilityChangeHandler = null;
      }
    },
    
    reconnectSocket() {
      pusherService.reconnect();
    },
    
    async refreshMessagesAfterReconnect() {
      // Fetch latest messages to catch any missed during disconnect
      try {
        const result = await ChatService.getMessages({
          conversationId: this.conversationId,
          limit: 20,
        });
        
        if (result && result.messages) {
          // Merge new messages with existing
          const newMessages = result.messages.filter(
            newMsg => !this.messagesRaw.some(m => m.id === newMsg.id)
          );
          
          if (newMessages.length > 0) {
            this.messagesRaw = [...newMessages, ...this.messagesRaw];
            this.$nextTick(this.scrollToBottomSmooth);
          }
        }
      } catch (e) {
        console.error('[Conversation] Failed to refresh messages after reconnect:', e);
      }
    },
  },
};
</script>

<style scoped>
/* ========================================
   Professional Chat Design
   WhatsApp/Messenger inspired UI
   Fixed input + scrollable messages
   ======================================== */

/* ===== CSS DESIGN SYSTEM ===== */
:root {
  /* Primary Colors */
  --chat-primary: #00c444;
  --chat-primary-dark: #00a838;
  --chat-primary-light: #dcfce7;
  --chat-primary-rgb: 0, 196, 68;
  
  /* Background Colors */
  --chat-bg: #f0f4f0;
  --chat-bg-secondary: #f8faf8;
  --chat-bubble-own: #d9fdd3;
  --chat-bubble-other: #ffffff;
  
  /* Text Colors */
  --chat-text: #1f2937;
  --chat-text-secondary: #4b5563;
  --chat-text-muted: #6b7280;
  --chat-text-light: #9ca3af;
  
  /* Border & Divider */
  --chat-border: #e5e7eb;
  --chat-border-light: #f3f4f6;
  --chat-divider: #d1d5db;
  
  /* Action Button Colors */
  --chat-action-bg: #00c444;
  --chat-action-bg-hover: #00a838;
  --chat-action-text: #ffffff;
  --chat-action-secondary-bg: #f3f4f6;
  --chat-action-secondary-text: #4b5563;
  
  /* Status Colors */
  --chat-success: #10b981;
  --chat-warning: #f59e0b;
  --chat-error: #ef4444;
  --chat-info: #3b82f6;
  
  /* Shadows */
  --chat-shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.05);
  --chat-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  --chat-shadow-md: 0 4px 12px rgba(0, 0, 0, 0.1);
  --chat-shadow-lg: 0 8px 24px rgba(0, 0, 0, 0.12);
  --chat-shadow-primary: 0 4px 14px rgba(var(--chat-primary-rgb), 0.35);
  
  /* Spacing */
  --chat-spacing-xs: 4px;
  --chat-spacing-sm: 8px;
  --chat-spacing-md: 12px;
  --chat-spacing-lg: 16px;
  --chat-spacing-xl: 24px;
  
  /* Border Radius */
  --chat-radius-sm: 8px;
  --chat-radius-md: 12px;
  --chat-radius-lg: 16px;
  --chat-radius-xl: 24px;
  --chat-radius-full: 9999px;
  
  /* Transitions */
  --chat-transition-fast: 0.15s ease;
  --chat-transition: 0.2s ease;
  --chat-transition-slow: 0.3s ease;
  
  /* Button Sizes */
  --chat-btn-size-sm: 32px;
  --chat-btn-size-md: 40px;
  --chat-btn-size-lg: 44px;
  --chat-btn-icon-sm: 16px;
  --chat-btn-icon-md: 18px;
  --chat-btn-icon-lg: 20px;
}

/* Selection Mode Toolbar */
.selection-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  background: linear-gradient(135deg, #00c444 0%, #00a344 100%);
  color: #ffffff;
  flex-shrink: 0;
  box-shadow: 
    0 4px 16px rgba(0, 196, 68, 0.35),
    0 2px 4px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 10;
}

.selection-toolbar__left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.selection-toolbar__close {
  width: 38px;
  height: 38px;
  border: none;
  background: rgba(255, 255, 255, 0.25);
  border-radius: 50%;
  color: #ffffff !important;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.25s ease;
  backdrop-filter: blur(4px);
}

.selection-toolbar__close i {
  color: #ffffff !important;
  font-size: 16px;
}

.selection-toolbar__close:hover {
  background: rgba(255, 255, 255, 0.4);
  transform: rotate(90deg);
}

.selection-toolbar__count {
  font-size: 17px;
  font-weight: 700;
  color: #ffffff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 6px;
}

.selection-toolbar__count::before {
  content: '';
  width: 8px;
  height: 8px;
  background: #ffffff;
  border-radius: 50%;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(0.8); }
}

.selection-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.selection-toolbar__btn {
  width: 44px;
  height: 44px;
  border: none;
  background: rgba(255, 255, 255, 0.25);
  border-radius: 12px;
  color: #ffffff !important;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 20px;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(4px);
}

.selection-toolbar__btn i {
  color: #ffffff !important;
}

.selection-toolbar__btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.4);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.selection-toolbar__btn:active:not(:disabled) {
  transform: translateY(0);
}

.selection-toolbar__btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  transform: none;
}

.selection-toolbar__btn--danger {
  background: rgba(239, 68, 68, 0.9);
}

.selection-toolbar__btn--danger:hover:not(:disabled) {
  background: #dc2626;
  box-shadow: 0 4px 14px rgba(239, 68, 68, 0.4);
}

/* Slide down transition */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-100%);
}

/* Selection Checkbox */
.msg-selection-checkbox {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  cursor: pointer;
}

.checkbox-circle {
  width: 24px;
  height: 24px;
  border: 2px solid var(--chat-primary);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  background: #fff;
}

.checkbox-circle--checked {
  background: var(--chat-primary);
  color: #fff;
}

.checkbox-circle i {
  font-size: 14px;
  font-weight: bold;
}

/* Selection mode row styling */
.msg-row--selection-mode {
  cursor: pointer;
}

.msg-row--selection-mode.msg-row--own {
  padding-left: 8px;
}

/* ===== CONNECTION STATUS BANNER ===== */
.connection-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--chat-spacing-md);
  padding: var(--chat-spacing-md) var(--chat-spacing-lg);
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  color: #92400e;
  font-size: 13px;
  font-weight: 600;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(251, 191, 36, 0.2);
}

.connection-banner--error {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: #991b1b;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.2);
}

.connection-banner--success {
  background: linear-gradient(135deg, var(--chat-primary-light) 0%, #bbf7d0 100%);
  color: #166534;
  box-shadow: 0 2px 8px rgba(var(--chat-primary-rgb), 0.2);
}

.connection-banner i {
  font-size: 18px;
}

.connection-banner i.spin {
  animation: spin 1s linear infinite;
}

.connection-banner__retry {
  padding: 6px 14px;
  border: none;
  background: rgba(255, 255, 255, 0.9);
  border-radius: var(--chat-radius-full);
  font-size: 12px;
  font-weight: 700;
  color: inherit;
  cursor: pointer;
  transition: all var(--chat-transition);
  box-shadow: var(--chat-shadow-sm);
}

.connection-banner__retry:hover {
  background: #fff;
  transform: scale(1.05);
  box-shadow: var(--chat-shadow);
}

.connection-banner__retry:active {
  transform: scale(0.95);
}

/* Main Container - Fixed height, no page scroll */
.chat-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  max-height: 100vh;
  background: var(--chat-bg);
  overflow: hidden;
}

/* When embedded in Master.vue */
.embedded-host.chat-container {
  height: 100%;
  max-height: 100%;
  background: transparent;
}

/* ===== CHAT HEADER ===== */
.chat-header {
  background: #ffffff;
  border-bottom: 1px solid var(--chat-border);
  padding: 12px 20px;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.chat-header__content {
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: 900px;
  margin: 0 auto;
}

.chat-header__back {
  width: 40px;
  height: 40px;
  border: none;
  background: var(--chat-primary);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff !important;
  cursor: pointer;
  transition: all 0.2s ease;
}

.chat-header__back i,
.chat-header__back svg {
  color: #ffffff !important;
  fill: #ffffff !important;
}

.chat-header__back:hover {
  background: var(--chat-primary-dark);
  transform: scale(1.05);
}

.chat-header__avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
}

.chat-header__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.chat-header__avatar-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(
    135deg,
    var(--chat-primary) 0%,
    var(--chat-primary-dark) 100%
  );
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 16px;
}

.chat-header__info {
  flex: 1;
  min-width: 0;
}

/* ===== TYPING INDICATOR - Modern Animated ===== */
.chat-header__typing {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--chat-primary);
  font-weight: 600;
  padding: 4px 10px;
  background: var(--chat-primary-light);
  border-radius: var(--chat-radius-full);
}

.typing-dots {
  display: flex;
  gap: 4px;
}

.typing-dots span {
  width: 7px;
  height: 7px;
  background: var(--chat-primary);
  border-radius: 50%;
  animation: typingBounce 1.4s infinite ease-in-out both;
}

.typing-dots span:nth-child(1) {
  animation-delay: -0.32s;
}
.typing-dots span:nth-child(2) {
  animation-delay: -0.16s;
}
.typing-dots span:nth-child(3) {
  animation-delay: 0s;
}

@keyframes typingBounce {
  0%,
  80%,
  100% {
    transform: scale(0.6);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Header Actions Menu (Flutter sync) */
.chat-header__actions {
  flex-shrink: 0;
}

/* Header Menu Button */
.chat-header__menu-btn {
  width: var(--chat-btn-size-md);
  height: var(--chat-btn-size-md);
  border: none;
  background: linear-gradient(135deg, var(--chat-primary) 0%, var(--chat-primary-dark) 100%);
  border-radius: var(--chat-radius-full);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  cursor: pointer;
  transition: all var(--chat-transition);
  box-shadow: var(--chat-shadow-primary);
  font-size: var(--chat-btn-icon-md);
}

.chat-header__menu-btn i,
.chat-header__menu-btn svg {
  color: #ffffff;
  fill: #ffffff;
}

.chat-header__menu-btn:hover {
  background: var(--chat-primary-dark);
}

.chat-header__actions .dropdown-menu {
  min-width: 180px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border: 1px solid var(--chat-border);
}

.chat-header__actions .dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  font-size: 14px;
}

.chat-header__actions .dropdown-item i {
  font-size: 16px;
}

/* Search Button in Header */
.chat-header__search-btn {
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--chat-text-muted);
  cursor: pointer;
  transition: all 0.2s ease;
}

.chat-header__search-btn:hover {
  background: var(--chat-border);
  color: var(--chat-primary);
}

.chat-header__search-btn.active {
  background: var(--chat-primary);
  color: #fff;
}

/* ===== IN-CONVERSATION SEARCH BAR ===== */
.chat-search-bar {
  display: flex;
  align-items: center;
  gap: var(--chat-spacing-sm);
  padding: var(--chat-spacing-md) var(--chat-spacing-lg);
  background: #ffffff;
  border-bottom: 1px solid var(--chat-border);
  box-shadow: var(--chat-shadow-sm);
  min-height: 60px;
}

.chat-search-bar__input-wrapper {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: var(--chat-spacing-sm);
  background: var(--chat-bg-secondary);
  border: 2px solid var(--chat-border);
  border-radius: var(--chat-radius-xl);
  padding: var(--chat-spacing-sm) var(--chat-spacing-lg);
  transition: all var(--chat-transition);
}

/* Embedded search bar inside Master panel */
.chat-search-bar--embedded {
  padding: 10px 12px;
  gap: 8px;
}

.chat-search-bar--embedded .chat-search-bar__input-wrapper {
  padding: 8px 12px;
  border-radius: 20px;
}

.chat-search-bar--embedded .chat-search-bar__input-wrapper input {
  min-width: 0;
}

.chat-search-bar__input-wrapper:focus-within {
  border-color: var(--chat-primary);
  background: #ffffff;
  box-shadow: 0 0 0 4px rgba(var(--chat-primary-rgb), 0.1);
}

.chat-search-bar__input-wrapper i {
  color: var(--chat-text-muted);
  font-size: var(--chat-btn-icon-sm);
}

.chat-search-bar__input-wrapper:focus-within i {
  color: var(--chat-primary);
}

.chat-search-bar__input-wrapper input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 14px;
  color: var(--chat-text);
  outline: none;
}

.chat-search-bar__input-wrapper input::placeholder {
  color: var(--chat-text-muted);
}

.search-clear {
  width: 26px;
  height: 26px;
  border: none;
  background: var(--chat-border);
  border-radius: var(--chat-radius-full);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--chat-text-muted);
  font-size: 12px;
  transition: all var(--chat-transition);
}

.search-clear:hover {
  background: var(--chat-error);
  color: #fff;
  transform: scale(1.1);
}

.chat-search-bar__nav {
  display: flex;
  align-items: center;
  gap: var(--chat-spacing-sm);
}

.search-count {
  font-size: 13px;
  font-weight: 500;
  color: var(--chat-text-muted);
  min-width: 60px;
  text-align: center;
  font-variant-numeric: tabular-nums;
}

/* Search Navigation Buttons */
.search-nav-btn {
  width: var(--chat-btn-size-sm);
  height: var(--chat-btn-size-sm);
  border: none;
  background: var(--chat-action-secondary-bg);
  border-radius: var(--chat-radius-full);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--chat-text-secondary);
  font-size: var(--chat-btn-icon-sm);
  transition: all var(--chat-transition);
}

.search-nav-btn:hover:not(:disabled) {
  background: var(--chat-primary);
  color: #fff;
  transform: scale(1.05);
}

.search-nav-btn:active:not(:disabled) {
  transform: scale(0.95);
}

.search-nav-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
  background: var(--chat-border-light);
}

/* Search Close Button */
.search-close-btn {
  width: var(--chat-btn-size-sm);
  height: var(--chat-btn-size-sm);
  border: none;
  background: var(--chat-action-secondary-bg);
  border-radius: var(--chat-radius-full);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--chat-text-muted);
  font-size: var(--chat-btn-icon-sm);
  transition: all var(--chat-transition);
}

.search-close-btn:hover {
  background: var(--chat-error);
  color: #fff;
  transform: scale(1.05);
}

.search-close-btn:active {
  transform: scale(0.95);
}

/* Search slide transition */
.search-slide-enter-active,
.search-slide-leave-active {
  transition: all 0.3s ease;
}

.search-slide-enter-from,
.search-slide-leave-to {
  opacity: 0;
  transform: translateY(-100%);
}

.chat-header__title {
  font-size: 16px;
  font-weight: 600;
  color: var(--chat-text);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chat-header__status {
  font-size: 12px;
  color: var(--chat-text-muted);
  display: flex;
  align-items: center;
  gap: 6px;
}

.chat-header__status.status-online {
  color: var(--chat-primary);
}

.chat-header__status.status-online::before {
  content: '';
  width: 8px;
  height: 8px;
  background: var(--chat-primary);
  border-radius: 50%;
  animation: onlinePulse 2s ease-in-out infinite;
}

@keyframes onlinePulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(1.1); }
}

.chat-header__status.status-warning {
  color: #f59e0b;
}
.chat-header__status.status-error {
  color: #ef4444;
}

/* ===== CHAT CONTENT ===== */
.chat-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

/* Loading State */
.chat-loading {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  color: var(--chat-text-muted);
}

.chat-loading__spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e5e7eb;
  border-top-color: var(--chat-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Error State */
.chat-error {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px;
  text-align: center;
}

.chat-error i {
  font-size: 48px;
  color: #ef4444;
}

.chat-error p {
  color: var(--chat-text-muted);
  margin: 0;
}

.chat-error__btn {
  padding: 10px 24px;
  background: var(--chat-primary);
  color: white;
  border: none;
  border-radius: 24px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.chat-error__btn:hover {
  background: var(--chat-primary-dark);
  transform: translateY(-1px);
}

/* ===== DAY SEPARATOR (Flutter sync) ===== */
.day-separator {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 0;
  margin: 8px 0;
}

.day-separator__text {
  background: rgba(0, 0, 0, 0.08);
  color: var(--chat-text-muted);
  font-size: 12px;
  font-weight: 500;
  padding: 6px 14px;
  border-radius: 12px;
  text-transform: capitalize;
}

/* ===== SYSTEM MESSAGE (Flutter sync: _buildSystemMessage) ===== */
.system-message {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px 16px;
  margin: 4px 0;
  animation: system-message-fade-in 0.3s ease-out;
}

@keyframes system-message-fade-in {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.system-message__pill {
  background: rgba(0, 0, 0, 0.06);
  border-radius: 8px;
  padding: 6px 12px;
  max-width: 85%;
  text-align: center;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.system-message__text {
  color: var(--chat-text-muted, #6b7280);
  font-size: 12px;
  font-weight: 500;
  line-height: 1.4;
  letter-spacing: 0.01em;
}

/* System message icon variants */
.system-message--join .system-message__pill::before,
.system-message--leave .system-message__pill::before {
  margin-right: 6px;
  font-family: "bootstrap-icons";
}

/* Dark mode system message */
.chat-dark-mode .system-message__pill {
  background: rgba(255, 255, 255, 0.08);
}

.chat-dark-mode .system-message__text {
  color: rgba(255, 255, 255, 0.6);
}

/* ===== MESSAGE GROUPING (Flutter sync) ===== */
.msg-row--grouped {
  margin-top: 2px;
}

.msg-row--grouped .msg-bubble {
  border-radius: 16px;
}

.msg-row--own.msg-row--grouped .msg-bubble {
  border-top-left-radius: 16px;
  border-bottom-left-radius: 16px;
}

.msg-row:not(.msg-row--own).msg-row--grouped .msg-bubble {
  border-top-right-radius: 16px;
  border-bottom-right-radius: 16px;
}

/* Show tail only on last message in group */
.msg-row:not(.msg-row--grouped) .msg-bubble--own {
  border-bottom-right-radius: 4px;
}

.msg-row:not(.msg-row--grouped) .msg-bubble:not(.msg-bubble--own) {
  border-bottom-left-radius: 4px;
}

/* ===== BLOCKER & OWNER BANNERS (Flutter sync) ===== */
.chat-blocker-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  border-top: 2px solid #ef4444;
  color: #991b1b;
  font-size: 13px;
}

.chat-blocker-banner__content {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.chat-blocker-banner i {
  font-size: 20px;
  color: #ef4444;
}

.chat-blocker-banner span {
  flex: 1;
  font-weight: 500;
}

.chat-blocker-banner__unblock {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.chat-blocker-banner__unblock:hover {
  background: #dc2626;
  transform: scale(1.02);
}

.chat-blocker-banner__unblock i {
  color: white;
  font-size: 14px;
}

.chat-owner-locked-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  border-top: 1px solid #3b82f6;
  color: #1e40af;
  font-size: 13px;
}

.chat-owner-locked-banner i {
  font-size: 16px;
  color: #3b82f6;
}

/* ===== CHAT BODY ===== */
.chat-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  width: 100%;
}

/* ===== MESSAGES AREA - Only this scrolls ===== */
.chat-messages {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px;
  scroll-behavior: auto; /* Changed from smooth for better performance */
  min-height: 0;
  /* Light mode - green pattern (instant load via data URI) */
  background-color: #e8f5e9;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80'%3E%3Cg fill='%2386c67c' fill-opacity='0.2'%3E%3Cpath d='M10 10L14 6l4 4-4 4z'/%3E%3Ccircle cx='50' cy='15' r='3'/%3E%3Cpath d='M70 8h6v4h-6z'/%3E%3Cpath d='M15 35l3-5h6l3 5-3 5h-6z'/%3E%3Crect x='45' y='32' width='8' height='10' rx='1'/%3E%3Ccircle cx='72' cy='38' r='4'/%3E%3Cpath d='M8 58l6 3-6 3z'/%3E%3Cpath d='M35 55l5 8h-10z'/%3E%3Crect x='60' y='55' width='12' height='6' rx='2'/%3E%3Ccircle cx='20' cy='72' r='3'/%3E%3Cpath d='M48 70l4-4 4 4-4 4z'/%3E%3Cpath d='M68 68h8v8h-8z' fill-opacity='0.15'/%3E%3C/g%3E%3C/svg%3E");
  background-size: 80px 80px;
  /* GPU acceleration for smooth scrolling */
  will-change: scroll-position;
  -webkit-overflow-scrolling: touch;
}

/* Load More Indicator (infinite scroll) */
.load-more-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px;
  color: var(--chat-text-muted);
  font-size: 13px;
}

.load-more-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid var(--chat-border);
  border-top-color: var(--chat-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

/* End of Messages Indicator */
.end-of-messages {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px 16px;
  color: var(--chat-text-muted);
  font-size: 13px;
}

.end-of-messages i {
  font-size: 24px;
  opacity: 0.5;
}

.chat-messages::-webkit-scrollbar {
  width: 10px;
}

.chat-messages::-webkit-scrollbar-track {
  background: rgba(0, 196, 68, 0.15);
  border-radius: 5px;
  margin: 4px 0;
}

.chat-messages::-webkit-scrollbar-thumb {
  background: #00c444;
  border-radius: 5px;
  border: 2px solid transparent;
  background-clip: padding-box;
}

.chat-messages::-webkit-scrollbar-thumb:hover {
  background: #00a83a;
  background-clip: padding-box;
}

.chat-messages::-webkit-scrollbar-thumb:active {
  background: #008c30;
  background-clip: padding-box;
}

/* Firefox scrollbar */
.chat-messages {
  scrollbar-width: auto;
  scrollbar-color: #00c444 rgba(0, 196, 68, 0.15);
}

/* =============================================
   PROFESSIONAL CHAT UI - WhatsApp/Messenger Style
   ============================================= */

/* Message Row */
.msg-row {
  display: flex;
  justify-content: flex-start;
  padding: 4px 16px;
  animation: msgFadeIn 0.2s ease-out;
}

.msg-row--own {
  justify-content: flex-end;
}

/* Unread message row - subtle highlight */
.msg-row--unread {
  position: relative;
}

.msg-row--unread::before {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background: rgba(0, 196, 68, 0.05);
  pointer-events: none;
  border-radius: 8px;
}

/* Highlighted message (scroll-to-reply) */
.msg-row--highlighted {
  animation: highlightPulse 2s ease-out;
}

.msg-row--highlighted .msg-bubble {
  box-shadow: 0 0 0 3px var(--chat-primary), 0 4px 12px rgba(0, 196, 68, 0.3);
}

@keyframes highlightPulse {
  0% {
    background: rgba(0, 196, 68, 0.3);
  }
  100% {
    background: transparent;
  }
}

/* Selected message (selection mode) */
.msg-row--selected {
  background: rgba(0, 196, 68, 0.15);
  border-radius: 8px;
}

.msg-row--selected .msg-bubble {
  border: 2px solid var(--chat-primary);
}

/* Search match highlight */
.msg-row--search-match .msg-bubble {
  background: rgba(255, 193, 7, 0.2) !important;
  border: 2px solid #ffc107;
}

/* Inline keyword highlight inside message text (WhatsApp-style) */
.msg-text .search-highlight {
  background-color: #ffeb3b;
  color: #1a1a2e;
  border-radius: 3px;
  padding: 1px 2px;
  font-weight: 600;
}

.msg-bubble--own .msg-text .search-highlight {
  background-color: #fff176;
  color: #1a1a2e;
}

/* Unread Messages Divider */
.unread-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 16px;
  margin: 8px 0;
}

.unread-divider__text {
  background: linear-gradient(135deg, #00c444 0%, #00a838 100%);
  color: white;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 16px;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 196, 68, 0.3);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

@keyframes msgFadeIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Bubble Wrapper */
.msg-bubble-wrapper {
  max-width: 75%;
  min-width: 120px;
  touch-action: pan-y;
  user-select: none;
}

@media (min-width: 768px) {
  .msg-bubble-wrapper {
    max-width: 500px;
  }
}

/* Swipe Reply Indicator */
.swipe-reply-indicator {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--chat-primary);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  opacity: 0;
  pointer-events: none;
  z-index: 1;
}

.swipe-reply-indicator--left {
  left: 8px;
}

.swipe-reply-indicator--right {
  right: 8px;
}

.msg-row {
  position: relative;
}

/* Message Bubble */
.msg-bubble {
  position: relative;
  background: #ffffff;
  border-radius: 16px;
  padding: 10px 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
  transition: box-shadow 0.2s ease, transform 0.15s ease;
}

.msg-bubble:hover {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12), 0 1px 3px rgba(0, 0, 0, 0.08);
}

.msg-bubble--own {
  background: linear-gradient(135deg, #d9fdd3 0%, #c8f7c5 100%);
  border-bottom-right-radius: 4px;
}

.msg-bubble:not(.msg-bubble--own) {
  border-bottom-left-radius: 4px;
}

.msg-bubble--pending {
  opacity: 0.75;
}

.msg-bubble--failed {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border: 1px solid #fca5a5;
}

.msg-bubble--has-media {
  padding: 5px;
  overflow: hidden;
}

.msg-bubble--has-media .msg-text {
  padding: 8px 8px 4px;
}

/* Sender Name (for group chats) */
.msg-sender-name {
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 4px;
  padding-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}

/* ===== MESSAGE HOVER ACTIONS - Floating Toolbar ===== */
.msg-actions {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  gap: 4px;
  background: rgba(255, 255, 255, 0.98);
  border-radius: var(--chat-radius-md);
  padding: 4px;
  box-shadow: var(--chat-shadow-md);
  border: 1px solid var(--chat-border-light);
  opacity: 0;
  visibility: hidden;
  transform: translateY(-4px);
  transition: all var(--chat-transition-fast);
  z-index: 10;
}

.msg-bubble:hover .msg-actions {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

/* Touch devices - show on tap */
@media (hover: none) {
  .msg-actions {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
    position: relative;
    top: auto;
    right: auto;
    margin-top: 8px;
    width: fit-content;
  }
}

.msg-actions button {
  width: var(--chat-btn-size-sm);
  height: var(--chat-btn-size-sm);
  border: none;
  background: transparent;
  color: var(--chat-text-muted);
  border-radius: var(--chat-radius-sm);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--chat-btn-icon-sm);
  transition: all var(--chat-transition-fast);
}

.msg-actions button:hover {
  background: var(--chat-action-secondary-bg);
  color: var(--chat-text);
  transform: scale(1.1);
}

.msg-actions button:active {
  transform: scale(0.9);
}

/* Specific action button colors */
.msg-actions__emoji:hover {
  background: #fef3c7;
  color: #d97706;
}

.msg-actions__reply:hover {
  background: var(--chat-primary-light);
  color: var(--chat-primary-dark);
}

.msg-actions__info:hover {
  background: #dbeafe;
  color: #2563eb;
}

.msg-actions__delete:hover {
  background: #fee2e2;
  color: #dc2626;
}

.msg-actions__edit:hover {
  background: #e0e7ff;
  color: #4f46e5;
}

/* ===== MESSAGE OPTIONS MENU - WhatsApp Style ===== */
.message-options-menu {
  position: fixed;
  z-index: 1300;
  min-width: 200px;
  max-width: 280px;
}

.menu-fade-enter-active,
.menu-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.menu-fade-enter-from,
.menu-fade-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

.message-options-menu__content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 8px 0;
  overflow: hidden;
}

.message-options-menu__item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 18px;
  border: none;
  background: transparent;
  color: #1f2937;
  font-size: 14px;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s ease;
}

.message-options-menu__item:hover,
.message-options-menu__item:focus {
  background: #f3f4f6;
}

.message-options-menu__item i {
  font-size: 18px;
  color: #6b7280;
  width: 22px;
  text-align: center;
  flex-shrink: 0;
}

.message-options-menu__item--danger {
  color: #dc2626;
}

.message-options-menu__item--danger:hover,
.message-options-menu__item--danger:focus {
  background: #fef2f2;
}

.message-options-menu__item--danger i {
  color: #dc2626;
}

.message-options-menu__divider {
  height: 1px;
  background: #e5e7eb;
  margin: 6px 0;
}

/* ===== REPLY PREVIEW - Inline Quote Style ===== */
.msg-reply-preview {
  display: flex;
  gap: var(--chat-spacing-sm);
  background: rgba(0, 0, 0, 0.05);
  border-radius: var(--chat-radius-sm);
  padding: var(--chat-spacing-sm) var(--chat-spacing-md);
  margin-bottom: var(--chat-spacing-sm);
  cursor: pointer;
  transition: all var(--chat-transition);
  border-left: 3px solid var(--chat-primary);
}

.msg-reply-preview:hover {
  background: rgba(0, 0, 0, 0.08);
  transform: translateX(2px);
}

.msg-bubble--own .msg-reply-preview {
  background: rgba(0, 100, 0, 0.1);
  border-left-color: #ffffff;
}

.msg-reply-preview__bar {
  width: 3px;
  background: var(--chat-primary);
  border-radius: 2px;
  flex-shrink: 0;
  display: none; /* Using border-left instead */
}

.msg-reply-preview__content {
  flex: 1;
  min-width: 0;
}

.msg-reply-preview__sender {
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: var(--chat-primary);
  margin-bottom: 2px;
}

.msg-bubble--own .msg-reply-preview__sender {
  color: rgba(255, 255, 255, 0.9);
}

.msg-reply-preview__text {
  display: block;
  font-size: 13px;
  color: var(--chat-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.msg-bubble--own .msg-reply-preview__text {
  color: rgba(255, 255, 255, 0.75);
}

/* Message Text - Clear and Readable */
.msg-text {
  font-size: 15px;
  line-height: 1.5;
  color: #1f2937;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
}

.msg-bubble--own .msg-text {
  color: #1a1a1a;
}

/* Message Links - Clickable URLs */
.msg-text .msg-link {
  color: #2563eb;
  text-decoration: underline;
  word-break: break-all;
  cursor: pointer;
}

.msg-text .msg-link:hover {
  color: #1d4ed8;
  text-decoration: underline;
}

.msg-bubble--own .msg-text .msg-link {
  color: #1e40af;
}

.msg-bubble--own .msg-text .msg-link:hover {
  color: #1e3a8a;
}

/* Message Meta (Time & Status) */
.msg-meta {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  margin-top: 4px;
  padding-right: 2px;
}

.msg-meta__edited {
  font-size: 11px;
  color: #9ca3af;
  font-style: italic;
}

.msg-meta__time {
  font-size: 11px;
  color: #9ca3af;
}

.msg-bubble--own .msg-meta__time {
  color: #6b8e6b;
}

.msg-meta__status {
  font-size: 14px;
  display: flex;
  align-items: center;
}

.msg-meta__status--pending {
  width: 12px;
  height: 12px;
  border: 2px solid #9ca3af;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.msg-meta__status--sent {
  color: #9ca3af;
}

.msg-meta__status--seen {
  color: #00c444;
}

.msg-meta__status--seen i {
  color: #00c444 !important;
}

.msg-meta__status--failed {
  color: #ef4444;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Upload Progress */
.msg-progress-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}

.msg-progress {
  flex: 1;
  height: 3px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 2px;
  overflow: hidden;
}

.msg-progress__bar {
  height: 100%;
  background: #25d366;
  border-radius: 2px;
  transition: width 0.3s ease;
}

.msg-progress__cancel {
  width: 20px;
  height: 20px;
  border: none;
  background: rgba(239, 68, 68, 0.9);
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
  flex-shrink: 0;
}

.msg-progress__cancel:hover {
  background: #dc2626;
  transform: scale(1.1);
}

/* ===== REACTIONS - Modern Pill Style ===== */
.msg-reactions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.msg-reactions__item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: rgba(0, 0, 0, 0.06);
  border: 1.5px solid transparent;
  border-radius: var(--chat-radius-full);
  font-size: 14px;
  cursor: pointer;
  transition: all var(--chat-transition-fast);
  user-select: none;
}

.msg-reactions__item:hover {
  background: rgba(0, 0, 0, 0.1);
  transform: scale(1.05);
}

.msg-reactions__item:active {
  transform: scale(0.95);
}

.msg-reactions__item--active {
  background: var(--chat-primary-light);
  border-color: var(--chat-primary);
  box-shadow: 0 2px 8px rgba(var(--chat-primary-rgb), 0.2);
}

.msg-reactions__count {
  font-size: 12px;
  font-weight: 600;
  color: var(--chat-text-secondary);
}

.msg-reactions__item--active .msg-reactions__count {
  color: var(--chat-primary-dark);
}

/* Media Container */
.msg-media {
  border-radius: 12px;
  overflow: hidden;
}

/* ===== ATTACHMENT STYLES ===== */
.attachment-content {
  display: block;
}

/* Image - Large Size */
.image-message {
  display: inline-block !important;
  max-width: 220px !important;
  border-radius: 12px !important;
  overflow: hidden !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12) !important;
}

.chat-image {
  display: block !important;
  width: 220px !important;
  height: 220px !important;
  max-width: 220px !important;
  max-height: 220px !important;
  object-fit: cover !important;
  background: #f3f4f6 !important;
  cursor: pointer !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease !important;
}

.chat-image:hover {
  transform: scale(1.02);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
}

/* Voice Message - Modern WhatsApp Style */
.voice-message {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 12px;
  min-width: 200px;
  max-width: 320px;
}

.msg-bubble--own .voice-message {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
}

.voice-icon {
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #25d366;
  border-radius: 50%;
  color: white;
  font-size: 18px;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(37, 211, 102, 0.3);
}

.voice-content {
  flex: 1;
  min-width: 0;
}

.voice-audio {
  width: 100%;
  height: 36px;
  border-radius: 18px;
}

.voice-duration {
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
  flex-shrink: 0;
  margin-left: 4px;
}

.msg-bubble--own .voice-duration {
  color: #166534;
}

/* ===== FILE CARD PREMIUM - Modern Design ===== */
/* Using :deep() for inline component styles */
.attachment-content :deep(.file-card-premium),
.file-card-premium {
  position: relative;
  min-width: 300px;
  max-width: 360px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #f1f5f9 100%);
  border-radius: 20px;
  overflow: hidden;
  border: 2px solid #e2e8f0;
  box-shadow: 
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06),
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.file-card-premium:hover {
  transform: translateY(-4px) scale(1.01);
  box-shadow: 
    0 25px 50px -12px rgba(0, 0, 0, 0.25),
    0 0 0 1px rgba(0, 196, 68, 0.1);
  border-color: #00c444;
}

.msg-bubble--own .file-card-premium {
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 50%, #a7f3d0 100%);
  border-color: #6ee7b7;
  box-shadow: 
    0 4px 6px -1px rgba(0, 196, 68, 0.15),
    0 2px 4px -1px rgba(0, 196, 68, 0.1),
    0 20px 25px -5px rgba(0, 196, 68, 0.15),
    0 10px 10px -5px rgba(0, 196, 68, 0.08);
}

.msg-bubble--own .file-card-premium:hover {
  border-color: #00c444;
  box-shadow: 
    0 25px 50px -12px rgba(0, 196, 68, 0.3),
    0 0 0 2px rgba(0, 196, 68, 0.2);
}

/* Background decorations */
.file-card-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  overflow: hidden;
}

.file-card-pattern {
  position: absolute;
  top: -50%;
  right: -30%;
  width: 200px;
  height: 200px;
  background: radial-gradient(circle, rgba(0, 196, 68, 0.06) 0%, transparent 70%);
  border-radius: 50%;
}

.file-card-glow {
  position: absolute;
  bottom: -30%;
  left: -20%;
  width: 150px;
  height: 150px;
  background: radial-gradient(circle, rgba(59, 130, 246, 0.05) 0%, transparent 70%);
  border-radius: 50%;
}

/* Main content area */
.file-card-content {
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 20px;
  z-index: 1;
}

/* 3D File Icon */
.file-icon-3d {
  position: relative;
  width: 72px;
  height: 72px;
  flex-shrink: 0;
}

.file-icon-face {
  position: absolute;
  border-radius: 16px;
}

.file-icon-front {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(145deg, #6b7280 0%, #4b5563 100%);
  color: white;
  font-size: 32px;
  border-radius: 16px;
  box-shadow: 
    0 10px 30px rgba(0, 0, 0, 0.25),
    0 6px 12px rgba(0, 0, 0, 0.15),
    inset 0 2px 0 rgba(255, 255, 255, 0.25),
    inset 0 -3px 0 rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
}

.file-card-premium:hover .file-icon-front {
  transform: scale(1.05);
  box-shadow: 
    0 14px 40px rgba(0, 0, 0, 0.3),
    0 8px 16px rgba(0, 0, 0, 0.2),
    inset 0 2px 0 rgba(255, 255, 255, 0.3),
    inset 0 -3px 0 rgba(0, 0, 0, 0.15);
}

.file-icon-front i {
  filter: drop-shadow(0 3px 6px rgba(0, 0, 0, 0.4));
}

.file-icon-fold {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 22px;
  height: 22px;
  background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
  border-radius: 0 16px 0 12px;
  box-shadow: -3px 3px 8px rgba(0, 0, 0, 0.15);
  z-index: 2;
  border: 2px solid white;
}

.file-type-badge {
  position: absolute;
  bottom: -6px;
  right: -6px;
  background: linear-gradient(135deg, #1f2937 0%, #111827 100%);
  color: white;
  font-size: 10px;
  font-weight: 800;
  padding: 5px 10px;
  border-radius: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  border: 2.5px solid white;
  z-index: 3;
}

/* File type specific colors */
.file-icon--pdf .file-icon-front {
  background: linear-gradient(145deg, #ef4444 0%, #dc2626 50%, #b91c1c 100%);
}
.file-icon--pdf .file-type-badge {
  background: linear-gradient(135deg, #dc2626 0%, #991b1b 100%);
}

.file-icon--word .file-icon-front {
  background: linear-gradient(145deg, #3b82f6 0%, #2563eb 50%, #1d4ed8 100%);
}
.file-icon--word .file-type-badge {
  background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
}

.file-icon--excel .file-icon-front {
  background: linear-gradient(145deg, #22c55e 0%, #16a34a 50%, #15803d 100%);
}
.file-icon--excel .file-type-badge {
  background: linear-gradient(135deg, #16a34a 0%, #166534 100%);
}

.file-icon--ppt .file-icon-front {
  background: linear-gradient(145deg, #f97316 0%, #ea580c 50%, #c2410c 100%);
}
.file-icon--ppt .file-type-badge {
  background: linear-gradient(135deg, #ea580c 0%, #9a3412 100%);
}

.file-icon--zip .file-icon-front {
  background: linear-gradient(145deg, #8b5cf6 0%, #7c3aed 50%, #6d28d9 100%);
}
.file-icon--zip .file-type-badge {
  background: linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%);
}

.file-icon--default .file-icon-front {
  background: linear-gradient(145deg, #00c444 0%, #00a038 50%, #008830 100%);
}
.file-icon--default .file-type-badge {
  background: linear-gradient(135deg, #00a038 0%, #006620 100%);
}

/* File info section */
.file-info-section {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.file-name-title {
  margin: 0;
  font-size: 16px;
  font-weight: 800;
  color: #1f2937;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: -0.2px;
}

.msg-bubble--own .file-name-title {
  color: #064e3b;
}

.file-meta-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.file-ext-tag {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%);
  border-radius: 10px;
  font-size: 12px;
  font-weight: 800;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
}

.msg-bubble--own .file-ext-tag {
  background: linear-gradient(135deg, #6ee7b7 0%, #34d399 100%);
  color: #064e3b;
}

.file-size-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.file-size-tag i {
  font-size: 12px;
  color: #94a3b8;
}

.msg-bubble--own .file-size-tag {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #047857;
}

.msg-bubble--own .file-size-tag i {
  color: #10b981;
}

/* Action buttons row */
.file-actions-row {
  position: relative;
  display: flex;
  gap: 12px;
  padding: 16px 20px;
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  border-top: 2px solid #e2e8f0;
  z-index: 1;
}

.msg-bubble--own .file-actions-row {
  background: linear-gradient(180deg, #d1fae5 0%, #a7f3d0 100%);
  border-top-color: #6ee7b7;
}

.file-action-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 20px;
  border-radius: 14px;
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  border: none;
}

.file-action-download {
  background: linear-gradient(135deg, #00c444 0%, #00a038 50%, #008830 100%);
  color: white;
  box-shadow: 
    0 6px 20px rgba(0, 196, 68, 0.4),
    0 3px 8px rgba(0, 196, 68, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

.file-action-download:hover {
  background: linear-gradient(135deg, #00d44a 0%, #00c444 50%, #00a038 100%);
  transform: translateY(-3px) scale(1.02);
  box-shadow: 
    0 10px 30px rgba(0, 196, 68, 0.5),
    0 5px 12px rgba(0, 196, 68, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
  color: white;
}

.file-action-download:active {
  transform: translateY(-1px) scale(1.01);
}

.file-action-download i {
  font-size: 18px;
}

.file-action-open {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  color: #475569;
  border: 2px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.file-action-open:hover {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  color: #1f2937;
  transform: translateY(-3px) scale(1.02);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
  border-color: #cbd5e1;
}

.file-action-open i {
  font-size: 18px;
}

.msg-bubble--own .file-action-open {
  background: linear-gradient(135deg, #ffffff 0%, #ecfdf5 100%);
  border-color: #6ee7b7;
  color: #047857;
}

.msg-bubble--own .file-action-open:hover {
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  border-color: #34d399;
}

/* Upload progress */
.file-upload-status {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.file-upload-bar {
  position: relative;
  height: 8px;
  background: rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  overflow: hidden;
}

.file-upload-fill {
  height: 100%;
  background: linear-gradient(90deg, #00c444 0%, #00d44a 50%, #00c444 100%);
  background-size: 200% 100%;
  border-radius: 10px;
  transition: width 0.3s ease;
  animation: uploadShimmer 1.5s ease infinite;
}

@keyframes uploadShimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.file-upload-shine {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  animation: uploadShine 2s ease infinite;
}

@keyframes uploadShine {
  0% { left: -100%; }
  100% { left: 100%; }
}

.file-upload-text {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #00c444;
}

.file-upload-text i {
  animation: uploadBounce 1s ease infinite;
}

@keyframes uploadBounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
}

/* Legacy file-message for fallback */
.file-message {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: #f8fafc;
  border-radius: 12px;
  min-width: 200px;
}

.msg-bubble--own .file-message {
  background: rgba(0, 80, 40, 0.08);
}

.file-icon {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #dc2626;
  border-radius: 10px;
  color: white;
  font-size: 20px;
  flex-shrink: 0;
}

/* File type specific colors */
.file-icon.file-icon--pdf {
  background: #dc2626;
}
.file-icon.file-icon--word {
  background: #2563eb;
}
.file-icon.file-icon--excel {
  background: #16a34a;
}
.file-icon.file-icon--ppt {
  background: #ea580c;
}
.file-icon.file-icon--zip {
  background: #7c3aed;
}
.file-icon.file-icon--default {
  background: #6b7280;
}

.file-info {
  flex: 1;
  min-width: 0;
}

.file-name {
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 2px;
  display: block;
}

.file-download {
  font-size: 12px;
  color: #25d366;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.file-download:hover {
  text-decoration: underline;
}

/* Failed Message Actions */
.message-failed-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  justify-content: flex-end;
}

/* (Old duplicate styles removed - using new msg- prefixed classes) */

.failed-action {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border: none;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.failed-action--retry {
  background: var(--chat-primary);
  color: white;
}

.failed-action--retry:hover {
  background: var(--chat-primary-dark);
}

.failed-action--delete {
  background: #fee2e2;
  color: #ef4444;
}

.failed-action--delete:hover {
  background: #fecaca;
}

/* ========== Additional Attachment Styles ========== */

/* Failed state */
.attachment-failed {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border-radius: 12px;
  color: #dc2626;
}

.attachment-failed .failed-icon {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fecaca;
  border-radius: 50%;
  font-size: 16px;
}

/* Voice Waveform Animation */
.voice-waves {
  display: flex;
  align-items: center;
  gap: 3px;
  height: 24px;
}

.voice-waves span {
  display: block;
  width: 3px;
  background: #25d366;
  border-radius: 3px;
  animation: wave 1s ease-in-out infinite;
}

.voice-waves span:nth-child(1) {
  height: 8px;
  animation-delay: 0s;
}
.voice-waves span:nth-child(2) {
  height: 16px;
  animation-delay: 0.1s;
}
.voice-waves span:nth-child(3) {
  height: 24px;
  animation-delay: 0.2s;
}
.voice-waves span:nth-child(4) {
  height: 16px;
  animation-delay: 0.3s;
}
.voice-waves span:nth-child(5) {
  height: 8px;
  animation-delay: 0.4s;
}

@keyframes wave {
  0%,
  100% {
    transform: scaleY(1);
  }
  50% {
    transform: scaleY(0.5);
  }
}

.voice-waves.has-waveform {
  animation: none;
}

.voice-waves.has-waveform span {
  animation: none;
}

.voice-label {
  font-size: 12px;
  color: #6b7280;
}

/* Video Message - Simple with native controls */
.video-message.is-uploading {
  opacity: 0.85;
}

/* Attachment Empty */
.attachment-empty {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #f8fafc;
  border-radius: 12px;
  color: #6b7280;
}

/* Image Upload - Elegant Indicator */
.image-message {
  position: relative;
  display: inline-block;
}

.chat-image.is-uploading {
  opacity: 0.7;
  transition: opacity 0.3s ease;
}

.chat-image:not(.is-uploading) {
  opacity: 1;
  transition: opacity 0.3s ease;
}

.image-upload-indicator {
  position: absolute;
  bottom: 10px;
  right: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
  background: #00c444;
  padding: 6px 10px;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 196, 68, 0.4);
  animation: fade-in 0.2s ease;
}

@keyframes fade-in {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.image-upload-indicator i {
  font-size: 12px;
  color: white;
  animation: bounce-up 0.8s ease-in-out infinite;
}

@keyframes bounce-up {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-2px);
  }
}

.image-upload-indicator span {
  font-size: 11px;
  font-weight: 600;
  color: white;
  letter-spacing: 0.3px;
}

.loading-dots span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes dot-pulse {
  0%,
  80%,
  100% {
    opacity: 0.3;
    transform: scale(0.8);
  }
  40% {
    opacity: 1;
    transform: scale(1);
  }
}

/* Voice Uploading State - Modern */
.voice-uploading {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 160px;
}

.voice-uploading .voice-waves {
  animation: wave-pulse 1.2s ease-in-out infinite;
}

@keyframes wave-pulse {
  0%,
  100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}

.uploading-text {
  font-size: 11px;
  color: #64748b;
  font-weight: 500;
}

.upload-progress-mini {
  display: flex;
  align-items: center;
  gap: 10px;
}

.upload-progress-mini .progress-track {
  flex: 1;
  height: 3px;
  background: rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  overflow: hidden;
}

.upload-progress-mini .progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #00c444, #10b981);
  border-radius: 4px;
  transition: width 0.2s ease-out;
}

.upload-progress-mini span {
  font-size: 11px;
  font-weight: 600;
  color: #00c444;
  min-width: 32px;
}

.voice-placeholder {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Sender's message upload styles */
.message-bubble.me .upload-progress-mini .progress-track {
  background: rgba(255, 255, 255, 0.25);
}

.message-bubble.me .upload-progress-mini .progress-fill {
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.9),
    rgba(255, 255, 255, 0.7)
  );
}

.message-bubble.me .upload-progress-mini span {
  color: rgba(255, 255, 255, 0.95);
}

.uploading-text {
  font-size: 12px;
  color: #6b7280;
}

.message-bubble.me .uploading-text {
  color: rgba(255, 255, 255, 0.8);
}

/* Empty attachment */
.attachment-empty {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #9ca3af;
  font-size: 14px;
}

/* ===== REPLY BANNER - Premium Modern Design ===== */
.reply-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  margin: 0 12px 8px;
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.08) 0%, rgba(16, 185, 129, 0.12) 100%);
  border-radius: 16px;
  border: 1px solid rgba(34, 197, 94, 0.2);
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);
  box-shadow: 
    0 4px 15px rgba(34, 197, 94, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
}

.reply-banner::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: linear-gradient(180deg, #22c55e 0%, #10b981 100%);
  border-radius: 4px 0 0 4px;
}

/* Reply slide transition */
.reply-slide-enter-active {
  animation: replySlideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.reply-slide-leave-active {
  animation: replySlideOut 0.2s ease-out forwards;
}

@keyframes replySlideIn {
  0% {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes replySlideOut {
  0% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  100% {
    opacity: 0;
    transform: translateY(10px) scale(0.95);
  }
}

.reply-banner__icon {
  width: 36px;
  height: 36px;
  min-width: 36px;
  background: linear-gradient(135deg, #22c55e 0%, #10b981 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(34, 197, 94, 0.3);
}

.reply-banner__icon i {
  color: #ffffff;
  font-size: 16px;
  transform: scaleX(-1);
}

.reply-banner__content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.reply-banner__header {
  display: flex;
  align-items: center;
  gap: 6px;
}

.reply-banner__quote-icon {
  font-size: 10px;
  color: #22c55e;
  opacity: 0.8;
}

.reply-banner__label {
  font-size: 11px;
  font-weight: 600;
  color: #22c55e;
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

.reply-banner__text {
  font-size: 13px;
  color: var(--chat-text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  line-height: 1.4;
}

.reply-banner__close {
  width: 28px;
  height: 28px;
  min-width: 28px;
  min-height: 28px;
  border: none;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: #ffffff;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
  font-size: 12px;
  box-shadow: 0 3px 10px rgba(239, 68, 68, 0.3);
  flex-shrink: 0;
}

.reply-banner__close i,
.reply-banner__close svg {
  color: #ffffff;
  fill: #ffffff;
}

.reply-banner__close:hover {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  transform: scale(1.1) rotate(90deg);
  box-shadow: 0 5px 15px rgba(239, 68, 68, 0.4);
}

.reply-banner__close:active {
  transform: scale(0.95) rotate(90deg);
}

/* ===== CHAT INPUT AREA - Premium Glass Design ===== */
.chat-input-area {
  flex-shrink: 0;
  padding: 16px 20px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.98) 100%);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-top: 1px solid rgba(var(--chat-primary-rgb), 0.1);
  position: relative;
  z-index: 5;
  box-shadow: 0 -4px 30px rgba(0, 0, 0, 0.05);
}

/* Subtle gradient line at top */
.chat-input-area::before {
  content: '';
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 60%;
  height: 2px;
  background: linear-gradient(90deg, transparent, var(--chat-primary), transparent);
  opacity: 0.5;
  border-radius: 2px;
}

.chat-blocked-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 18px 24px;
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: #991b1b;
  border-radius: 16px;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 4px 15px rgba(239, 68, 68, 0.15);
}

.chat-blocked-banner i {
  font-size: 20px;
  color: #ef4444;
}

.chat-blocked-banner--blocker {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  color: #92400e;
  box-shadow: 0 4px 15px rgba(245, 158, 11, 0.15);
}

.chat-blocked-banner--blocker i {
  color: #f59e0b;
}

/* ===== BLOCKED INPUT STATE ===== */
.chat-input--blocked {
  position: relative;
  opacity: 0.85;
  pointer-events: none;
}

.chat-input__blocked-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 20px;
  background: rgba(239, 68, 68, 0.95);
  color: #ffffff;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
  z-index: 10;
  white-space: nowrap;
  box-shadow: 0 4px 15px rgba(239, 68, 68, 0.3);
}

.chat-input__blocked-overlay i {
  font-size: 16px;
}

.chat-input--blocker .chat-input__blocked-overlay {
  background: rgba(245, 158, 11, 0.95);
  box-shadow: 0 4px 15px rgba(245, 158, 11, 0.3);
}

.chat-input__field--blocked {
  background: #f3f4f6 !important;
  border-color: #d1d5db !important;
  color: #9ca3af !important;
  cursor: not-allowed !important;
}

.chat-input__field--blocked::placeholder {
  color: transparent !important;
}

.chat-input__send--blocked {
  background: #d1d5db !important;
  cursor: not-allowed !important;
  box-shadow: none !important;
}

.chat-input__send--blocked i {
  color: #9ca3af !important;
}

.chat-input {
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: 900px;
  margin: 0 auto;
}

/* ===== ATTACHMENT BUTTON - Stunning 3D Effect ===== */
.chat-input__action {
  width: 50px;
  height: 50px;
  min-width: 50px;
  border: none;
  background: linear-gradient(145deg, #00d449 0%, #00a838 50%, #008830 100%);
  border-radius: 16px;
  color: #ffffff;
  font-size: 22px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  flex-shrink: 0;
  box-shadow: 
    0 4px 15px rgba(0, 196, 68, 0.4),
    0 2px 4px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  position: relative;
  overflow: hidden;
}

/* Shine effect */
.chat-input__action::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  transition: left 0.5s ease;
}

.chat-input__action:hover::before {
  left: 100%;
}

/* Glow ring effect */
.chat-input__action::after {
  content: '';
  position: absolute;
  inset: -3px;
  border-radius: 18px;
  background: linear-gradient(145deg, #00ff5a, #00c444, #00a838);
  z-index: -1;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.chat-input__action:hover::after {
  opacity: 0.5;
  animation: glow-pulse 2s ease-in-out infinite;
}

@keyframes glow-pulse {
  0%, 100% { opacity: 0.3; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(1.02); }
}

.chat-input__action i,
.chat-input__action svg {
  color: #ffffff;
  fill: #ffffff;
  font-size: inherit;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
  transition: transform 0.3s ease;
}

.chat-input__action:hover {
  transform: translateY(-3px) scale(1.05);
  box-shadow: 
    0 8px 25px rgba(0, 196, 68, 0.5),
    0 4px 8px rgba(0, 0, 0, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
}

.chat-input__action:hover i {
  transform: rotate(15deg) scale(1.1);
}

.chat-input__action:active {
  transform: translateY(0) scale(0.95);
  box-shadow: 
    0 2px 10px rgba(0, 196, 68, 0.4),
    inset 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* ===== VOICE BUTTON - Special Microphone Style ===== */
.chat-input__action--voice {
  background: linear-gradient(145deg, #6366f1 0%, #4f46e5 50%, #4338ca 100%);
  box-shadow: 
    0 4px 15px rgba(99, 102, 241, 0.4),
    0 2px 4px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

.chat-input__action--voice::after {
  background: linear-gradient(145deg, #818cf8, #6366f1, #4f46e5);
}

.chat-input__action--voice:hover {
  box-shadow: 
    0 8px 25px rgba(99, 102, 241, 0.5),
    0 4px 8px rgba(0, 0, 0, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
}

/* Voice Recording State - Pulsing Red */
.chat-input__action--voice.recording {
  background: linear-gradient(145deg, #f87171 0%, #ef4444 50%, #dc2626 100%);
  animation: recording-pulse 1s ease-in-out infinite;
  box-shadow: 
    0 4px 20px rgba(239, 68, 68, 0.6),
    0 0 40px rgba(239, 68, 68, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

.chat-input__action--voice.recording::after {
  background: linear-gradient(145deg, #fca5a5, #ef4444, #dc2626);
  opacity: 0.7;
  animation: recording-glow 1s ease-in-out infinite;
}

@keyframes recording-pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

@keyframes recording-glow {
  0%, 100% {
    opacity: 0.4;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.15);
  }
}

.chat-input__action--voice.recording i,
.chat-input__action--voice.recording svg {
  color: #ffffff;
  fill: #ffffff;
  animation: mic-pulse 0.5s ease-in-out infinite;
}

@keyframes mic-pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.2); }
}

/* ===== INPUT FIELD - Elegant Floating Style ===== */
.chat-input__field-wrapper {
  flex: 1;
  position: relative;
}

.chat-input__field {
  width: 100%;
  height: 52px;
  padding: 0 24px;
  border: 2px solid transparent;
  border-radius: 26px;
  font-size: 16px;
  font-weight: 500;
  background: linear-gradient(#ffffff, #ffffff) padding-box,
              linear-gradient(135deg, #e5e7eb, #d1d5db) border-box;
  color: var(--chat-text);
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  outline: none;
  box-shadow: 
    0 2px 10px rgba(0, 0, 0, 0.05),
    inset 0 1px 2px rgba(0, 0, 0, 0.02);
}

.chat-input__field:focus {
  background: linear-gradient(#ffffff, #ffffff) padding-box,
              linear-gradient(135deg, var(--chat-primary), #00a838, #00d449) border-box;
  box-shadow: 
    0 4px 20px rgba(var(--chat-primary-rgb), 0.15),
    0 0 0 4px rgba(var(--chat-primary-rgb), 0.08),
    inset 0 1px 2px rgba(0, 0, 0, 0.02);
  transform: translateY(-1px);
}

.chat-input__field::placeholder {
  color: #9ca3af;
  font-weight: 400;
}

.chat-input__field:disabled {
  background: #f3f4f6;
  cursor: not-allowed;
  opacity: 0.7;
}

/* ===== SEND BUTTON - Premium Animated Design ===== */
.chat-input__send {
  width: 52px;
  height: 52px;
  min-width: 52px;
  border: none;
  background: linear-gradient(145deg, #00d449 0%, #00c444 30%, #00a838 70%, #008830 100%);
  color: #ffffff !important;
  border-radius: 18px;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
  box-shadow:
    0 4px 15px rgba(0, 196, 68, 0.4),
    0 2px 4px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.25),
    inset 0 -2px 0 rgba(0, 0, 0, 0.1);
}

/* Animated gradient background */
.chat-input__send::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    45deg,
    transparent 30%,
    rgba(255, 255, 255, 0.2) 50%,
    transparent 70%
  );
  transform: translateX(-100%);
  transition: transform 0.6s ease;
}

.chat-input__send:hover:not(:disabled)::before {
  transform: translateX(100%);
}

/* Outer glow ring */
.chat-input__send::after {
  content: '';
  position: absolute;
  inset: -4px;
  border-radius: 22px;
  background: conic-gradient(
    from 0deg,
    #00ff5a,
    #00c444,
    #00a838,
    #00c444,
    #00ff5a
  );
  z-index: -1;
  opacity: 0;
  transition: opacity 0.3s ease;
  animation: rotate-glow 3s linear infinite paused;
}

.chat-input__send:hover:not(:disabled)::after {
  opacity: 0.6;
  animation-play-state: running;
}

@keyframes rotate-glow {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.chat-input__send i,
.chat-input__send .bi,
.chat-input__send .bi-send-fill {
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
  color: white !important;
}

.chat-input__send:hover:not(:disabled) {
  transform: translateY(-4px) scale(1.08);
  box-shadow: 
    0 10px 30px rgba(0, 196, 68, 0.5),
    0 4px 10px rgba(0, 0, 0, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.3),
    inset 0 -2px 0 rgba(0, 0, 0, 0.1);
}

.chat-input__send:hover:not(:disabled) i {
  transform: translateX(3px) rotate(-15deg);
  color: white !important;
}

.chat-input__send:active:not(:disabled) {
  transform: translateY(0) scale(0.95);
  box-shadow: 
    0 2px 10px rgba(0, 196, 68, 0.4),
    inset 0 2px 4px rgba(0, 0, 0, 0.15);
}

.chat-input__send:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  box-shadow: none;
  background: linear-gradient(145deg, #9ca3af, #6b7280);
  transform: none;
}

.chat-input__send:disabled::before,
.chat-input__send:disabled::after {
  display: none;
}

.chat-input__send:disabled i {
  color: white !important;
}

/* Send Spinner */
.chat-input__send-spinner {
  width: 22px;
  height: 22px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ===== EMOJI PICKER POPUP ===== */
.emoji-picker-popup {
  position: fixed;
  background: white;
  border-radius: 12px;
  padding: 8px;
  display: flex;
  gap: 4px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  animation: popIn 0.2s ease-out;
}

@keyframes popIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.emoji-picker-backdrop {
  position: fixed;
  inset: 0;
  z-index: 999;
}

.emoji-pick-btn {
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  border-radius: 8px;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}

.emoji-pick-btn:hover {
  background: #f3f4f6;
  transform: scale(1.2);
}

/* ===== MODALS ===== */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

/* Edit Modal */
.edit-modal {
  width: 100%;
  max-width: 440px;
  background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 
    0 25px 50px -12px rgba(0, 0, 0, 0.25),
    0 0 0 1px rgba(0, 0, 0, 0.05);
  animation: modalIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes modalIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.edit-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  background: linear-gradient(135deg, #00c444 0%, #00a344 100%) !important;
  border-bottom: none;
  min-height: 64px;
}

.edit-modal__header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #ffffff !important;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.edit-modal__close {
  width: 36px;
  height: 36px;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  color: #ffffff !important;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  backdrop-filter: blur(4px);
}

.edit-modal__close i,
.edit-modal__close svg {
  color: #ffffff !important;
  fill: #ffffff !important;
  font-size: 16px;
}

.edit-modal__close:hover {
  background: rgba(255, 255, 255, 0.35);
  transform: rotate(90deg);
}

.edit-modal__body {
  padding: 24px;
  background: #ffffff;
}

.edit-modal__input {
  width: 100%;
  padding: 16px 18px;
  border: 2px solid #e5e7eb;
  border-radius: 14px;
  font-size: 15px;
  line-height: 1.6;
  resize: vertical;
  min-height: 120px;
  outline: none;
  background: #f9fafb;
  color: #1f2937;
  transition: all 0.25s ease;
}

.edit-modal__input::placeholder {
  color: #9ca3af;
}

.edit-modal__input:focus {
  border-color: var(--chat-primary);
  background: #ffffff;
  box-shadow: 
    0 0 0 4px rgba(0, 196, 68, 0.12),
    0 4px 12px rgba(0, 196, 68, 0.08);
}

.edit-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid #f3f4f6;
  background: #fafbfc;
  flex-shrink: 0;
}

.edit-modal__btn {
  padding: 12px 24px;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.edit-modal__btn--cancel {
  background: #f3f4f6;
  color: #4b5563;
  border: 1px solid #e5e7eb;
}

.edit-modal__btn--cancel:hover {
  background: #e5e7eb;
  color: #374151;
  transform: translateY(-1px);
}

.edit-modal__btn--save {
  background: #00c444 !important;
  background-image: linear-gradient(135deg, #00c444 0%, #00a344 100%) !important;
  color: #ffffff !important;
  font-weight: 700;
  min-width: 100px;
  box-shadow: 0 4px 14px rgba(0, 196, 68, 0.35);
}

.edit-modal__btn--save:hover:not(:disabled) {
  background: linear-gradient(135deg, #00a344 0%, #008837 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 196, 68, 0.45);
}

.edit-modal__btn--save:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 2px 8px rgba(0, 196, 68, 0.3);
}

.edit-modal__btn--save:disabled {
  background: #e5e7eb !important;
  color: #9ca3af !important;
  cursor: not-allowed;
  box-shadow: none !important;
  transform: none;
  opacity: 0.7;
}

.edit-modal__btn--save:not(:disabled) {
  background: linear-gradient(135deg, #00c444 0%, #00a344 100%) !important;
  color: #ffffff !important;
}

/* Confirm Modal */
.confirm-modal {
  width: 100%;
  max-width: 360px;
  background: white;
  border-radius: 20px;
  padding: 28px 24px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  animation: modalIn 0.25s ease-out;
}

.confirm-modal__icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  font-size: 28px;
}

.confirm-modal__icon--danger {
  background: #fee2e2;
  color: #ef4444;
}

.confirm-modal__title {
  margin: 0 0 8px;
  font-size: 20px;
  font-weight: 600;
  color: var(--chat-text);
}

.confirm-modal__text {
  margin: 0 0 24px;
  font-size: 14px;
  color: var(--chat-text-muted);
  line-height: 1.5;
}

.confirm-modal__actions {
  display: flex;
  gap: 12px;
}

.confirm-modal__btn {
  flex: 1;
  padding: 12px 20px;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.confirm-modal__btn--cancel {
  background: #f3f4f6;
  color: var(--chat-text);
}

.confirm-modal__btn--cancel:hover {
  background: #e5e7eb;
}

.confirm-modal__btn--danger {
  background: #ef4444;
  color: white;
}

.confirm-modal__btn--danger:hover {
  background: #dc2626;
}

.confirm-modal__btn--danger:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

/* ===== LIGHTBOX ===== */
.lightbox {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.95);
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  cursor: zoom-out;
}

.lightbox__close {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 44px;
  height: 44px;
  border: none;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border-radius: 50%;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.lightbox__close:hover {
  background: rgba(255, 255, 255, 0.2);
}

.lightbox__image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 8px;
  cursor: default;
}

/* ===== TRANSITIONS ===== */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(20px);
  opacity: 0;
}

/* ===== UTILITIES ===== */
.d-none {
  display: none !important;
}

/* =============================================
   RESPONSIVE DESIGN
   ============================================= */

/* Tablet */
@media (max-width: 1024px) {
  .chat-header {
    padding: 10px 16px;
  }

  .chat-header__content {
    gap: 10px;
  }

  .chat-header__avatar {
    width: 40px;
    height: 40px;
  }

  .chat-header__title {
    font-size: 15px;
  }

  .chat-messages {
    padding: 12px;
  }

  .msg-bubble-wrapper {
    max-width: 85%;
  }

  .msg-text {
    font-size: 14px;
  }

  .chat-input-area {
    padding: 10px 14px;
  }

  .chat-input__action {
    width: 40px;
    height: 40px;
    font-size: 18px;
  }

  .chat-input__field {
    height: 40px;
    font-size: 14px;
  }

  .chat-input__send {
    width: 40px;
    height: 40px;
    font-size: 16px;
  }
}

/* Mobile */
@media (max-width: 768px) {
  .chat-header {
    padding: 8px 12px;
  }

  .chat-header__back {
    width: 36px;
    height: 36px;
  }

  .chat-header__avatar {
    width: 36px;
    height: 36px;
  }

  .chat-header__title {
    font-size: 14px;
  }

  .chat-header__status {
    font-size: 11px;
  }

  .chat-messages {
    padding: 10px 8px;
  }

  .msg-row {
    padding: 3px 8px;
  }

  .msg-bubble-wrapper {
    max-width: 90%;
    min-width: 100px;
  }

  .msg-bubble {
    padding: 8px 12px;
    border-radius: 14px;
  }

  .msg-text {
    font-size: 14px;
    line-height: 1.4;
  }

  .msg-meta__time {
    font-size: 10px;
  }

  .msg-actions {
    padding: 2px;
    gap: 1px;
  }

  .msg-actions button {
    width: 24px;
    height: 24px;
    font-size: 12px;
  }

  /* Media adjustments */
  .image-message {
    max-width: 180px !important;
  }

  .chat-image {
    width: 180px !important;
    height: 180px !important;
    max-width: 180px !important;
    max-height: 180px !important;
  }

  .voice-message {
    min-width: 180px;
    max-width: 260px;
    padding: 8px 12px;
  }

  .voice-icon {
    width: 36px;
    height: 36px;
    font-size: 16px;
  }

  .file-message {
    min-width: 180px;
    padding: 10px 12px;
  }

  .file-icon {
    width: 38px;
    height: 38px;
    font-size: 18px;
  }

  /* Input area */
  .chat-input-area {
    padding: 8px 10px;
  }

  .chat-input {
    gap: 6px;
  }

  .chat-input__action {
    width: 38px;
    height: 38px;
    font-size: 17px;
  }

  .chat-input__field {
    height: 38px;
    padding: 0 14px;
    font-size: 14px;
    border-radius: 20px;
  }

  .chat-input__send {
    width: 38px;
    height: 38px;
    font-size: 15px;
  }

  /* Reply banner */
  .reply-banner {
    padding: 10px 12px;
    margin: 0 8px 8px;
    border-radius: 12px;
    gap: 10px;
  }

  .reply-banner__icon {
    width: 32px;
    height: 32px;
    min-width: 32px;
    border-radius: 8px;
  }

  .reply-banner__icon i {
    font-size: 14px;
  }

  .reply-banner__label {
    font-size: 10px;
  }

  .reply-banner__text {
    font-size: 12px;
  }

  .reply-banner__close {
    width: 26px;
    height: 26px;
    min-width: 26px;
    min-height: 26px;
    font-size: 11px;
    border-radius: 6px;
  }
}

/* Small Mobile */
@media (max-width: 480px) {
  .chat-header {
    padding: 6px 10px;
  }

  .chat-header__back {
    width: 32px;
    height: 32px;
  }

  .chat-header__avatar {
    width: 32px;
    height: 32px;
  }

  .chat-header__title {
    font-size: 13px;
  }

  .chat-messages {
    padding: 8px 6px;
  }

  .msg-row {
    padding: 2px 6px;
  }

  .msg-bubble {
    padding: 6px 10px;
    border-radius: 12px;
  }

  .msg-text {
    font-size: 13px;
  }

  .msg-meta__time {
    font-size: 9px;
  }

  .image-message {
    max-width: 150px !important;
  }

  .chat-image {
    width: 150px !important;
    height: 150px !important;
    max-width: 150px !important;
    max-height: 150px !important;
  }

  .voice-message {
    min-width: 160px;
    max-width: 220px;
    gap: 8px;
  }

  .voice-icon {
    width: 32px;
    height: 32px;
    font-size: 14px;
  }

  .chat-input-area {
    padding: 6px 8px;
  }

  .chat-input {
    gap: 4px;
  }

  .chat-input__action {
    width: 34px;
    height: 34px;
    font-size: 16px;
  }

  .chat-input__field {
    height: 34px;
    padding: 0 12px;
    font-size: 13px;
    border-radius: 17px;
  }

  .chat-input__send {
    width: 34px;
    height: 34px;
    font-size: 14px;
  }

  /* Modals */
  .edit-modal {
    width: 95%;
    margin: 10px;
    border-radius: 12px;
  }

  .edit-modal__header h3 {
    font-size: 16px;
  }

  .edit-modal__input {
    font-size: 14px;
  }

  .emoji-picker-popup {
    padding: 6px;
    gap: 2px;
  }

  .emoji-pick-btn {
    width: 32px;
    height: 32px;
    font-size: 16px;
  }
}

/* Ensure no layout shift when typing */
.chat-body {
  position: relative;
}

.chat-input-area {
  will-change: auto;
}

/* =============================================
   DARK MODE STYLES
   ============================================= */
/* Messages Area - Dark Mode */
.chat-dark-mode .chat-messages {
  background-color: #0d1f15;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80'%3E%3Cg fill='%234a9c5d' fill-opacity='0.1'%3E%3Cpath d='M10 10L14 6l4 4-4 4z'/%3E%3Ccircle cx='50' cy='15' r='3'/%3E%3Cpath d='M70 8h6v4h-6z'/%3E%3Cpath d='M15 35l3-5h6l3 5-3 5h-6z'/%3E%3Crect x='45' y='32' width='8' height='10' rx='1'/%3E%3Ccircle cx='72' cy='38' r='4'/%3E%3Cpath d='M8 58l6 3-6 3z'/%3E%3Cpath d='M35 55l5 8h-10z'/%3E%3Crect x='60' y='55' width='12' height='6' rx='2'/%3E%3Ccircle cx='20' cy='72' r='3'/%3E%3Cpath d='M48 70l4-4 4 4-4 4z'/%3E%3Cpath d='M68 68h8v8h-8z' fill-opacity='0.07'/%3E%3C/g%3E%3C/svg%3E");
}

.chat-dark-mode .chat-header {
  background: #1a1a2e;
  border-color: #2d2d44;
}

.chat-dark-mode .chat-header__title {
  color: #e5e5e5;
}

.chat-dark-mode .chat-header__subtitle {
  color: #9ca3af;
}

.chat-dark-mode .chat-header__back,
.chat-dark-mode .chat-header__more,
.chat-dark-mode .chat-header__menu-btn {
  color: #ffffff !important;
  background: var(--chat-primary);
}

.chat-dark-mode .chat-header__back i,
.chat-dark-mode .chat-header__back svg,
.chat-dark-mode .chat-header__more i,
.chat-dark-mode .chat-header__more svg,
.chat-dark-mode .chat-header__menu-btn i,
.chat-dark-mode .chat-header__menu-btn svg {
  color: #ffffff !important;
  fill: #ffffff !important;
}

.chat-dark-mode .chat-header__back:hover,
.chat-dark-mode .chat-header__more:hover,
.chat-dark-mode .chat-header__menu-btn:hover {
  background: var(--chat-primary-dark);
}

/* Message Bubbles - Dark Mode */
.chat-dark-mode .msg-bubble {
  background: #1e2a30;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.chat-dark-mode .msg-bubble--own {
  background: linear-gradient(135deg, #0a5c36 0%, #064e2b 100%);
}

.chat-dark-mode .msg-bubble--failed {
  background: linear-gradient(135deg, #4a1c1c 0%, #3d1515 100%);
  border-color: #7f2d2d;
}

.chat-dark-mode .msg-text {
  color: #e5e5e5;
}

.chat-dark-mode .msg-sender-name {
  filter: brightness(1.2);
}

.chat-dark-mode .msg-text .msg-link {
  color: #60a5fa;
}

.chat-dark-mode .msg-text .msg-link:hover {
  color: #93c5fd;
}

.chat-dark-mode .msg-bubble--own .msg-text .msg-link {
  color: #93c5fd;
}

.chat-dark-mode .msg-bubble--own .msg-text .msg-link:hover {
  color: #bfdbfe;
}

.chat-dark-mode .msg-bubble--own .msg-text {
  color: #ffffff;
}

.chat-dark-mode .msg-meta__time {
  color: #9ca3af;
}

.chat-dark-mode .msg-bubble--own .msg-meta__time {
  color: rgba(255, 255, 255, 0.7);
}

.chat-dark-mode .msg-meta__status--sent {
  color: #9ca3af;
}

.chat-dark-mode .msg-meta__status--seen {
  color: #4ade80;
}

/* Actions - Dark Mode */
.chat-dark-mode .msg-actions {
  background: rgba(30, 42, 48, 0.98);
  border-color: #3d3d54;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);
}

.chat-dark-mode .msg-actions button {
  color: #9ca3af;
}

.chat-dark-mode .msg-actions button:hover {
  background: #3d3d54;
  color: #e5e5e5;
}

/* Specific action button colors in dark mode */
.chat-dark-mode .msg-actions__emoji:hover {
  background: rgba(251, 191, 36, 0.2);
  color: #fbbf24;
}

.chat-dark-mode .msg-actions__reply:hover {
  background: rgba(74, 222, 128, 0.2);
  color: #4ade80;
}

.chat-dark-mode .msg-actions__info:hover {
  background: rgba(96, 165, 250, 0.2);
  color: #60a5fa;
}

.chat-dark-mode .msg-actions__delete:hover {
  background: rgba(248, 113, 113, 0.2);
  color: #f87171;
}

.chat-dark-mode .msg-actions__edit:hover {
  background: rgba(167, 139, 250, 0.2);
  color: #a78bfa;
}

/* Message Options Menu - Dark Mode */
.chat-dark-mode .message-options-menu__content {
  background: #1e293b;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4), 0 2px 8px rgba(0, 0, 0, 0.3);
}

.chat-dark-mode .message-options-menu__item {
  color: #e2e8f0;
}

.chat-dark-mode .message-options-menu__item:hover,
.chat-dark-mode .message-options-menu__item:focus {
  background: #334155;
}

.chat-dark-mode .message-options-menu__item i {
  color: #94a3b8;
}

.chat-dark-mode .message-options-menu__item--danger {
  color: #f87171;
}

.chat-dark-mode .message-options-menu__item--danger:hover,
.chat-dark-mode .message-options-menu__item--danger:focus {
  background: rgba(248, 113, 113, 0.15);
}

.chat-dark-mode .message-options-menu__item--danger i {
  color: #f87171;
}

.chat-dark-mode .message-options-menu__divider {
  background: #475569;
}

/* Reply Preview - Dark Mode */
.chat-dark-mode .reply-preview {
  background: rgba(255, 255, 255, 0.05);
  border-color: #4ade80;
}

.chat-dark-mode .reply-preview__name {
  color: #4ade80;
}

.chat-dark-mode .reply-preview__text {
  color: #9ca3af;
}

/* Voice Message - Dark Mode */
.chat-dark-mode .voice-message {
  background: linear-gradient(135deg, #1e2a30 0%, #263238 100%);
}

.chat-dark-mode .msg-bubble--own .voice-message {
  background: linear-gradient(135deg, #064e2b 0%, #0a5c36 100%);
}

.chat-dark-mode .voice-duration {
  color: #9ca3af;
}

.chat-dark-mode .msg-bubble--own .voice-duration {
  color: rgba(255, 255, 255, 0.7);
}

/* File Message - Dark Mode */
.chat-dark-mode .file-message {
  background: #1e2a30;
}

.chat-dark-mode .msg-bubble--own .file-message {
  background: rgba(255, 255, 255, 0.1);
}

.chat-dark-mode .file-name {
  color: #e5e5e5;
}

.chat-dark-mode .file-download {
  color: #4ade80;
}

/* File Card Premium - Dark Mode */
.chat-dark-mode .file-card-premium {
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 50%, #020617 100%);
  border-color: #334155;
  box-shadow: 
    0 10px 40px rgba(0, 0, 0, 0.5),
    0 4px 12px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

.chat-dark-mode .msg-bubble--own .file-card-premium {
  background: linear-gradient(135deg, #064e3b 0%, #022c22 50%, #052e16 100%);
  border-color: #059669;
  box-shadow: 
    0 10px 40px rgba(0, 196, 68, 0.2),
    0 4px 12px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

.chat-dark-mode .file-card-pattern {
  background: radial-gradient(circle, rgba(0, 196, 68, 0.15) 0%, transparent 70%);
}

.chat-dark-mode .file-card-glow {
  background: radial-gradient(circle, rgba(59, 130, 246, 0.12) 0%, transparent 70%);
}

.chat-dark-mode .file-icon-fold {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.1) 100%);
  border-color: rgba(255, 255, 255, 0.1);
}

.chat-dark-mode .file-name-title {
  color: #f1f5f9;
}

.chat-dark-mode .msg-bubble--own .file-name-title {
  color: #ecfdf5;
}

.chat-dark-mode .file-ext-tag {
  background: linear-gradient(135deg, #334155 0%, #1e293b 100%);
  color: #cbd5e1;
}

.chat-dark-mode .msg-bubble--own .file-ext-tag {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  color: #ecfdf5;
}

.chat-dark-mode .file-size-tag {
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  color: #94a3b8;
}

.chat-dark-mode .file-size-tag i {
  color: #64748b;
}

.chat-dark-mode .msg-bubble--own .file-size-tag {
  background: linear-gradient(135deg, #064e3b 0%, #022c22 100%);
  color: #6ee7b7;
}

.chat-dark-mode .msg-bubble--own .file-size-tag i {
  color: #34d399;
}

.chat-dark-mode .file-actions-row {
  background: linear-gradient(180deg, #1e293b 0%, #0f172a 100%);
  border-top-color: #334155;
}

.chat-dark-mode .msg-bubble--own .file-actions-row {
  background: linear-gradient(180deg, #064e3b 0%, #022c22 100%);
  border-top-color: #059669;
}

.chat-dark-mode .file-action-open {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
  color: #d1d5db;
  border-color: rgba(255, 255, 255, 0.1);
}

.chat-dark-mode .file-action-open:hover {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.1) 100%);
  color: #f3f4f6;
}

.chat-dark-mode .msg-bubble--own .file-action-open {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.08) 100%);
  border-color: rgba(74, 222, 128, 0.2);
  color: #6ee7b7;
}

.chat-dark-mode .file-upload-bar {
  background: rgba(255, 255, 255, 0.1);
}

.chat-dark-mode .file-upload-text {
  color: #4ade80;
}

/* Image - Dark Mode */
.chat-dark-mode .chat-image {
  background: #1e2a30;
}

/* ===== DARK MODE OVERRIDES ===== */
/* Input Area - Dark Mode */
.chat-dark-mode .chat-input-area {
  background: #1a1a2e;
  border-color: #2d2d44;
}

.chat-dark-mode .chat-input__field {
  background: #2d2d44;
  border-color: #3d3d54;
  color: #e5e5e5;
}

.chat-dark-mode .chat-input__field::placeholder {
  color: #6b7280;
}

.chat-dark-mode .chat-input__field:focus {
  border-color: #4ade80;
  background: #252540;
  box-shadow: 0 0 0 4px rgba(74, 222, 128, 0.15);
}

/* Action Buttons - Keep Primary Green in Dark Mode */
.chat-dark-mode .chat-input__action {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  color: #ffffff;
  box-shadow: 0 4px 14px rgba(34, 197, 94, 0.35);
}

.chat-dark-mode .chat-input__action i,
.chat-dark-mode .chat-input__action svg {
  color: #ffffff;
  fill: #ffffff;
}

.chat-dark-mode .chat-input__action:hover {
  background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
  color: #ffffff;
}

/* Voice Recording in Dark Mode */
.chat-dark-mode .chat-input__action--voice.recording {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  box-shadow: 0 4px 14px rgba(239, 68, 68, 0.4);
}

/* Send Button - Dark Mode */
.chat-dark-mode .chat-input__send {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  box-shadow: 0 4px 14px rgba(34, 197, 94, 0.35);
}

.chat-dark-mode .chat-input__send:hover:not(:disabled) {
  background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
  box-shadow: 0 6px 20px rgba(34, 197, 94, 0.45);
}

.chat-dark-mode .chat-input__send:disabled {
  background: #4b5563;
  box-shadow: none;
}

/* Search Bar - Dark Mode */
.chat-dark-mode .chat-search-bar {
  background: #1a1a2e;
  border-color: #2d2d44;
}

.chat-dark-mode .chat-search-bar__input-wrapper {
  background: #2d2d44;
  border-color: #3d3d54;
}

.chat-dark-mode .chat-search-bar__input-wrapper:focus-within {
  border-color: #4ade80;
  background: #252540;
  box-shadow: 0 0 0 4px rgba(74, 222, 128, 0.15);
}

.chat-dark-mode .chat-search-bar__input-wrapper i {
  color: #6b7280;
}

.chat-dark-mode .chat-search-bar__input-wrapper:focus-within i {
  color: #4ade80;
}

.chat-dark-mode .chat-search-bar__input-wrapper input {
  color: #e5e5e5;
}

.chat-dark-mode .chat-search-bar__input-wrapper input::placeholder {
  color: #6b7280;
}

.chat-dark-mode .search-clear {
  background: #3d3d54;
  color: #9ca3af;
}

.chat-dark-mode .search-clear:hover {
  background: #ef4444;
  color: #fff;
}

.chat-dark-mode .search-nav-btn {
  background: #2d2d44;
  color: #9ca3af;
}

.chat-dark-mode .search-nav-btn:hover:not(:disabled) {
  background: #4ade80;
  color: #1a1a2e;
}

.chat-dark-mode .search-nav-btn:disabled {
  background: #252540;
  color: #4b5563;
}

.chat-dark-mode .search-close-btn {
  background: #2d2d44;
  color: #9ca3af;
}

.chat-dark-mode .search-close-btn:hover {
  background: #ef4444;
  color: #fff;
}

.chat-dark-mode .search-count {
  color: #9ca3af;
}

/* Reactions - Dark Mode */
.chat-dark-mode .msg-reactions__item {
  background: rgba(255, 255, 255, 0.08);
  border-color: transparent;
}

.chat-dark-mode .msg-reactions__item:hover {
  background: rgba(255, 255, 255, 0.15);
}

.chat-dark-mode .msg-reactions__item--active {
  background: rgba(74, 222, 128, 0.2);
  border-color: #4ade80;
}

.chat-dark-mode .msg-reactions__count {
  color: #9ca3af;
}

.chat-dark-mode .msg-reactions__item--active .msg-reactions__count {
  color: #4ade80;
}

/* Typing Indicator - Dark Mode */
.chat-dark-mode .chat-header__typing {
  background: rgba(74, 222, 128, 0.15);
  color: #4ade80;
}

.chat-dark-mode .typing-dots span {
  background: #4ade80;
}

/* Reply Banner - Dark Mode */
.chat-dark-mode .reply-banner {
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.12) 0%, rgba(16, 185, 129, 0.08) 100%);
  border-color: rgba(74, 222, 128, 0.3);
  box-shadow: 
    0 4px 15px rgba(0, 0, 0, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

.chat-dark-mode .reply-banner::before {
  background: linear-gradient(180deg, #4ade80 0%, #22c55e 100%);
}

.chat-dark-mode .reply-banner__icon {
  background: linear-gradient(135deg, #4ade80 0%, #22c55e 100%);
  box-shadow: 0 4px 12px rgba(74, 222, 128, 0.3);
}

.chat-dark-mode .reply-banner__quote-icon {
  color: #4ade80;
}

.chat-dark-mode .reply-banner__label {
  color: #4ade80;
}

.chat-dark-mode .reply-banner__text {
  color: #a1a1aa;
}

.chat-dark-mode .reply-banner__close {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: #ffffff;
  box-shadow: 0 3px 10px rgba(239, 68, 68, 0.4);
}

.chat-dark-mode .reply-banner__close:hover {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  box-shadow: 0 5px 15px rgba(239, 68, 68, 0.5);
}

/* Modals - Dark Mode */
.chat-dark-mode .edit-modal {
  background: linear-gradient(145deg, #1e1e32 0%, #16162a 100%);
  box-shadow: 
    0 25px 50px -12px rgba(0, 0, 0, 0.5),
    0 0 0 1px rgba(255, 255, 255, 0.05);
}

.chat-dark-mode .edit-modal__header {
  background: linear-gradient(135deg, #00c444 0%, #00a344 100%) !important;
  border-color: transparent;
}

.chat-dark-mode .edit-modal__header h3 {
  color: #ffffff;
}

.chat-dark-mode .edit-modal__close {
  background: rgba(255, 255, 255, 0.15);
  color: #ffffff !important;
}

.chat-dark-mode .edit-modal__close:hover {
  background: rgba(255, 255, 255, 0.25);
}

.chat-dark-mode .edit-modal__body {
  background: #1a1a2e;
}

.chat-dark-mode .edit-modal__input {
  background: #252540;
  border-color: #3d3d5c;
  color: #f0f0f5;
}

.chat-dark-mode .edit-modal__input::placeholder {
  color: #6b6b8a;
}

.chat-dark-mode .edit-modal__input:focus {
  border-color: var(--chat-primary);
  background: #2a2a48;
  box-shadow: 
    0 0 0 4px rgba(0, 196, 68, 0.15),
    0 4px 12px rgba(0, 196, 68, 0.1);
}

.chat-dark-mode .edit-modal__footer {
  background: #141428;
  border-color: #2d2d44;
}

.chat-dark-mode .edit-modal__btn--cancel {
  background: #2d2d48;
  color: #b0b0c5;
  border-color: #3d3d5c;
}

.chat-dark-mode .edit-modal__btn--cancel:hover {
  background: #3a3a58;
  color: #e0e0f0;
}

.chat-dark-mode .edit-modal__btn--save {
  background: linear-gradient(135deg, var(--chat-primary) 0%, #00a344 100%);
  color: #ffffff;
  box-shadow: 0 4px 14px rgba(0, 196, 68, 0.3);
}

.chat-dark-mode .edit-modal__btn--save:hover:not(:disabled) {
  box-shadow: 0 6px 20px rgba(0, 196, 68, 0.4);
}

.chat-dark-mode .edit-modal__btn--save:disabled {
  background: #3d3d5c;
  color: #6b6b8a;
  box-shadow: none;
}

/* Delete Modal - Dark Mode */
.chat-dark-mode .delete-modal__content {
  background: #1a1a2e;
}

.chat-dark-mode .delete-modal__title {
  color: #e5e5e5;
}

.chat-dark-mode .delete-modal__message {
  color: #9ca3af;
}

/* Emoji Picker - Dark Mode */
.chat-dark-mode .emoji-picker-popup {
  background: #1a1a2e;
  border-color: #2d2d44;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
}

.chat-dark-mode .emoji-pick-btn:hover {
  background: #2d2d44;
}

/* Scrollbar - Dark Mode */
.chat-dark-mode .chat-messages::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
}

.chat-dark-mode .chat-messages::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* Date Divider - Dark Mode */
.chat-dark-mode .msg-date-divider span {
  background: #1e2a30;
  color: #9ca3af;
}

/* System Message - Dark Mode */
.chat-dark-mode .system-message {
  color: #9ca3af;
  background: rgba(255, 255, 255, 0.05);
}

/* Loading & Error - Dark Mode */
.chat-dark-mode .chat-loading,
.chat-dark-mode .chat-error {
  color: #9ca3af;
}

/* Lightbox - Dark Mode */
.chat-dark-mode .lightbox-overlay {
  background: rgba(0, 0, 0, 0.95);
}

/* =============================================
   MIA SUMMARY BUTTON (Flutter sync: _showChatSummary)
   ============================================= */
.chat-header__mia-btn {
  width: 36px;
  height: 36px;
  border: none;
  background: var(--chat-primary);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff !important;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.chat-header__mia-btn .mia-icon {
  width: 20px;
  height: 20px;
  object-fit: contain;
}

.chat-header__mia-btn:hover {
  background: var(--chat-primary-dark);
  transform: scale(1.05);
}

.mia-icon-small {
  width: 18px;
  height: 18px;
  object-fit: contain;
}

/* =============================================
   MIA SUMMARY DIALOG (Flutter sync: _showChatSummary)
   ============================================= */
.chat-summary-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 24px;
}

.chat-summary-dialog {
  background: white;
  border-radius: 24px;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.3);
  width: 100%;
  max-width: 600px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: summary-dialog-enter 0.3s ease-out;
}

@keyframes summary-dialog-enter {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.chat-summary-dialog__header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 28px;
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  color: white;
  font-size: 18px;
  font-weight: 600;
}

.chat-summary-dialog__header i:first-child {
  font-size: 22px;
  color: white;
}

.chat-summary-dialog__header span {
  flex: 1;
}

.chat-summary-dialog__close {
  width: 32px;
  height: 32px;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  transition: background 0.15s;
}

.chat-summary-dialog__close:hover {
  background: rgba(255, 255, 255, 0.3);
}

.chat-summary-dialog__body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
}

.chat-summary-presets {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}

.chat-summary-chip {
  padding: 8px 18px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  color: #6b7280;
  font-size: 14px;
  font-weight: 500;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.chat-summary-chip:hover {
  border-color: #8b5cf6;
  color: #7c3aed;
}

.chat-summary-chip.active {
  background: #8b5cf6;
  color: white;
  border-color: #8b5cf6;
  font-weight: 600;
}

.chat-summary-chip:disabled,
.chat-summary-chip[disabled] {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
  background: #e5e7eb;
  color: #9ca3af;
  border-color: #d1d5db;
}

.chat-summary-dates {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
}

.chat-summary-date-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #374151;
  font-weight: 500;
}

.chat-summary-date-label i {
  color: #8b5cf6;
  font-size: 16px;
}

.chat-summary-date-input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  color: #374151;
  outline: none;
  transition: border-color 0.15s;
}

.chat-summary-date-input:focus {
  border-color: #8b5cf6;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
}

.chat-summary-date-input:disabled,
.chat-summary-date-input[disabled] {
  opacity: 0.6;
  cursor: not-allowed;
  background: #f3f4f6;
  color: #9ca3af;
}

.chat-summary-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 32px 0;
  color: #6b7280;
  font-size: 14px;
}

.chat-summary-result {
  background: linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%);
  border: 2px solid #ddd6fe;
  border-radius: 16px;
  overflow: hidden;
  animation: result-fade-in 0.4s ease-out;
}

@keyframes result-fade-in {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.chat-summary-result__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%);
  border-bottom: 1px solid #ddd6fe;
}

.chat-summary-result__title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.chat-summary-result__title > i,
.chat-summary-result__title > i.bi,
.chat-summary-result__title > i.fas {
  width: 40px !important;
  height: 40px !important;
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%) !important;
  color: white !important;
  border-radius: 12px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  font-size: 18px !important;
  flex-shrink: 0;
}

.chat-summary-result__title-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.chat-summary-result__title-text > span:first-child {
  font-weight: 700;
  color: #1f2937;
  font-size: 16px;
}

.chat-summary-result__count {
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
}

.chat-summary-result__count::before {
  content: "";
  width: 6px;
  height: 6px;
  background: #8b5cf6;
  border-radius: 50%;
}

.chat-summary-result__actions {
  display: flex;
  gap: 8px;
}

.chat-summary-action-btn {
  width: 38px !important;
  height: 38px !important;
  min-width: 38px !important;
  min-height: 38px !important;
  border: none !important;
  background: linear-gradient(
    135deg,
    rgba(139, 92, 246, 0.15) 0%,
    rgba(124, 58, 237, 0.15) 100%
  ) !important;
  color: #7c3aed !important;
  border-radius: 10px !important;
  cursor: pointer !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  font-size: 16px !important;
  transition: all 0.2s ease !important;
  box-shadow: 0 2px 8px rgba(139, 92, 246, 0.15) !important;
  padding: 0 !important;
  flex-shrink: 0 !important;
}

.chat-summary-action-btn:hover {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%) !important;
  color: white !important;
  transform: translateY(-2px) !important;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.3) !important;
}

.chat-summary-action-btn:active {
  transform: translateY(0) !important;
}

.chat-summary-action-btn i {
  font-size: 16px !important;
}

.chat-summary-result__text {
  padding: 20px;
  font-size: 15px;
  line-height: 1.8;
  color: #374151;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 400px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #8b5cf6 #f5f3ff;
}

.chat-summary-result__text::-webkit-scrollbar {
  width: 6px;
}

.chat-summary-result__text::-webkit-scrollbar-track {
  background: #f5f3ff;
  border-radius: 3px;
}

.chat-summary-result__text::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  border-radius: 3px;
}

.chat-summary-result__footer {
  padding: 14px 20px;
  border-top: 1px solid #ddd6fe;
  background: rgba(237, 233, 254, 0.5);
}

.chat-summary-mia-badge {
  display: inline-flex !important;
  align-items: center !important;
  gap: 10px !important;
  padding: 10px 16px !important;
  background: linear-gradient(
    135deg,
    rgba(139, 92, 246, 0.15) 0%,
    rgba(124, 58, 237, 0.15) 100%
  ) !important;
  border-radius: 24px !important;
  font-size: 14px !important;
  color: #7c3aed !important;
  font-weight: 600 !important;
  box-shadow: 0 2px 8px rgba(139, 92, 246, 0.1) !important;
}

.chat-summary-mia-badge i {
  font-size: 16px !important;
  color: #8b5cf6 !important;
}

.chat-summary-error {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px;
  color: #dc2626;
  font-size: 14px;
}

.chat-summary-dialog__footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 24px;
  border-top: 1px solid #e5e7eb;
}

.chat-summary-btn--cancel {
  padding: 10px 20px;
  border: 1px solid #e5e7eb;
  background: white;
  color: #6b7280;
  font-size: 14px;
  font-weight: 500;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s;
}

.chat-summary-btn--cancel:hover {
  background: #f9fafb;
  color: #374151;
}

.chat-summary-btn--submit {
  padding: 10px 20px;
  border: none;
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  color: white;
  font-size: 14px;
  font-weight: 600;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.15s;
}

.chat-summary-btn--submit:hover:not(:disabled) {
  background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
}

.chat-summary-btn--submit:disabled,
.chat-summary-btn--submit[disabled] {
  opacity: 0.5 !important;
  cursor: not-allowed !important;
  pointer-events: none !important;
  background: #9ca3af !important;
  filter: grayscale(1) !important;
}

.chat-summary-btn--submit:disabled:hover,
.chat-summary-btn--submit[disabled]:hover {
  background: #9ca3af !important;
  transform: none !important;
}

.chat-summary-btn--submit i {
  color: white;
  font-size: 14px;
}

/* Summary Dialog - Dark Mode */
.chat-dark-mode .chat-summary-dialog {
  background: #1f2937;
}

.chat-dark-mode .chat-summary-dialog__body {
  color: #e5e5e5;
}

.chat-dark-mode .chat-summary-chip {
  background: #374151;
  border-color: #4b5563;
  color: #9ca3af;
}

.chat-dark-mode .chat-summary-chip:hover {
  border-color: #a78bfa;
  color: #c4b5fd;
}

.chat-dark-mode .chat-summary-chip.active {
  background: #7c3aed;
  border-color: #7c3aed;
  color: white;
}

.chat-dark-mode .chat-summary-date-label {
  color: #d1d5db;
}

.chat-dark-mode .chat-summary-date-input {
  background: #374151;
  border-color: #4b5563;
  color: #e5e5e5;
}

.chat-dark-mode .chat-summary-result {
  background: linear-gradient(135deg, #2d2044 0%, #3b2960 100%);
  border-color: #5b3d8a;
}

.chat-dark-mode .chat-summary-result__header {
  background: linear-gradient(135deg, #3b2960 0%, #4c3370 100%);
  border-color: #5b3d8a;
}

.chat-dark-mode .chat-summary-result__title > i {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
}

.chat-dark-mode .chat-summary-result__title-text > span:first-child {
  color: #f3f4f6;
}

.chat-dark-mode .chat-summary-result__count {
  color: #c4b5fd;
}

.chat-dark-mode .chat-summary-result__count::before {
  background: #a78bfa;
}

[data-bs-theme="dark"] .chat-summary-action-btn,
[data-theme="dark"] .chat-summary-action-btn,
.dark .chat-summary-action-btn,
.chat-dark-mode .chat-summary-action-btn {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%) !important;
  color: #ffffff !important;
  box-shadow: 0 2px 8px rgba(139, 92, 246, 0.3) !important;
}

[data-bs-theme="dark"] .chat-summary-action-btn i,
[data-theme="dark"] .chat-summary-action-btn i,
.dark .chat-summary-action-btn i,
.chat-dark-mode .chat-summary-action-btn i {
  color: #ffffff !important;
}

[data-bs-theme="dark"] .chat-summary-action-btn:hover,
[data-theme="dark"] .chat-summary-action-btn:hover,
.dark .chat-summary-action-btn:hover,
.chat-dark-mode .chat-summary-action-btn:hover {
  background: linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%) !important;
  color: #ffffff !important;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4) !important;
}

[data-bs-theme="dark"] .chat-summary-action-btn:hover i,
[data-theme="dark"] .chat-summary-action-btn:hover i,
.dark .chat-summary-action-btn:hover i,
.chat-dark-mode .chat-summary-action-btn:hover i {
  color: #ffffff !important;
}

.chat-dark-mode .chat-summary-result__text {
  color: #e5e7eb;
  scrollbar-color: #8b5cf6 #2d2044;
}

.chat-dark-mode .chat-summary-result__text::-webkit-scrollbar-track {
  background: #2d2044;
}

.chat-dark-mode .chat-summary-result__footer {
  border-color: #5b3d8a;
  background: rgba(59, 41, 96, 0.5);
}

.chat-dark-mode .chat-summary-mia-badge {
  background: linear-gradient(
    135deg,
    rgba(139, 92, 246, 0.25) 0%,
    rgba(124, 58, 237, 0.25) 100%
  );
  color: #c4b5fd;
  box-shadow: 0 2px 8px rgba(139, 92, 246, 0.15);
}

.chat-dark-mode .chat-summary-mia-badge i {
  color: #a78bfa;
}

.chat-dark-mode .chat-summary-dialog__footer {
  border-color: #374151;
}

.chat-dark-mode .chat-summary-btn--cancel {
  background: #374151;
  border-color: #4b5563;
  color: #d1d5db;
}

.chat-dark-mode .chat-summary-btn--cancel:hover {
  background: #4b5563;
  border-color: #6b7280;
  color: #f3f4f6;
}

.chat-dark-mode .chat-summary-btn--submit {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.3);
}

.chat-dark-mode .chat-summary-btn--submit:hover:not(:disabled) {
  background: linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%);
  box-shadow: 0 6px 16px rgba(139, 92, 246, 0.4);
}

.chat-dark-mode .chat-summary-btn--submit:disabled {
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.4) 0%, rgba(124, 58, 237, 0.4) 100%);
  color: rgba(255, 255, 255, 0.6);
  box-shadow: none;
}

.chat-dark-mode .chat-summary-chip {
  background: #374151;
  border-color: #4b5563;
  color: #d1d5db;
}

.chat-dark-mode .chat-summary-chip:hover {
  background: #4b5563;
  border-color: #6b7280;
  color: #f3f4f6;
}

.chat-dark-mode .chat-summary-chip.active {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  border-color: #8b5cf6;
  color: white;
}

.chat-dark-mode .chat-summary-date-label {
  color: #d1d5db;
}

.chat-dark-mode .chat-summary-date-input {
  background: #374151;
  border-color: #4b5563;
  color: #f3f4f6;
}

.chat-dark-mode .chat-summary-date-input:focus {
  border-color: #8b5cf6;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.2);
}
</style>

<!-- Unscoped styles for inline AttachmentRenderer component -->
<style>
/* ===== FILE CARD PREMIUM STYLES (Unscoped for inline component) ===== */
.file-card-premium {
  position: relative;
  min-width: 280px;
  max-width: 340px;
  background: transparent !important;
  border-radius: 12px !important;
  overflow: hidden;
  border: none !important;
  box-shadow: none !important;
  transition: all 0.2s ease;
}

.file-card-premium:hover {
  transform: none;
  box-shadow: none !important;
  border-color: transparent !important;
}

.msg-bubble--own .file-card-premium {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
}

/* Background decorations - hidden for clean look */
.file-card-bg {
  display: none;
}

.file-card-pattern,
.file-card-glow {
  display: none;
}

/* Main content area */
.file-card-content {
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 8px 4px;
  z-index: 1;
}

/* 3D File Icon */
.file-icon-3d {
  position: relative;
  width: 72px;
  height: 72px;
  flex-shrink: 0;
}

.file-icon-front {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(145deg, #6b7280 0%, #4b5563 100%);
  color: white;
  font-size: 32px;
  border-radius: 16px;
  box-shadow: 
    0 10px 30px rgba(0, 0, 0, 0.25),
    0 6px 12px rgba(0, 0, 0, 0.15),
    inset 0 2px 0 rgba(255, 255, 255, 0.25),
    inset 0 -3px 0 rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
}

.file-card-premium:hover .file-icon-front {
  transform: scale(1.05);
}

.file-icon-front i {
  filter: drop-shadow(0 3px 6px rgba(0, 0, 0, 0.4));
}

.file-icon-fold {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 22px;
  height: 22px;
  background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
  border-radius: 0 16px 0 12px;
  box-shadow: -3px 3px 8px rgba(0, 0, 0, 0.15);
  z-index: 2;
  border: 2px solid white;
}

.file-type-badge {
  position: absolute;
  bottom: -6px;
  right: -6px;
  background: linear-gradient(135deg, #1f2937 0%, #111827 100%);
  color: white;
  font-size: 10px;
  font-weight: 800;
  padding: 5px 10px;
  border-radius: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  border: 2.5px solid white;
  z-index: 3;
}

/* File type specific colors */
.file-icon--pdf .file-icon-front {
  background: linear-gradient(145deg, #ef4444 0%, #dc2626 50%, #b91c1c 100%) !important;
}
.file-icon--pdf .file-type-badge {
  background: linear-gradient(135deg, #dc2626 0%, #991b1b 100%) !important;
}

.file-icon--word .file-icon-front {
  background: linear-gradient(145deg, #3b82f6 0%, #2563eb 50%, #1d4ed8 100%) !important;
}
.file-icon--word .file-type-badge {
  background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%) !important;
}

.file-icon--excel .file-icon-front {
  background: linear-gradient(145deg, #22c55e 0%, #16a34a 50%, #15803d 100%) !important;
}
.file-icon--excel .file-type-badge {
  background: linear-gradient(135deg, #16a34a 0%, #166534 100%) !important;
}

.file-icon--ppt .file-icon-front {
  background: linear-gradient(145deg, #f97316 0%, #ea580c 50%, #c2410c 100%) !important;
}
.file-icon--ppt .file-type-badge {
  background: linear-gradient(135deg, #ea580c 0%, #9a3412 100%) !important;
}

.file-icon--zip .file-icon-front {
  background: linear-gradient(145deg, #8b5cf6 0%, #7c3aed 50%, #6d28d9 100%) !important;
}
.file-icon--zip .file-type-badge {
  background: linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%) !important;
}

.file-icon--default .file-icon-front {
  background: linear-gradient(145deg, #00c444 0%, #00a038 50%, #008830 100%) !important;
}
.file-icon--default .file-type-badge {
  background: linear-gradient(135deg, #00a038 0%, #006620 100%) !important;
}

/* File info section */
.file-info-section {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.file-name-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.msg-bubble--own .file-name-title {
  color: #065f46;
}

.file-meta-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.file-ext-tag {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background: rgba(0, 0, 0, 0.08) !important;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  box-shadow: none;
}

.msg-bubble--own .file-ext-tag {
  background: rgba(0, 100, 50, 0.15) !important;
  color: #065f46;
}

.file-size-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 8px;
  background: transparent;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  color: #9ca3af;
  box-shadow: none;
}

.file-size-tag i {
  font-size: 11px;
  color: #9ca3af;
}

.msg-bubble--own .file-size-tag {
  background: transparent;
  color: #059669;
}

.msg-bubble--own .file-size-tag i {
  color: #059669;
}

/* Action buttons row */
.file-actions-row {
  position: relative;
  display: flex;
  gap: 10px;
  padding: 10px 4px 4px 4px;
  background: transparent !important;
  border-top: none !important;
  z-index: 1;
}

.msg-bubble--own .file-actions-row {
  background: transparent !important;
  border-top: none !important;
}

.file-action-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;
  cursor: pointer;
  border: none;
}

.file-action-download {
  background: rgba(0, 196, 68, 0.15) !important;
  color: #047857 !important;
  box-shadow: none;
}

.file-action-download:hover {
  background: rgba(0, 196, 68, 0.25) !important;
  transform: none;
  box-shadow: none;
  color: #047857 !important;
}

.file-action-download i {
  font-size: 16px;
}

.file-action-open {
  background: rgba(0, 0, 0, 0.06) !important;
  color: #475569 !important;
  border: none !important;
  box-shadow: none;
}

.file-action-open:hover {
  background: rgba(0, 0, 0, 0.1) !important;
  color: #1f2937 !important;
  transform: none;
  box-shadow: none;
}

.file-action-open i {
  font-size: 16px;
}

.msg-bubble--own .file-action-download {
  background: rgba(0, 100, 50, 0.2) !important;
  color: #065f46 !important;
}

.msg-bubble--own .file-action-download:hover {
  background: rgba(0, 100, 50, 0.3) !important;
}

.msg-bubble--own .file-action-open {
  background: rgba(0, 100, 50, 0.12) !important;
  color: #065f46 !important;
}

.msg-bubble--own .file-action-open:hover {
  background: rgba(0, 100, 50, 0.2) !important;
}

/* Upload progress */
.file-upload-status {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.file-upload-bar {
  position: relative;
  height: 8px;
  background: rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  overflow: hidden;
}

.file-upload-fill {
  height: 100%;
  background: linear-gradient(90deg, #00c444 0%, #00d44a 50%, #00c444 100%);
  background-size: 200% 100%;
  border-radius: 10px;
  transition: width 0.3s ease;
  animation: uploadShimmer 1.5s ease infinite;
}

.file-upload-text {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #00c444;
}

/* Dark mode for file card */
.chat-dark-mode .file-card-premium {
  background: transparent !important;
  border: none !important;
}

.chat-dark-mode .msg-bubble--own .file-card-premium {
  background: transparent !important;
  border: none !important;
}

.chat-dark-mode .file-name-title {
  color: #e5e7eb;
}

.chat-dark-mode .msg-bubble--own .file-name-title {
  color: #d1fae5;
}

.chat-dark-mode .file-ext-tag {
  background: rgba(255, 255, 255, 0.1) !important;
  color: #9ca3af;
}

.chat-dark-mode .msg-bubble--own .file-ext-tag {
  background: rgba(255, 255, 255, 0.15) !important;
  color: #a7f3d0;
}

.chat-dark-mode .file-size-tag {
  background: transparent;
  color: #6b7280;
}

.chat-dark-mode .file-actions-row {
  background: transparent !important;
  border-top: none !important;
}

.chat-dark-mode .msg-bubble--own .file-actions-row {
  background: transparent !important;
  border-top: none !important;
}

.chat-dark-mode .file-action-download {
  background: rgba(74, 222, 128, 0.2) !important;
  color: #4ade80 !important;
}

.chat-dark-mode .file-action-open {
  background: rgba(255, 255, 255, 0.08) !important;
  color: #9ca3af !important;
  border: none !important;
}
</style>
